// =====================================================
// Typed helpers per resource. Keeps the rest of the SPA free of URL
// literals and HTTP verbs.
// =====================================================

import { api, publicApi, ApiError } from "./client.js";
import { sessionStore } from "../auth/session-store.js";

// Prefix every SPA path with /api (host Caddy strips it). Mirrors client.js's
// API_PREFIX — kept here too for the one place we bypass the fetch wrapper
// (multipart upload with progress, which needs XMLHttpRequest).
const API_PREFIX = "/api";

/**
 * Multipart POST with upload-progress, used for self-hosted media uploads
 * (R-MARKET P2). The shared fetch client (client.js) has no progress events,
 * so this one call uses XMLHttpRequest directly. It attaches the access token
 * the same way client.js does, parses the JSON body, and rejects with the same
 * structured `ApiError` (so callers read `.displayMessage` exactly as elsewhere).
 * No 401-refresh retry here: a fresh access token is in hand at upload start and
 * the upload is a single short-lived request; on a 401 the caller surfaces the
 * error and the user retries (their next call refreshes the session).
 *
 * Large/slow uploads (a 2 GB HD lecture over a throttled Iran link can take many
 * minutes): we DISABLE the XHR timeout (`xhr.timeout = 0`) so the browser never
 * aborts a still-progressing upload, and we report a clean Persian message for the
 * network/timeout/abort cases so the dialog never shows a raw English "network error".
 *
 * @param {string} path        — API path WITHOUT the /api prefix (e.g. /v1/market/assets)
 * @param {FormData} formData  — the multipart body
 * @param {(pct:number)=>void} [onProgress] — 0..100 upload percentage
 */
const uploadMultipart = (path, formData, onProgress) =>
  new Promise((resolve, reject) => {
    const url = API_PREFIX + path;
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url);
    xhr.withCredentials = true; // same-origin cookies (refresh cookie rides along)
    // No HARD client total-timeout: a large/slow (Iran) upload must not be killed mid-flight
    // while bytes are still flowing (a 2 GB lecture over a throttled link can legitimately take
    // a long time). The server (multer fileSize) + Caddy (request-body cap + proxy read/write
    // timeout) are the authoritative ceilings. 0 = no total cap on THIS in-progress request.
    // A blunt 30-min total timeout would instead abort a slow-but-progressing upload — so we use
    // the STALL WATCHDOG below (which only trips on a genuinely hung connection) instead.
    xhr.timeout = 0;

    // STALL WATCHDOG — the one failure mode the other handlers miss: the connection HANGS with
    // no bytes flowing and NO error/timeout event (a half-open socket, a proxy holding the
    // connection). With xhr.timeout=0 that would spin the progress bar forever. So: if there is
    // ZERO upload progress for STALL_MS, fail with an ACTIONABLE Persian message. The timer
    // RESETS on every progress tick, so a slow upload that keeps moving never trips it.
    const STALL_MS = 120000; // 2 min of no progress ⇒ the upload has stalled
    let stallTimer = null;
    const clearStall = () => {
      if (stallTimer) {
        clearTimeout(stallTimer);
        stallTimer = null;
      }
    };
    const armStall = () => {
      clearStall();
      stallTimer = setTimeout(() => {
        // Abort the hung request (frees the socket); onloadend then clears the timer. The
        // reject below wins (first settle), so the user sees this actionable message — not the
        // generic "network error" or an endless spinner.
        try {
          xhr.abort();
        } catch {
          /* ignore */
        }
        reject(
          new ApiError({
            status: 0,
            statusText: "stalled",
            body: {
              message:
                "بارگذاری متوقف شد (داده‌ای ارسال نمی‌شود). اتصال اینترنت یا حجم فایل را بررسی کنید و دوباره تلاش کنید؛ برای فایل‌های حجیم اتصالِ پایدار لازم است.",
            },
            url,
          }),
        );
      }, STALL_MS);
    };

    const token = sessionStore.get()?.accessToken;
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.setRequestHeader("Accept", "application/json");
    // NOTE: do NOT set Content-Type — the browser sets the multipart boundary.

    // Progress: report 0..100 (when asked) AND re-arm the stall watchdog — each tick is proof
    // the upload is still moving. Attached even without an onProgress callback so the watchdog
    // always has its heartbeat.
    if (xhr.upload) {
      xhr.upload.onprogress = (e) => {
        armStall();
        if (onProgress && e.lengthComputable) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };
    }

    const parseBody = () => {
      const ct = xhr.getResponseHeader("content-type") || "";
      if (ct.includes("application/json")) {
        try {
          return JSON.parse(xhr.responseText);
        } catch {
          return null;
        }
      }
      return xhr.responseText || null;
    };

    // Normalize a NON-JSON error body so the dialog never shows a raw HTML proxy page.
    // A 413 commonly arrives from a reverse-proxy/body-size limit as an HTML error page
    // (not our API's JSON) → surface a clean Persian message; other non-JSON errors get a
    // generic Persian fallback. (Our API errors are JSON { message } and pass through.)
    const normalizeErrorBody = (body, status) => {
      const looksHtml = typeof body === "string" && /<\s*(?:!doctype|html)/i.test(body.trim());
      // A 413 from our API arrives as JSON (multer filter — it names the exact GB
      // ceiling); pass that through. A 413 from the reverse proxy (body-size cap)
      // arrives as a non-JSON/HTML page → give a clean Persian fallback that names
      // the (video) ceiling so the author knows the practical limit.
      if (status === 413) {
        if (body && typeof body === "object" && typeof body.message === "string") return body;
        return {
          message:
            "حجم فایل بیش از حد مجاز است (حداکثر حدود ۲ گیگابایت برای ویدئو). فایل را فشرده یا کوتاه‌تر کنید و دوباره بارگذاری کنید.",
        };
      }
      if (typeof body === "string" && looksHtml) {
        return { message: "بارگذاری ناموفق بود (پاسخ نامعتبر از سرور). دوباره تلاش کنید." };
      }
      return body;
    };

    xhr.onload = () => {
      const body = parseBody();
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(body);
      } else {
        reject(
          new ApiError({
            status: xhr.status,
            statusText: xhr.statusText,
            body: normalizeErrorBody(body, xhr.status),
            url,
          }),
        );
      }
    };
    // A failed connection / dropped upload (no HTTP status). Surface a clean,
    // actionable Persian message — NOT the raw English "network error" — so the
    // in-dialog error tells the author what to do (check the connection, retry).
    xhr.onerror = () =>
      reject(
        new ApiError({
          status: 0,
          statusText: "network error",
          body: {
            message:
              "بارگذاری ناتمام ماند (قطعِ ارتباط با سرور). اتصال اینترنت را بررسی کنید و دوباره تلاش کنید. برای فایل‌های حجیم، اتصال پایدار لازم است.",
          },
          url,
        }),
      );
    // xhr.timeout is 0 (disabled) so ontimeout should never fire; we wire it anyway
    // for defensiveness in case an upstream proxy resets a very long-idle connection.
    xhr.ontimeout = () =>
      reject(
        new ApiError({
          status: 0,
          statusText: "timeout",
          body: {
            message:
              "بارگذاری بیش از حد طول کشید و قطع شد. در صورت امکان فایل را فشرده یا کوتاه‌تر کنید و با اتصال پایدارتر دوباره تلاش کنید.",
          },
          url,
        }),
      );
    xhr.onabort = () =>
      reject(
        new ApiError({
          status: 0,
          statusText: "aborted",
          body: { message: "بارگذاری لغو شد." },
          url,
        }),
      );

    // onloadend fires after ANY terminal state (load/error/abort/timeout) → clear the watchdog
    // so it can never fire once the request has settled. Then arm it and send.
    xhr.onloadend = () => clearStall();
    armStall();
    xhr.send(formData);
  });

// ---------- auth ----------
export const authApi = {
  login: ({ tenantSlug, email, password }) =>
    publicApi.post("/v1/auth/login", { tenantSlug, email, password }),
  register: ({ tenantSlug, email, password, fullName }) =>
    publicApi.post("/v1/auth/register", { tenantSlug, email, password, fullName }),
  refresh: (refreshToken) =>
    publicApi.post("/v1/auth/refresh", refreshToken ? { refreshToken } : undefined),
  // logout is authenticated (not @Public) → api.post so the access token is sent
  // (auto-refreshes if expired) and the server revokes + clears the HttpOnly
  // refresh cookie. The cookie itself rides along automatically (same-origin).
  logout: (refreshToken) =>
    api.post("/v1/auth/logout", refreshToken ? { refreshToken } : undefined),
  me: () => api.get("/v1/auth/me"),
  // R-Notif (D100) — password-claim flow (public, token-based). validateClaim
  // is a non-consuming check for the landing page; claim sets the password +
  // returns a session.
  validateClaim: (token) => publicApi.post("/v1/auth/claim/validate", { token }),
  claim: ({ token, password }) => publicApi.post("/v1/auth/claim", { token, password }),
  // R-AUTH-3 — forgot/reset password. forgotPassword is anti-enumeration
  // (always { ok: true }); the reset token travels in the body.
  forgotPassword: ({ tenantSlug, email }) =>
    publicApi.post("/v1/auth/forgot-password", { tenantSlug, email }),
  validateReset: (token) => publicApi.post("/v1/auth/reset/validate", { token }),
  resetPassword: ({ token, password }) =>
    publicApi.post("/v1/auth/reset-password", { token, password }),
  // R-AUTH-5 — 2FA. loginTwoFactor is the public step-2; the rest are
  // authenticated 2FA management (the user is logged in to configure it).
  loginTwoFactor: ({ pendingToken, code }) =>
    publicApi.post("/v1/auth/2fa/login", { pendingToken, code }),
  twoFactor: {
    status: () => api.get("/v1/auth/2fa/status"),
    setup: () => api.post("/v1/auth/2fa/setup"),
    enable: (code) => api.post("/v1/auth/2fa/enable", { code }),
    disable: (code) => api.post("/v1/auth/2fa/disable", { code }),
  },
  // R-SESSIONS — the caller's own active refresh-token sessions (real device
  // management for the Settings → امنیت tab). All self-scoped by JWT.
  //   list         → [{ id, createdAt, lastUsedAt?, userAgent?, ip?, current:boolean }]
  //                  (current = the session behind THIS request, if determinable).
  //   revoke(id)   → revoke ONE of the caller's own sessions (404 if not theirs).
  //   revokeOthers → revoke every session EXCEPT the current → { revoked:number }.
  sessions: {
    list: () => api.get("/v1/auth/sessions"),
    revoke: (id) => api.delete("/v1/auth/sessions/" + encodeURIComponent(id)),
    revokeOthers: () => api.post("/v1/auth/sessions/revoke-others", {}),
  },
};

// ---------- catalog ----------
export const catalogApi = {
  // Phase B R1 Commit F (D62) — listSchools added. Existing listFaculties
  // gains optional schoolId filter (additive — undefined arg behaves
  // identically to the prior call shape).
  listSchools: () => api.get("/v1/schools"),
  listFaculties: ({ schoolId } = {}) =>
    api.get(
      "/v1/faculties" + (schoolId ? "?schoolId=" + encodeURIComponent(schoolId) : ""),
    ),
  listDepartments: ({ facultyId } = {}) =>
    api.get(
      "/v1/departments" + (facultyId ? "?facultyId=" + encodeURIComponent(facultyId) : ""),
    ),
  listPrograms: ({ departmentId, degreeLevel } = {}) => {
    const q = [];
    if (departmentId) q.push("departmentId=" + encodeURIComponent(departmentId));
    if (degreeLevel) q.push("degreeLevel=" + encodeURIComponent(degreeLevel));
    return api.get("/v1/programs" + (q.length ? "?" + q.join("&") : ""));
  },
  listCourses: ({ programId, level, language } = {}) => {
    const q = [];
    if (programId) q.push("programId=" + encodeURIComponent(programId));
    if (level) q.push("level=" + encodeURIComponent(level));
    if (language) q.push("language=" + encodeURIComponent(language));
    return api.get("/v1/courses" + (q.length ? "?" + q.join("&") : ""));
  },
  getCourse: (id) => api.get("/v1/courses/" + encodeURIComponent(id)),
  listCohorts: ({ programId } = {}) =>
    api.get(
      "/v1/cohorts" + (programId ? "?programId=" + encodeURIComponent(programId) : ""),
    ),
};

// R-ACAD — گرایش (specialization) per-program CRUD. admin/super_admin/
// faculty_admin (backend enforces faculty_admin's facultyId scope + the
// tenantId+programId+code uniqueness). list is scoped by programId; mutations
// are guarded server-side. Soft-delete on remove.
export const specializationApi = {
  list: (programId) =>
    api.get("/v1/specializations" + (programId ? "?programId=" + encodeURIComponent(programId) : "")),
  create: (data) => api.post("/v1/specializations", data),
  update: (id, data) => api.patch("/v1/specializations/" + encodeURIComponent(id), data),
  remove: (id) => api.delete("/v1/specializations/" + encodeURIComponent(id)),
};

// R-ACAD — وزارت علوم reference catalogs (مقاطع تحصیلی + گروه‌های آموزشی). Reads
// feed the program/specialization forms (active rows). CRUD powers the admin
// «داده‌های مرجع» page; mutations are admin/super_admin-guarded server-side
// (soft-delete on remove). Both tables are tenant-scoped.
export const referenceApi = {
  degreeLevels: () => api.get("/v1/degree-levels"),
  createDegreeLevel: (data) => api.post("/v1/degree-levels", data),
  updateDegreeLevel: (id, data) => api.patch("/v1/degree-levels/" + encodeURIComponent(id), data),
  deleteDegreeLevel: (id) => api.delete("/v1/degree-levels/" + encodeURIComponent(id)),
  fieldGroups: () => api.get("/v1/field-groups"),
  createFieldGroup: (data) => api.post("/v1/field-groups", data),
  updateFieldGroup: (id, data) => api.patch("/v1/field-groups/" + encodeURIComponent(id), data),
  deleteFieldGroup: (id) => api.delete("/v1/field-groups/" + encodeURIComponent(id)),
};

// ---------- content authoring + learner content (Phase C A.1, D91) ----------
// Module + lesson CRUD / reorder (admin+instructor authoring, server-guarded
// via @Roles + @AuditAction) and the gated learner content read. The learner
// GET /courses/:id/content applies the dual-shape content gate (course-level
// OR program-term enrollment); staff preview drafts, students see published.
export const contentApi = {
  // --- modules ---
  listModules: (courseId) =>
    api.get("/v1/course-modules?courseId=" + encodeURIComponent(courseId)),
  createModule: (data) => api.post("/v1/course-modules", data),
  updateModule: (id, data) =>
    api.patch("/v1/course-modules/" + encodeURIComponent(id), data),
  reorderModules: (courseId, orderedIds) =>
    api.post("/v1/course-modules/reorder", { courseId, orderedIds }),
  deleteModule: (id) => api.delete("/v1/course-modules/" + encodeURIComponent(id)),
  // --- lessons ---
  listLessons: (moduleId) =>
    api.get("/v1/lessons?moduleId=" + encodeURIComponent(moduleId)),
  createLesson: (data) => api.post("/v1/lessons", data),
  updateLesson: (id, data) =>
    api.patch("/v1/lessons/" + encodeURIComponent(id), data),
  reorderLessons: (moduleId, orderedIds) =>
    api.post("/v1/lessons/reorder", { moduleId, orderedIds }),
  deleteLesson: (id) => api.delete("/v1/lessons/" + encodeURIComponent(id)),
  // --- lesson content blocks (Phase C A.2) ---
  listBlocks: (lessonId) =>
    api.get("/v1/lessons/" + encodeURIComponent(lessonId) + "/blocks"),
  createBlock: (lessonId, data) =>
    api.post("/v1/lessons/" + encodeURIComponent(lessonId) + "/blocks", data),
  updateBlock: (id, data) =>
    api.patch("/v1/blocks/" + encodeURIComponent(id), data),
  reorderBlocks: (lessonId, orderedIds) =>
    api.post("/v1/lessons/" + encodeURIComponent(lessonId) + "/blocks/reorder", { orderedIds }),
  deleteBlock: (id) => api.delete("/v1/blocks/" + encodeURIComponent(id)),
  // --- gated learner content read ---
  courseContent: (courseId) =>
    api.get("/v1/courses/" + encodeURIComponent(courseId) + "/content"),
  // --- learning objectives (Phase C A.3) ---
  listObjectives: (courseId) =>
    api.get("/v1/learning-objectives?courseId=" + encodeURIComponent(courseId)),
  createObjective: (data) => api.post("/v1/learning-objectives", data),
  updateObjective: (id, data) =>
    api.patch("/v1/learning-objectives/" + encodeURIComponent(id), data),
  reorderObjectives: (courseId, orderedIds) =>
    api.post("/v1/learning-objectives/reorder", { courseId, orderedIds }),
  deleteObjective: (id) => api.delete("/v1/learning-objectives/" + encodeURIComponent(id)),
  // --- lesson progress (Phase C A.3) ---
  markLessonComplete: (courseId, lessonId) =>
    api.post(
      "/v1/courses/" + encodeURIComponent(courseId) + "/lessons/" + encodeURIComponent(lessonId) + "/complete",
    ),
  unmarkLessonComplete: (courseId, lessonId) =>
    api.delete(
      "/v1/courses/" + encodeURIComponent(courseId) + "/lessons/" + encodeURIComponent(lessonId) + "/complete",
    ),
};

// ---------- gradebook (Phase C A.4, D93) ----------
// Derived from graded submissions. classGradebook is staff-only (server
// @Roles-guarded); myGrades returns the caller's own grades for a course.
export const gradebookApi = {
  classGradebook: (courseId) =>
    api.get("/v1/courses/" + encodeURIComponent(courseId) + "/gradebook"),
  myGrades: (courseId) =>
    api.get("/v1/courses/" + encodeURIComponent(courseId) + "/grades/me"),
  // R-COMPLY W5 — picker source: ONLY the courses the caller has gradebook
  // authority over (instructor → taught; faculty_admin → own faculty; admin → all),
  // NOT the whole tenant catalogue.
  teachingCourses: () => api.get("/v1/gradebook/courses"),
};

// ---------- transcript (Phase C A.5, D98) ----------
// The student's own consolidated record across all enrolled courses, derived
// (no new schema): per-course average + a credit-weighted overall.
export const achievementsApi = {
  // Real gamified achievements derived from the student's record (self-data).
  me: () => api.get("/v1/achievements/me"),
};

export const transcriptApi = {
  // Candidate-A submission-based transcript (learning loop).
  me: () => api.get("/v1/transcript/me"),
  // R-SIS-4 — Iranian academic transcript (per-term/overall GPA, probation,
  // comparative), from SectionEnrollment.finalGrade.
  academic: () => api.get("/v1/transcript/academic"),
  // Student-1 (§6.13) — degree audit (مسیر مدرک): required-by-bucket vs earned.
  degreeAudit: () => api.get("/v1/transcript/degree-audit"),
  // Staff advising — a single student's full person-360 by userId (R-MON W6):
  // { transcript, audit, exams, assignments, attendance }. admin/super_admin
  // tenant-wide; faculty_admin own-faculty (scoped server-side); foreign/
  // out-of-scope → 404 no-leak. Reading another person self-logs an audit row.
  // exams/assignments carry status + score ONLY (never raw answers).
  studentOverview: (userId) =>
    api.get("/v1/transcript/students/" + encodeURIComponent(userId) + "/overview"),
  // R-MON W6 — an instructor's person-360 by userId:
  // { instructor, sections, load, grading }. Same scoping (admin tenant-wide /
  // faculty_admin own-faculty / 404 no-leak); reading another person self-logs.
  instructorOverview: (userId) =>
    api.get("/v1/transcript/instructors/" + encodeURIComponent(userId) + "/overview"),
};

// ---------- academic admin (Phase B R1 Commit F — D62) ----------
//
// Full CRUD across the 4-level Academic Hierarchy. Used by the new
// admin pages /admin/schools, /admin/faculties, etc. (Commits G + H).
// All mutations go through @AuditAction-decorated NestJS endpoints
// and require admin role (server-side guarded; client just receives
// 403 if a non-admin somehow reaches these pages).

// S-4/5 موج ۲ — tenant-wide آیین‌نامهٔ آموزشی (RegulationPolicy). get() returns
// { raw, effective, defaults } so the panel shows overridden-vs-default; update()
// upserts the (all-optional) knobs. admin/super_admin only.
export const regulationPolicyApi = {
  get: () => api.get("/v1/regulation-policy"),
  update: (body) => api.put("/v1/regulation-policy", body),
};

export const academicAdminApi = {
  // --- Schools ---
  listSchools: () => api.get("/v1/schools"),
  getSchool: (id) => api.get("/v1/schools/" + encodeURIComponent(id)),
  createSchool: (data) => api.post("/v1/schools", data),
  updateSchool: (id, data) => api.patch("/v1/schools/" + encodeURIComponent(id), data),
  deleteSchool: (id) => api.delete("/v1/schools/" + encodeURIComponent(id)),

  // --- Faculties ---
  listFaculties: ({ schoolId } = {}) =>
    api.get("/v1/faculties" + (schoolId ? "?schoolId=" + encodeURIComponent(schoolId) : "")),
  getFaculty: (id) => api.get("/v1/faculties/" + encodeURIComponent(id)),
  createFaculty: (data) => api.post("/v1/faculties", data),
  updateFaculty: (id, data) => api.patch("/v1/faculties/" + encodeURIComponent(id), data),
  deleteFaculty: (id) => api.delete("/v1/faculties/" + encodeURIComponent(id)),

  // --- Departments ---
  listDepartments: ({ facultyId } = {}) =>
    api.get("/v1/departments" + (facultyId ? "?facultyId=" + encodeURIComponent(facultyId) : "")),
  getDepartment: (id) => api.get("/v1/departments/" + encodeURIComponent(id)),
  createDepartment: (data) => api.post("/v1/departments", data),
  updateDepartment: (id, data) => api.patch("/v1/departments/" + encodeURIComponent(id), data),
  deleteDepartment: (id) => api.delete("/v1/departments/" + encodeURIComponent(id)),

  // --- Programs ---
  listPrograms: ({ departmentId, degreeLevel } = {}) => {
    const q = [];
    if (departmentId) q.push("departmentId=" + encodeURIComponent(departmentId));
    if (degreeLevel) q.push("degreeLevel=" + encodeURIComponent(degreeLevel));
    return api.get("/v1/programs" + (q.length ? "?" + q.join("&") : ""));
  },
  getProgram: (id) => api.get("/v1/programs/" + encodeURIComponent(id)),
  createProgram: (data) => api.post("/v1/programs", data),
  updateProgram: (id, data) => api.patch("/v1/programs/" + encodeURIComponent(id), data),
  deleteProgram: (id) => api.delete("/v1/programs/" + encodeURIComponent(id)),

  // --- CourseOfferings (Phase B R2 D65) — modern surface for the
  // Cohort → Offering migration. Both APIs live in parallel during
  // the Sunset window per MIGRATION_POLICY §6.
  listOfferings: ({ status, programId } = {}) => {
    const q = [];
    if (status) q.push("status=" + encodeURIComponent(status));
    if (programId) q.push("programId=" + encodeURIComponent(programId));
    return api.get("/v1/offerings" + (q.length ? "?" + q.join("&") : ""));
  },
  getOffering: (id) => api.get("/v1/offerings/" + encodeURIComponent(id)),
  createOffering: (data) => api.post("/v1/offerings", data),
  updateOffering: (id, data) => api.patch("/v1/offerings/" + encodeURIComponent(id), data),
  // Status transitions guarded by service-layer state machine.
  // Illegal transitions reject 400 with an allowed-from-current list.
  transitionOffering: (id, to) =>
    api.post("/v1/offerings/" + encodeURIComponent(id) + "/transition", { to }),
  deleteOffering: (id) => api.delete("/v1/offerings/" + encodeURIComponent(id)),
  // Phase B R3.a Commit G (D68 Q3.a + D69) — assign/unassign instructor.
  // Pass instructorId=null (or "") to unassign. Backend validates the
  // target User holds the 'instructor' role and lives in the same tenant.
  assignOfferingInstructor: (id, instructorId) =>
    api.patch("/v1/offerings/" + encodeURIComponent(id) + "/instructor", { instructorId }),

  // --- Cohorts (legacy, Sunset 2026-12-31) — list/CRUD kept alive
  // for the dual-write window. Backend emits Sunset / Deprecation /
  // Link headers on every endpoint per MIGRATION_POLICY §6.
  listCohorts: ({ programId } = {}) =>
    api.get("/v1/cohorts" + (programId ? "?programId=" + encodeURIComponent(programId) : "")),
  getCohort: (id) => api.get("/v1/cohorts/" + encodeURIComponent(id)),
  createCohort: (data) => api.post("/v1/cohorts", data),
  updateCohort: (id, data) => api.patch("/v1/cohorts/" + encodeURIComponent(id), data),
  deleteCohort: (id) => api.delete("/v1/cohorts/" + encodeURIComponent(id)),
};

// ---------- organizational hierarchy (S-NRI-2) ----------
//
// The multi-level ORG tree (وزارت نیرو → هلدینگ → شرکت → واحد) that sits
// ALONGSIDE the academic hierarchy above — it models the employer side of
// صنعت آب و برق, not دانشکده/گروه. Every read is scoped server-side by the
// caller's OrgScope (deny-by-default; a cross-company id answers 404, never
// 403, so ids cannot be probed).
//
// READ-ONLY here on purpose: the create/update/reparent/delete endpoints exist
// and are @Roles+@AuditAction guarded, but no UI calls them until the role
// matrix lands in S-NRI-3. Add them to this group in that package.
//   getTree()     → nested forest; each node carries { …, _count:{children},
//                   children:[…] }. Nodes whose parent is out of scope surface
//                   as roots, so a scoped actor gets its own subtree.
//   getSummary(id)→ { orgId, level, descendantCount, countsByLevel, memberCount,
//                   budgetsByFiscalYear:[{ fiscalYear, amount, committed, spent }] }
//   getIntegrity()→ admin/super_admin ONLY (403 otherwise) — the reconcile check
//                   for the denormalized path/depth pair:
//                   { checked, healthy, problems:[{ id, slug, issue }] }.
export const organizationsApi = {
  getTree: () => api.get("/v1/organizations/tree"),
  getSummary: (id) => api.get("/v1/organizations/" + encodeURIComponent(id) + "/summary"),
  getIntegrity: () => api.get("/v1/organizations/integrity"),
};

// ---------- class sessions ----------
export const classSessionsApi = {
  // §12 archive — saved chat + caption lines with author names (staff/host).
  transcript: (id) => api.get("/v1/class-sessions/" + encodeURIComponent(id) + "/transcript"),
  // R-SONIOX (phase 2) — the persisted live-translation transcript (kind="soniox"
  // finals only). Open to any participant who could join (the backend re-runs the
  // join gate); → Array<{ authorName, body, lang, createdAt }>. Used by the
  // «گفتگو» panel's download/save action.
  sonioxTranscript: (id) =>
    api.get("/v1/class-sessions/" + encodeURIComponent(id) + "/soniox-transcript"),
  list: ({ courseId, status } = {}) => {
    const q = [];
    if (courseId) q.push("courseId=" + encodeURIComponent(courseId));
    if (status) q.push("status=" + encodeURIComponent(status));
    return api.get("/v1/class-sessions" + (q.length ? "?" + q.join("&") : ""));
  },
  get: (id) => api.get("/v1/class-sessions/" + encodeURIComponent(id)),
  create: (body) => api.post("/v1/class-sessions", body),
  update: (id, body) => api.patch("/v1/class-sessions/" + encodeURIComponent(id), body),
  attendance: (id) => api.get("/v1/class-sessions/" + encodeURIComponent(id) + "/attendance"),
  // S-16 — per-session attendance analysis: { finalized, total, present, late,
  //   absent, excused, students:[{userId,name,status}] }. Provisional while the
  //   session is live; graded after it ends + auto-reconciles.
  attendanceSummary: (id) => api.get("/v1/class-sessions/" + encodeURIComponent(id) + "/attendance-summary"),
  // MS-17 — the full «گزارش کلاس» for a session (staff/host only): { session,
  //   timing, attendance, participants:[{userId,name,email,firstJoinedAt,joins,
  //   minutes}], chat:{count,lines}, captions:{captionCount,sonioxCount},
  //   recording }. Aggregates only PERSISTED data (no live-collab artifacts).
  report: (id) => api.get("/v1/class-sessions/" + encodeURIComponent(id) + "/report"),
  join: (id) => api.post("/v1/class-sessions/" + encodeURIComponent(id) + "/join"),
  leave: (id) => api.post("/v1/class-sessions/" + encodeURIComponent(id) + "/leave"),
  analyze: (id, { task = "analyze", language = "fa" } = {}) =>
    api.post("/v1/class-sessions/" + encodeURIComponent(id) + "/analyze", { task, language }),
};

// R-SONIOX (phase 1, EXPERIMENTAL) — optional real-time multilingual caption/
// translation layer for the live class. The browser NEVER sees the real
// SONIOX_API_KEY; it asks the backend to mint a SHORT-LIVED temporary token and
// connects to Soniox's realtime websocket with that. The endpoint is fully
// fail-safe: when the flag is off / key missing / Soniox unreachable it answers
// a clean 200 { enabled: false } (the live class is wholly unaffected — the UI
// just hides the translate button). Success →
//   { enabled: true, apiKey: "<TEMP key>", expiresAt: "<iso|null>", wsUrl: "<wss…>" }.
export const sonioxApi = {
  tempToken: () => api.post("/v1/live/soniox/temp-token", {}),
};

// §12 live-class TURN relay — short-lived coturn credentials for the media
// fallback. The browser reaches the SFU over DIRECT UDP; when that UDP is
// filtered (common in Iran) the media never flows. This endpoint mints a
// time-limited RTCIceServer array (coturn `use-auth-secret`) so the browser can
// RELAY through coturn when direct fails. JWT-authed (any participant role).
// FAIL-SAFE: when TURN is unconfigured the server answers a clean 200
//   { iceServers: [], ttl: 0 }
// (never a 500), so a missing relay never breaks the direct-UDP live class.
// Success → { iceServers: [{ urls:[turn:…?transport=udp, turn:…?transport=tcp],
//             username, credential }], ttl }.
export const liveApi = {
  turnCredentials: () => api.get("/v1/live/turn-credentials"),
};

// ---------- assessments + submissions ----------
export const assessmentsApi = {
  // S-21 — examMode ("true"|"false") splits the hub's تکالیف/آزمون‌ها tabs server-side.
  list: ({ courseId, status, kind, examMode } = {}) => {
    const q = [];
    if (courseId) q.push("courseId=" + encodeURIComponent(courseId));
    if (status) q.push("status=" + encodeURIComponent(status));
    if (kind) q.push("kind=" + encodeURIComponent(kind));
    if (examMode != null) q.push("examMode=" + encodeURIComponent(examMode));
    return api.get("/v1/assessments" + (q.length ? "?" + q.join("&") : ""));
  },
  get: (id) => api.get("/v1/assessments/" + encodeURIComponent(id)),
  create: (body) => api.post("/v1/assessments", body),
  // S-12 — general assessment PATCH (title/config + exam question-pool poolConfig).
  update: (id, body) => api.patch("/v1/assessments/" + encodeURIComponent(id), body),
  addQuestion: (assessmentId, body) =>
    api.post("/v1/assessments/" + encodeURIComponent(assessmentId) + "/questions", body),
  // R-EXAM-1b — edit/remove an existing question (questions live at /v1/questions/:id).
  updateQuestion: (questionId, body) =>
    api.patch("/v1/questions/" + encodeURIComponent(questionId), body),
  deleteQuestion: (questionId) =>
    api.delete("/v1/questions/" + encodeURIComponent(questionId)),
  publish: (id) => api.patch("/v1/assessments/" + encodeURIComponent(id), { status: "published" }),
  // S-11 — clone a reusable QuestionBankItem into a Question on this quiz.
  addQuestionFromBank: (assessmentId, bankItemId) =>
    api.post(
      "/v1/assessments/" +
        encodeURIComponent(assessmentId) +
        "/questions/from-bank/" +
        encodeURIComponent(bankItemId),
      {},
    ),
};

// ---------- S-11 reusable question bank ----------
export const questionBankApi = {
  list: ({ courseId, kind, status, tag, q } = {}) => {
    const qs = [];
    if (courseId) qs.push("courseId=" + encodeURIComponent(courseId));
    if (kind) qs.push("kind=" + encodeURIComponent(kind));
    if (status) qs.push("status=" + encodeURIComponent(status));
    if (tag) qs.push("tag=" + encodeURIComponent(tag));
    if (q) qs.push("q=" + encodeURIComponent(q));
    return api.get("/v1/question-bank" + (qs.length ? "?" + qs.join("&") : ""));
  },
  get: (id) => api.get("/v1/question-bank/" + encodeURIComponent(id)),
  create: (body) => api.post("/v1/question-bank", body),
  update: (id, body) => api.patch("/v1/question-bank/" + encodeURIComponent(id), body),
  remove: (id) => api.delete("/v1/question-bank/" + encodeURIComponent(id)),
  // Promote an inline authored question up into the bank.
  fromQuestion: (questionId, body = {}) =>
    api.post("/v1/question-bank/from-question/" + encodeURIComponent(questionId), body),
};

export const submissionsApi = {
  // W4 — `attachments` (assignment file uploads) is optional; when present the
  // server replaces the submission's attachment set with it.
  submit: ({ assessmentId, answers, finalize = false, attachments }) =>
    api.post("/v1/submissions", { assessmentId, answers, finalize, ...(attachments ? { attachments } : {}) }),
  listMine: () => api.get("/v1/submissions/me"),
  getMine: (assessmentId) =>
    api.get("/v1/submissions/me/assessment/" + encodeURIComponent(assessmentId)),
  get: (id) => api.get("/v1/submissions/" + encodeURIComponent(id)),
  list: ({ assessmentId } = {}) =>
    api.get(
      "/v1/submissions" + (assessmentId ? "?assessmentId=" + encodeURIComponent(assessmentId) : ""),
    ),
  // R-GRADE-RUBRIC rubric-applied grading — when the submission's question
  // carries a rubricId, the grader scores PER CRITERION and we send those scores
  // as `criteriaScores`: an OBJECT keyed by the criterion's ARRAY INDEX (as a
  // string) → the awarded points. The server validates each index/score against
  // the question's rubric, COMPUTES grade = round(Σscore/Σmax*100, 2), and
  // persists the breakdown — so on this path we must NOT send `grade`. When
  // present, `questionId` targets the rubric-linked question (required only when
  // the assessment has more than one). On the free-score path (no rubric) the
  // call shape is unchanged ({ grade, feedback }) — `criteriaScores`/`questionId`
  // are simply absent.
  grade: (id, { grade, feedback, criteriaScores, questionId }) =>
    api.patch("/v1/submissions/" + encodeURIComponent(id) + "/grade", {
      feedback,
      ...(criteriaScores
        ? { criteriaScores, ...(questionId ? { questionId } : {}) }
        : { grade }),
    }),
  aiGradeDraft: (id) =>
    api.post("/v1/submissions/" + encodeURIComponent(id) + "/ai-grade-draft"),
};

// ---------- enrollments ----------
export const enrollmentsApi = {
  enrol: ({ courseId, cohortId } = {}) =>
    api.post("/v1/enrollments", { courseId, ...(cohortId ? { cohortId } : {}) }),
  listMine: () => api.get("/v1/enrollments/me"),
  setStatus: (id, status) =>
    api.patch("/v1/enrollments/" + encodeURIComponent(id) + "/status", { status }),
};

// ---------- enrollments admin (Phase B R4 D73 + D74) ----------
//
// The NEW R4 admin surface. Coexists with enrollmentsApi above (the
// student/instructor flow). Powers /admin/enrollments.
//   • adminList — offeringId / status / programId filters
//   • manualEnroll — admin enrolls a user into an offering and/or course
//   • transition — service-layer state machine (D74; legal moves only)
//   • softDelete
export const enrollmentsAdminApi = {
  adminList: ({ offeringId, status, programId } = {}) => {
    const q = [];
    if (offeringId) q.push("offeringId=" + encodeURIComponent(offeringId));
    if (status) q.push("status=" + encodeURIComponent(status));
    if (programId) q.push("programId=" + encodeURIComponent(programId));
    return api.get("/v1/enrollments" + (q.length ? "?" + q.join("&") : ""));
  },
  get: (id) => api.get("/v1/enrollments/" + encodeURIComponent(id)),
  manualEnroll: ({ userId, offeringId, courseId }) =>
    api.post("/v1/enrollments/manual", {
      userId,
      ...(offeringId ? { offeringId } : {}),
      ...(courseId ? { courseId } : {}),
    }),
  transition: (id, to) =>
    api.post("/v1/enrollments/" + encodeURIComponent(id) + "/transition", { to }),
  delete: (id) => api.delete("/v1/enrollments/" + encodeURIComponent(id)),
};

// ---------- analytics + learning events ----------
export const analyticsApi = {
  studentMe: () => api.get("/v1/analytics/student/me"),
  studentByUser: (userId) =>
    api.get("/v1/analytics/student/" + encodeURIComponent(userId)),
  course: (courseId) => api.get("/v1/analytics/course/" + encodeURIComponent(courseId)),
  tenant: () => api.get("/v1/analytics/tenant"),
  riskMe: () => api.get("/v1/analytics/risk/me"),
  riskByUser: (userId) =>
    api.get("/v1/analytics/risk/" + encodeURIComponent(userId)),
  // Institution-wide academic dashboard (admin/super_admin) — real tenant
  // roll-ups powering /admin/analytics: KPI totals + breakdowns derived from
  // Student/Instructor/Program/Term/SectionEnrollment.finalGrade. Shape:
  //   { kpis:{ totalStudents, totalInstructors, totalPrograms, activeTerms,
  //            avgGpa }, studentsByFaculty:[{ facultyId, name, count }],
  //     gradeDistribution:[{ band, from, to, count }],
  //     studentsByDegreeLevel:[{ level, label, count }],
  //     admissionsByEntryYear:[{ year, count }] }.
  academicDashboard: () => api.get("/v1/analytics/academic-dashboard"),
  // S-21 — instructor dashboard façade (self-data): { sections:[{sectionId,
  //   courseId, course:{code,title}, groupNumber, enrolled}], totals:{sections,
  //   students}, gradingQueue:{pending}, atRisk:{count, students:[…],
  //   humanReviewRequired:true}, gradeDistribution:[{band,count}], gradedCount }.
  // At-risk items are آیین‌نامه FLAGS for the human — never decisions.
  instructorMe: () => api.get("/v1/analytics/instructor/me"),
};

export const learningEventsApi = {
  emit: ({ type, courseId, lessonId, assessmentId, classSessionId, data } = {}) =>
    api.post("/v1/learning-events", {
      type,
      ...(courseId ? { courseId } : {}),
      ...(lessonId ? { lessonId } : {}),
      ...(assessmentId ? { assessmentId } : {}),
      ...(classSessionId ? { classSessionId } : {}),
      ...(data ? { data } : {}),
    }),
  listMine: ({ type, courseId } = {}) => {
    const q = [];
    if (type) q.push("type=" + encodeURIComponent(type));
    if (courseId) q.push("courseId=" + encodeURIComponent(courseId));
    return api.get("/v1/learning-events/me" + (q.length ? "?" + q.join("&") : ""));
  },
};

// ---------- tutor ----------
export const tutorApi = {
  createSession: ({ courseId, title } = {}) =>
    api.post("/v1/tutor/sessions", {
      ...(courseId ? { courseId } : {}),
      ...(title ? { title } : {}),
    }),
  listSessions: () => api.get("/v1/tutor/sessions"),
  getSession: (id) => api.get("/v1/tutor/sessions/" + encodeURIComponent(id)),
  ask: (id, { question, topK = 5 }) =>
    api.post("/v1/tutor/sessions/" + encodeURIComponent(id) + "/ask", { question, topK }),
  deleteSession: (id) => api.del("/v1/tutor/sessions/" + encodeURIComponent(id)),
};

export const documentsApi = {
  list: ({ courseId } = {}) =>
    api.get("/v1/documents" + (courseId ? "?courseId=" + encodeURIComponent(courseId) : "")),
  get: (id) => api.get("/v1/documents/" + encodeURIComponent(id)),
  create: (body) => api.post("/v1/documents", body),
};

// ---------- users ----------
export const usersApi = {
  me: () => api.get("/v1/users/me"),
  changePassword: ({ currentPassword, newPassword }) =>
    api.post("/v1/users/me/change-password", { currentPassword, newPassword }),

  // R-ACCOUNT-LIFECYCLE — account lifecycle + data portability (all self-scoped
  // by JWT). See apps/api/src/users/* (backend already built + committed-pending).
  //   deactivate(reason?)      → { deactivated:true }. SIDE EFFECT: revokes all the
  //     caller's sessions. After success the FE must clear its session + route to
  //     login (the account is now inactive; reversible only by an admin/super_admin).
  //   requestDeletion(reason?) → { scheduledFor:"<ISO ~now+30d>" }. A REQUEST only —
  //     NO data is purged; cancelable for the 30-day grace window.
  //   cancelDeletion()         → { cancelled:true } (idempotent — safe to call when
  //     there is no pending request).
  // reason is optional, trimmed, and only sent when non-empty (≤500 chars; server
  // validates). Blank/whitespace → no `reason` key at all.
  deactivate: (reason) =>
    api.post("/v1/users/me/deactivate", reason && reason.trim() ? { reason: reason.trim() } : {}),
  requestDeletion: (reason) =>
    api.post("/v1/users/me/request-deletion", reason && reason.trim() ? { reason: reason.trim() } : {}),
  cancelDeletion: () => api.post("/v1/users/me/cancel-deletion", {}),

  // GET /v1/users/me/export → the caller's own data as a JSON document (the server
  // sets Content-Disposition: attachment; filename="nri-export-YYYY-MM-DD.json").
  // The shared fetch client parses the JSON body (it has no raw-Response/Blob mode),
  // so we re-serialise the parsed object to a Blob and trigger a download via the
  // SAME anchor pattern used elsewhere (AdminAnalytics/Treasury/PayrollRun). We
  // rebuild the dated filename client-side (the parsed JSON path loses the response
  // header) so the saved file matches the server's intended name.
  exportData: async () => {
    const data = await api.get("/v1/users/me/export");
    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: "application/json;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
    const a = document.createElement("a");
    a.href = url;
    a.download = `nri-export-${today}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    return data;
  },

  // R-ACCOUNT — self-service email change (request → verify) + phone OTP
  // (verify-request → verify). All self-scoped by JWT; DEV-MOCK-FIRST (the
  // confirmation email / phone OTP are "sent" via the existing console transport
  // until a provider is wired). See apps/api/src/users/account.controller.ts.
  //   emailChangeRequest → { ok, maskedTarget, expiresInSec } (neutral, non-enumerable).
  //   emailVerify        → { changed:true, email } — revokes sessions, re-login needed.
  //   phoneVerifyRequest → { ok, maskedPhone, expiresInSec, resendInSec }.
  //   phoneVerify        → { verified:true, phoneNumber, phoneVerifiedAt }.
  emailChangeRequest: ({ newEmail, currentPassword }) =>
    api.post("/v1/users/me/email/change-request", { newEmail, currentPassword }),
  emailVerify: (token) => api.post("/v1/users/me/email/verify", { token }),
  phoneVerifyRequest: (phoneNumber) =>
    api.post("/v1/users/me/phone/verify-request", { phoneNumber }),
  phoneVerify: (code) => api.post("/v1/users/me/phone/verify", { code }),

  // Admin user management (§9.3) — admin/super_admin only.
  list: () => api.get("/v1/users"),
  listRoles: () => api.get("/v1/users/roles"),
  create: (data) => api.post("/v1/users", data),
  update: (id, data) => api.patch("/v1/users/" + encodeURIComponent(id), data),
  setRoles: (id, roleNames) =>
    api.put("/v1/users/" + encodeURIComponent(id) + "/roles", { roleNames }),
  resetPassword: (id, newPassword) =>
    api.post("/v1/users/" + encodeURIComponent(id) + "/reset-password", { newPassword }),
  // R-MON W3 — re-activate a locked (deactivated) account. admin/super_admin
  // tenant-wide; the support-scoped variant is supportApi.unlock.
  unlock: (id) => api.post("/v1/users/" + encodeURIComponent(id) + "/unlock", {}),
  // R-SIS-7 long-tail — bulk import (client parses CSV → JSON rows; server
  // validates per-row and reports ok/skipped/error so a partial file never
  // half-fails silently).
  bulk: (rows) => api.post("/v1/users/bulk", { rows }),
};

// R-COMPLY W6-People (W0 spine) — fine-grained, authority-scoped, per-role-
// REDACTED activity timeline for a single user. NO @Roles on the endpoint: any
// authenticated principal may call it; the backend (ActivityService) resolves
// the caller's authority over the target and returns 404 (never 403, never an
// empty 200) when the caller may not see them, so the frontend NEVER re-implements
// scope — the SAME component is safe for every privileged role (admin/super_admin
// full · support redacted-forensic ON TICKET REQUESTERS ONLY · faculty_admin
// own-faculty · instructor/ta own-section · counselor wellness-on-linked-student ·
// parent supportive-digest-on-linked-child · finance billing-slice). Shape (R-MON W2):
//   { target:{ userId },
//     tier:"full"|"redacted-forensic"|"redacted"|"parent",
//     domain:"academic"|"finance"|"wellness",
//     items:[{ at, kind, source:"audit"|"event", action, summary,
//              actor?:{ id, self }|null, ip?, userAgent?, meta? }],
//     page:{ limit, offset, total } }
// The "parent" tier sends NO audit rows, blanks `action` ("") and carries a friendly
// Persian `summary`; events carry resolved courseTitle/assessmentTitle/classTitle in
// `meta`. `domain` optionally narrows the slice (intersected server-side with the
// caller's authority — it can never widen access).
export const userActivityApi = {
  get: (userId, { limit, offset, domain } = {}) => {
    const q = [];
    if (limit != null) q.push("limit=" + encodeURIComponent(limit));
    if (offset != null) q.push("offset=" + encodeURIComponent(offset));
    if (domain) q.push("domain=" + encodeURIComponent(domain));
    return api.get(
      "/v1/users/" + encodeURIComponent(userId) + "/activity" + (q.length ? "?" + q.join("&") : ""),
    );
  },
};

// ---------- identity (Phase B R3.a D68 + D69) ----------
//
// Profile / Student / Instructor surface. All Profile endpoints use the
// SelfOrAdminGuard (D69 primitive): a non-admin caller can only access
// their own resource; admins can access any in the tenant. Backend
// returns 403 on cross-user attempts by non-admins.

export const profileApi = {
  // Self-service (uses JWT to resolve target user)
  getOwn: () => api.get("/v1/profile"),
  updateOwn: (data) => api.patch("/v1/profile", data),

  // R-SETTINGS — PUBLIC profile read (privacy-gated). Backed by the real
  // @Public() GET /v1/profile/public/:userId. The server applies the owner's
  // privacy settings and returns ONLY allowed fields:
  //   • profileVisibility "private"  → 404 for everyone (no-leak).
  //   • profileVisibility "members"  → an AUTHENTICATED, same-tenant caller may
  //     read it; a guest gets 401. So when the viewer has a session we go through
  //     the AUTHENTICATED `api` client (it attaches the bearer + auto-refreshes a
  //     401); a logged-out viewer uses `publicApi` (no header), and a "public"
  //     profile still returns 200 for them. An unknown/inactive user → 404.
  // One call serves all three viewers (guest / member / owner). Throttled 60/h.
  getPublic: (userId) => {
    const path = "/v1/profile/public/" + encodeURIComponent(userId);
    return sessionStore.get()?.accessToken ? api.get(path) : publicApi.get(path);
  },

  // SELF-ONLY avatar upload (R-profile-rework). Mirrors the market COURSE COVER
  // pattern: multipart `file` (PNG/JPEG/WebP ≤5MB), stored under a PUBLIC avatars/
  // subtree, returns the FULL profile row (PROFILE_SELECT shape) with avatarUrl set
  // to a token-FREE public url that loads in a plain same-origin <img src>. The
  // server reuses the SAME self guard as the other /v1/profile routes (caller can
  // only set their OWN avatar). onProgress(pct 0..100) drives the progress bar. A
  // bad MIME/size → a Persian 400 on the ApiError; an over-cap file → a 413 (multer).
  uploadAvatar: (file, onProgress) => {
    const fd = new FormData();
    fd.append("file", file);
    return uploadMultipart("/v1/profile/avatar", fd, onProgress);
  },

  // Admin listing — powers /admin/profiles
  listAdmin: () => api.get("/v1/profiles"),

  // SelfOrAdmin — :userId can be self (any role) or any user (admin only)
  getByUserId: (userId) => api.get("/v1/users/" + encodeURIComponent(userId) + "/profile"),
  updateByUserId: (userId, data) =>
    api.patch("/v1/users/" + encodeURIComponent(userId) + "/profile", data),
  // W1 (D-2026-06-14) — admin appoints a ریاست دانشکده to a faculty (scope).
  setFacultyScope: (userId, facultyId) =>
    api.patch("/v1/users/" + encodeURIComponent(userId) + "/faculty-scope", { facultyId }),
};

export const studentApi = {
  // Admin list/get/create/update/delete + self-read /me
  list: ({ status } = {}) =>
    api.get("/v1/students" + (status ? "?status=" + encodeURIComponent(status) : "")),
  get: (id) => api.get("/v1/students/" + encodeURIComponent(id)),
  getOwn: () => api.get("/v1/students/me"),
  create: (data) => api.post("/v1/students", data),
  update: (id, data) => api.patch("/v1/students/" + encodeURIComponent(id), data),
  delete: (id) => api.delete("/v1/students/" + encodeURIComponent(id)),
};

export const instructorApi = {
  // List + getById are visible to admin + student (catalog browsing).
  // All mutations admin-only (backend guarded).
  list: ({ status, departmentId } = {}) => {
    const q = [];
    if (status) q.push("status=" + encodeURIComponent(status));
    if (departmentId) q.push("departmentId=" + encodeURIComponent(departmentId));
    return api.get("/v1/instructors" + (q.length ? "?" + q.join("&") : ""));
  },
  get: (id) => api.get("/v1/instructors/" + encodeURIComponent(id)),
  getOwn: () => api.get("/v1/instructors/me"),
  // SELF self-update (R-profile-rework): an instructor edits their OWN limited,
  // non-privileged fields (expertise) without the admin :id route. Mirrors the
  // counselor/avatar self-service pattern (PATCH /v1/.../me, self-scoped by JWT).
  // Returns the full INSTRUCTOR_SELECT row so the page re-renders off the response.
  updateOwn: (data) => api.patch("/v1/instructors/me", data),
  create: (data) => api.post("/v1/instructors", data),
  update: (id, data) => api.patch("/v1/instructors/" + encodeURIComponent(id), data),
  // Dedicated sub-resource for department reassignment per memo. Pass
  // departmentId=null (or "") to detach.
  assignDepartment: (id, departmentId) =>
    api.patch("/v1/instructors/" + encodeURIComponent(id) + "/department", { departmentId }),
  delete: (id) => api.delete("/v1/instructors/" + encodeURIComponent(id)),
};

// R-SIS-1 — academic terms (نیم‌سال) + process windows. Reads (list/active/
// get) are open to any authenticated user (navbar term switcher §3.1.5 +
// every term-gated process reads the active term); mutations are admin /
// super_admin only (enforced server-side).
export const termsApi = {
  list: () => api.get("/v1/terms"),
  getActive: () => api.get("/v1/terms/active"),
  get: (id) => api.get("/v1/terms/" + encodeURIComponent(id)),
  create: (data) => api.post("/v1/terms", data),
  update: (id, data) => api.patch("/v1/terms/" + encodeURIComponent(id), data),
  transition: (id, to) =>
    api.post("/v1/terms/" + encodeURIComponent(id) + "/transition", { to }),
  setWindows: (id, windows) =>
    api.patch("/v1/terms/" + encodeURIComponent(id) + "/windows", { windows }),
  delete: (id) => api.delete("/v1/terms/" + encodeURIComponent(id)),
};

// R-SIS-2 — curriculum charts (چارت درسی). admin/super_admin/faculty_admin;
// the backend enforces faculty_admin's facultyId scope. Sub-resources for
// chart courses + prereq/coreq edges (circular prereq rejected server-side).
export const curriculumApi = {
  list: ({ programId } = {}) =>
    api.get("/v1/curricula" + (programId ? "?programId=" + encodeURIComponent(programId) : "")),
  get: (id) => api.get("/v1/curricula/" + encodeURIComponent(id)),
  // R-ACAD-4: a program's structure type + stages (for stage-based programs).
  programStructure: (programId) =>
    api.get("/v1/curricula/program/" + encodeURIComponent(programId) + "/structure"),
  create: (data) => api.post("/v1/curricula", data),
  update: (id, data) => api.patch("/v1/curricula/" + encodeURIComponent(id), data),
  transition: (id, to) =>
    api.post("/v1/curricula/" + encodeURIComponent(id) + "/transition", { to }),
  delete: (id) => api.delete("/v1/curricula/" + encodeURIComponent(id)),
  // R-ACAD — data may carry an optional specializationId (NULL/omit = common to
  // all گرایش‌ها; non-null = تخصصیِ that گرایش). Forwarded as-is; server validates
  // it belongs to the curriculum's program (400 «گرایش انتخاب‌شده … معتبر نیست»).
  addCourse: (id, data) =>
    api.post("/v1/curricula/" + encodeURIComponent(id) + "/courses", data),
  updateCourse: (id, courseId, data) =>
    api.patch(
      "/v1/curricula/" + encodeURIComponent(id) + "/courses/" + encodeURIComponent(courseId),
      data,
    ),
  removeCourse: (id, courseId) =>
    api.delete(
      "/v1/curricula/" + encodeURIComponent(id) + "/courses/" + encodeURIComponent(courseId),
    ),
  addPrerequisite: (id, data) =>
    api.post("/v1/curricula/" + encodeURIComponent(id) + "/prerequisites", data),
  removePrerequisite: (id, edgeId) =>
    api.delete(
      "/v1/curricula/" + encodeURIComponent(id) + "/prerequisites/" + encodeURIComponent(edgeId),
    ),
};

// R-SIS-3b/3c — انتخاب واحد + حذف و اضافه (student self-service). All §6.5 rules
// are enforced server-side; the page reflects them. context/cap/eligible/me are
// reads; enroll/drop are the mutations.
export const registrationApi = {
  context: () => api.get("/v1/registration/context"),
  cap: () => api.get("/v1/registration/cap"),
  eligibleCourses: () => api.get("/v1/registration/eligible-courses"),
  me: () => api.get("/v1/registration/me"),
  enroll: (sectionId) => api.post("/v1/registration/enroll", { sectionId }),
  drop: (sectionId) => api.post("/v1/registration/drop", { sectionId }),
  // «چارت درسی من» (self, any authed student). گرایش-filtered (common + the
  // student's گرایش); reuses the registration engine's passed/current logic →
  // { hasChart, specialization, terms:[{ termNumber, credits, courses:[…] }],
  //   totals:{ credits, passed, remaining } }.
  myChart: () => api.get("/v1/registration/my-chart"),
};

// S-NRI-ADAPT — «مسیر ارتقای شغلی من» (career path). Reads the S-NRI-4 competency
// layer for the logged-in employee: training hours (annual cap 200 + شغلی/عمومی/
// فرهنگی breakdown), advisory promotion eligibility (never auto-promotes — rule 11),
// نوع-دوم credential file status, and the JobGrade rung ladder. All holder-scoped
// server-side; the page is READ-ONLY.
export const careerPathApi = {
  trainingHoursMe: () => api.get("/v1/training-hours/me"),
  // targetGradeId optional — omitted here for a baseline advisory view.
  promotionEligibility: () => api.get("/v1/promotion/me/eligibility"),
  credentialType2Me: () => api.get("/v1/credential-type2/me"),
  jobGrades: () => api.get("/v1/job-grades"),
};

// S-NRI-5B — تجمیعِ سازمانیِ لایهٔ شایستگی (org roll-up). The in-service-training
// replacement for the degree-granting AdminAnalytics metrics: accrued training
// hours (annual cap applied per (person, year) — NEVER on the org sum), course
// completion rate, credential status, and headcount, one row per IMMEDIATE child
// of an org node. Read-only and side-effect-free server-side (it does NOT upsert a
// PromotionEligibility snapshot, unlike /promotion/.../eligibility) and org-scoped:
// a node outside the caller's subtree is an indistinguishable no-leak 404.
// Response carries advisory/humanReviewRequired (rule 11).
export const orgRollupApi = {
  // orgId null/omitted ⇒ the root of the caller's OWN domain. year = Jalali;
  // omitted ⇒ current Jalali year; outside 1300..1600 ⇒ 400 from the server.
  rollup: (orgId, year) =>
    api.get(
      "/v1/competency/org-rollup" +
        (orgId ? "/" + encodeURIComponent(orgId) : "") +
        (year ? "?year=" + encodeURIComponent(year) : ""),
    ),
};

// R-SIS-3b/6 — per-degree academic policy (credit cap + probation نصاب). admin.
export const degreePolicyApi = {
  list: () => api.get("/v1/degree-policies"),
  update: (degreeLevel, data) =>
    api.patch("/v1/degree-policies/" + encodeURIComponent(degreeLevel), data),
};

// R-PAY-1 — tuition (§19): student balance/installments/pay + admin tariff +
// per-bill installment generation. Payment is provider-agnostic (mock default).
export const tuitionApi = {
  // student
  balance: () => api.get("/v1/tuition/balance"),
  installments: () => api.get("/v1/tuition/installments"),
  clearance: () => api.get("/v1/tuition/clearance"), // R-PAY-2d §19.9
  pay: (body) => api.post("/v1/tuition/pay", body), // { billId, amount?, installmentId? } → { redirectUrl, isMock, ... }
  // admin (مدیر مالی)
  tariffs: () => api.get("/v1/tuition/tariffs"),
  upsertTariff: (degreeLevel, data) =>
    api.put("/v1/tuition/tariffs/" + encodeURIComponent(degreeLevel), data),
  bills: (termId) =>
    api.get("/v1/tuition/bills" + (termId ? "?termId=" + encodeURIComponent(termId) : "")),
  generateInstallments: (billId, data) =>
    api.post("/v1/tuition/bills/" + encodeURIComponent(billId) + "/installments", data),
};

// R-PAY-2d §19.9 — financial clearance (تسویه‌حساب) canonical contract.
//   clearance.me()              → caller's own clearance (student finance page)
//   clearance.forStudent(id)    → bursar view of one student (finance_manager /
//                                 admin / faculty_admin; faculty_admin is
//                                 fail-closed to their faculty → 404 otherwise)
// Both return { student:{name,studentCode}, items:[{key,label,clear,amount,note}], allClear }.
export const financeApi = {
  clearance: {
    me: () => api.get("/v1/finance/clearance/me"),
    forStudent: (studentId) =>
      api.get("/v1/finance/clearance/" + encodeURIComponent(studentId)),
  },
};

// R-PAY-2 §19.6 — student welfare-fund loans. Student: types/mine/apply.
// Admin (welfare): list + approve/reject/disburse.
export const loanApi = {
  types: () => api.get("/v1/loans/types"),
  mine: () => api.get("/v1/loans/mine"),
  apply: (data) => api.post("/v1/loans", data), // { loanTypeId, amount, reason?, maritalStatus? }
  list: (status) => api.get("/v1/loans" + (status ? "?status=" + encodeURIComponent(status) : "")),
  approve: (id, approvedAmount) =>
    api.post("/v1/loans/" + encodeURIComponent(id) + "/approve", approvedAmount ? { approvedAmount } : {}),
  reject: (id, reason) => api.post("/v1/loans/" + encodeURIComponent(id) + "/reject", { reason }),
  disburse: (id) => api.post("/v1/loans/" + encodeURIComponent(id) + "/disburse", {}),
  // LoanType config (admin) — list incl. inactive + upsert by code.
  adminTypes: () => api.get("/v1/loans/admin/types"),
  upsertType: (code, data) => api.put("/v1/loans/admin/types/" + encodeURIComponent(code), data),
};

// R-PAY-2e §19.7 — scholarships / financial aid. Student: types/mine/apply.
// Admin (committee): list + approve (percent/amount) / reject.
export const scholarshipApi = {
  types: () => api.get("/v1/scholarships/types"),
  mine: () => api.get("/v1/scholarships/mine"),
  apply: (data) => api.post("/v1/scholarships", data), // { scholarshipTypeId, reason? }
  list: (status) => api.get("/v1/scholarships" + (status ? "?status=" + encodeURIComponent(status) : "")),
  approve: (id, data) => api.post("/v1/scholarships/" + encodeURIComponent(id) + "/approve", data), // { awardedPercent?, awardedAmount? }
  reject: (id, reason) => api.post("/v1/scholarships/" + encodeURIComponent(id) + "/reject", { reason }),
  // ScholarshipType config (admin/committee) — list incl. inactive + upsert by code.
  adminTypes: () => api.get("/v1/scholarships/admin/types"),
  upsertType: (code, data) => api.put("/v1/scholarships/admin/types/" + encodeURIComponent(code), data),
};

// R-PAY-2f §19.8 — tuition refunds. Student: mine/apply. Admin (finance):
// list + approve/reject/refund.
export const refundApi = {
  mine: () => api.get("/v1/refunds/mine"), // → { refundable, requests }
  apply: (data) => api.post("/v1/refunds", data), // { amount, reason?, bankAccount? }
  list: (status) => api.get("/v1/refunds" + (status ? "?status=" + encodeURIComponent(status) : "")),
  approve: (id, approvedAmount) =>
    api.post("/v1/refunds/" + encodeURIComponent(id) + "/approve", approvedAmount ? { approvedAmount } : {}),
  reject: (id, reason) => api.post("/v1/refunds/" + encodeURIComponent(id) + "/reject", { reason }),
  refund: (id) => api.post("/v1/refunds/" + encodeURIComponent(id) + "/refund", {}),
};

// R-PAYROLL §19.10-19.15 — payable side. Personnel: my payslips. Admin
// (finance): decree issue/edit + group coefficient bump + config.
export const payrollApi = {
  myPayslips: () => api.get("/v1/payroll/my-payslips"),
  payslip: (year, month) => api.get("/v1/payroll/payslips/" + encodeURIComponent(year) + "/" + encodeURIComponent(month)),
  config: () => api.get("/v1/payroll/config"),
  upsertConfig: (data) => api.put("/v1/payroll/config", data),
  decrees: () => api.get("/v1/payroll/decrees"),
  issueDecree: (data) => api.post("/v1/payroll/decrees", data),
  bumpCoefficient: (annualCoefficient) => api.post("/v1/payroll/decrees/bump-coefficient", { annualCoefficient }),
};

// R-PAYROLL-2a §19.13 — variable-pay claims (حق‌التدریس + راهنمایی/داوری).
// Instructor: mine / generate (from sections) / addSupervision / submit.
// Manager (admin/super_admin): list + approve / reject. State-machine + authz
// are server-side; the page reflects them.
export const teachingClaimApi = {
  mine: (termId) =>
    api.get("/v1/teaching-claims/mine" + (termId ? "?termId=" + encodeURIComponent(termId) : "")),
  generate: (termId) => api.post("/v1/teaching-claims/generate", { termId }),
  addSupervision: (data) => api.post("/v1/teaching-claims/supervision", data), // { termId, level, count, title? }
  submit: (termId) => api.post("/v1/teaching-claims/submit", { termId }),
  list: ({ termId, status } = {}) => {
    const q = [];
    if (termId) q.push("termId=" + encodeURIComponent(termId));
    if (status) q.push("status=" + encodeURIComponent(status));
    return api.get("/v1/teaching-claims" + (q.length ? "?" + q.join("&") : ""));
  },
  approve: (id) => api.post("/v1/teaching-claims/" + encodeURIComponent(id) + "/approve", {}),
  reject: (id, reason) => api.post("/v1/teaching-claims/" + encodeURIComponent(id) + "/reject", { reason }),
};

// R-PAYROLL-2b §19.13 — monthly payroll run (admin/super_admin). generate →
// approve1 → approve2 → pay; cancel; bank-file rows. Posts the salary accrual to
// the ledger on pay + issues payslips. Server-side state machine + authz.
export const payrollRunApi = {
  list: () => api.get("/v1/payroll/runs"),
  get: (id) => api.get("/v1/payroll/runs/" + encodeURIComponent(id)),
  generate: (year, month) => api.post("/v1/payroll/runs/generate", { year, month }),
  approve1: (id) => api.post("/v1/payroll/runs/" + encodeURIComponent(id) + "/approve1", {}),
  approve2: (id) => api.post("/v1/payroll/runs/" + encodeURIComponent(id) + "/approve2", {}),
  pay: (id) => api.post("/v1/payroll/runs/" + encodeURIComponent(id) + "/pay", {}),
  cancel: (id) => api.post("/v1/payroll/runs/" + encodeURIComponent(id) + "/cancel", {}),
  bankFile: (id) => api.get("/v1/payroll/runs/" + encodeURIComponent(id) + "/bank-file"),
  // S6 — per-faculty cost report: { byFaculty:[{facultyName,headcount,totalGross,
  //   totalDeduction,totalNet}], totals }.
  costByFaculty: (id) => api.get("/v1/payroll/runs/" + encodeURIComponent(id) + "/cost-by-faculty"),
};

// R-PAYROLL-2c §19.16-19.18 — treasury (finance_manager/super_admin; admin is
// EXCLUDED — finance is مدیر مالی's job). Ledger balances dashboard + payroll cash
// settlement + salary-cost report.
// R-MON W5 — receivable-side people-360:
//   - students(q,status) → billing roster (only students WITH a TuitionBill):
//       { rows:[{ userId, studentCode, fullName, totalDue, paidTotal,
//                 overdueCount, lastPaymentAt, status }] }
//       status ∈ "has-overdue" | "cleared" | "partial" | "unpaid".
//   - billing(userId) → structured FINANCE-360 for one student (404, no probe,
//       for a student with no bill). FINANCE ONLY (no academic/grade fields):
//       { student:{ userId, studentCode, fullName },
//         totals:{ totalDue, paidTotal, outstanding },
//         bills:[{ id, termId, termTitle, fixedAmount, variableAmount,
//                  discountAmount, totalAmount, paidAmount, remaining, status,
//                  installments:[{ id, seq, amount, dueDate, status, paid }] }],
//         payments:[{ id, amount, status, provider, purpose, billId, paidAt, createdAt }],
//         loans:[{ id, loanTypeName, amountRequested, amountApproved, status,
//                  appliedToTuition, disbursedAt,
//                  installments:[{ id, seq, amount, paidAmount, dueDate, status }] }],
//         scholarships:[{ id, scholarshipName, status, awardedPercent,
//                         awardedAmount, createdAt }],
//         refunds:[{ id, requestedAmount, approvedAmount, status, bankAccount,
//                    refundedAt, createdAt }] }
//   - receivables() → per-term billed/collected/outstanding + overdue aging:
//       { byTerm:[{ termId, termTitle, year, semester, billCount, billed,
//                   collected, outstanding }],
//         aging:[{ key, label, count, amount }],
//         totals:{ billed, collected, outstanding, overdueAmount } }
//   - receivablesCsvUrl() → path for the CSV export (download attachment).
export const treasuryApi = {
  summary: () => api.get("/v1/treasury/summary"),
  payables: () => api.get("/v1/treasury/payables"),
  settle: (runId) => api.post("/v1/treasury/settle/" + encodeURIComponent(runId), {}),
  salaryReport: ({ year, month } = {}) => {
    const q = [];
    if (year) q.push("year=" + encodeURIComponent(year));
    if (month) q.push("month=" + encodeURIComponent(month));
    return api.get("/v1/treasury/salary-report" + (q.length ? "?" + q.join("&") : ""));
  },
  salaryReportByFaculty: ({ year, month } = {}) => {
    const q = [];
    if (year) q.push("year=" + encodeURIComponent(year));
    if (month) q.push("month=" + encodeURIComponent(month));
    return api.get("/v1/treasury/salary-report-by-faculty" + (q.length ? "?" + q.join("&") : ""));
  },
  // R-MON W5 — billing roster + finance-360 + receivables.
  students: (q, status) => {
    const p = [];
    if (q) p.push("q=" + encodeURIComponent(q));
    if (status) p.push("status=" + encodeURIComponent(status));
    return api.get("/v1/treasury/students" + (p.length ? "?" + p.join("&") : ""));
  },
  billing: (userId) =>
    api.get("/v1/treasury/students/" + encodeURIComponent(userId) + "/billing"),
  receivables: () => api.get("/v1/treasury/receivables"),
  receivablesCsvUrl: () => "/v1/treasury/receivables.csv",
};

// R-CHAT-1 §18.2 — self-hosted messenger (any authed user). DM + text messages;
// the service enforces tenant scope + conversation membership. R-CHAT-1b polls
// these for near-real-time; R-CHAT-1c swaps in WebSocket delivery.
export const chatApi = {
  conversations: () => api.get("/v1/chat/conversations"),
  contacts: (q) => api.get("/v1/chat/contacts" + (q ? "?q=" + encodeURIComponent(q) : "")),
  createDm: (userId) => api.post("/v1/chat/conversations/dm", { userId }),
  getConversation: (id) => api.get("/v1/chat/conversations/" + encodeURIComponent(id)),
  // R-CHAT M7 — message history (member-gated, client-safe hydration). Two cursor
  // modes (a single fetch is ONE direction):
  //   { before } → the page OLDER than the cursor (scroll-up; ascending in the page).
  //   { after }  → messages NEWER than the cursor (ISO timestamp OR a messageId),
  //                ASCENDING. Used on socket RECONNECT to backfill the gap missed
  //                while disconnected; nextCursor is the last message ID (drain by
  //                re-calling with it while hasMore). `before` wins if BOTH are passed.
  // → { messages, hasMore, nextCursor }.
  messages: (id, { before, after, limit } = {}) => {
    const q = [];
    if (before) q.push("before=" + encodeURIComponent(before));
    else if (after) q.push("after=" + encodeURIComponent(after));
    if (limit) q.push("limit=" + encodeURIComponent(limit));
    return api.get("/v1/chat/conversations/" + encodeURIComponent(id) + "/messages" + (q.length ? "?" + q.join("&") : ""));
  },
  // R-CHAT @MENTION — the send route accepts an OPTIONAL mentions:[User.ids] of
  // members the message @-mentions. The BE validates each id is a CURRENT member of
  // the conversation (drops non-members silently) and, for each valid mentioned member
  // that is NOT the sender (and is not chat-muted), creates a coalesced/idempotent
  // in-app NotificationLog row (link → /messages?c=:id). Transient — no migration, no
  // column; the payload MAY echo mentions:[] but the FE does not depend on persistence.
  send: (id, body, replyToId, mentions, asQuestion) => {
    const payload = { body };
    if (replyToId) payload.replyToId = replyToId;
    if (Array.isArray(mentions) && mentions.length > 0) payload.mentions = mentions;
    // S4 — structured classroom question (groups/channels; the BE degrades a DM
    // "question" to a normal message silently).
    if (asQuestion === true) payload.asQuestion = true;
    return api.post("/v1/chat/conversations/" + encodeURIComponent(id) + "/messages", payload);
  },
  markRead: (id) => api.post("/v1/chat/conversations/" + encodeURIComponent(id) + "/read", {}),
  // R-CHAT-2 message features.
  editMessage: (id, body) => api.patch("/v1/chat/messages/" + encodeURIComponent(id), { body }),
  deleteMessage: (id) => api.delete("/v1/chat/messages/" + encodeURIComponent(id)),
  react: (id, emoji) => api.post("/v1/chat/messages/" + encodeURIComponent(id) + "/react", { emoji }),
  // R-CHAT-2 — groups + channels.
  createGroup: (title, memberIds) => api.post("/v1/chat/groups", { title, memberIds }),
  createChannel: (data) => api.post("/v1/chat/channels", data), // { title, description?, memberIds? }
  addMembers: (id, memberIds) => api.post("/v1/chat/conversations/" + encodeURIComponent(id) + "/members", { memberIds }),
  removeMember: (id, userId) => api.delete("/v1/chat/conversations/" + encodeURIComponent(id) + "/members/" + encodeURIComponent(userId)),
  setRole: (id, userId, role) => api.post("/v1/chat/conversations/" + encodeURIComponent(id) + "/members/" + encodeURIComponent(userId) + "/role", { role }),
  leave: (id) => api.post("/v1/chat/conversations/" + encodeURIComponent(id) + "/leave", {}),
  update: (id, data) => api.patch("/v1/chat/conversations/" + encodeURIComponent(id), data),
  // S-22 wave 2 — mute/unmute a conversation FOR THE CALLER (بی‌صدا). A muted
  // conversation is skipped by BOTH in-app notification fan-outs (the coalesced
  // per-conversation chat.message row AND the @mention ping). → { conversationId, muted }.
  setMute: (id, muted) => api.post("/v1/chat/conversations/" + encodeURIComponent(id) + "/mute", { muted: !!muted }),
  // R-CHAT §18.2.4 — public channel discovery + self-subscribe (unsubscribe = leave).
  channels: () => api.get("/v1/chat/channels"),
  subscribe: (id) => api.post("/v1/chat/conversations/" + encodeURIComponent(id) + "/subscribe", {}),
  // R-CHAT-3 — auto class spaces from course sections.
  mySections: () => api.get("/v1/chat/my-sections"),
  provisionSection: (id) => api.post("/v1/chat/sections/" + encodeURIComponent(id) + "/conversations", {}),
  // S-21 — member-gated lookup (instructor or REGISTERED student of the section)
  // of the provisioned class spaces: { groupId, channelId, provisioned }. Feeds
  // the course hub's «گروه درس» deep-link (/messages?c=<groupId>).
  sectionConversation: (id) => api.get("/v1/chat/sections/" + encodeURIComponent(id) + "/conversation"),

  // R-CHAT M2 — message search across the caller's MEMBER conversations (server
  // membership + tenant gated). q (>=2 chars) + optional conversationId (restrict
  // to one thread) + before (ISO keyset cursor) + limit (1..50). →
  //   { results:[{ messageId, conversationId, conversationTitle, senderId,
  //                senderName, kind, body, snippet (<mark>…</mark>), createdAt,
  //                mine }], hasMore, nextCursor }.
  // ADVANCED FILTERS (composable, all optional + membership-scoped server-side):
  //   senderId → restrict to one sender (a member User.id).
  //   kind     → "media" (image+voice) | "file" | "question" (S4 Q&A) | "all" (default).
  //   from/to  → ISO date-range bounds (S4 — the FE sends computed quick-ranges).
  search: ({ q, conversationId, before, limit, senderId, kind, from, to } = {}) => {
    const params = [];
    if (q) params.push("q=" + encodeURIComponent(q));
    if (conversationId) params.push("conversationId=" + encodeURIComponent(conversationId));
    if (before) params.push("before=" + encodeURIComponent(before));
    if (limit) params.push("limit=" + encodeURIComponent(limit));
    if (senderId) params.push("senderId=" + encodeURIComponent(senderId));
    if (kind && kind !== "all") params.push("kind=" + encodeURIComponent(kind));
    if (from) params.push("from=" + encodeURIComponent(from));
    if (to) params.push("to=" + encodeURIComponent(to));
    return api.get("/v1/chat/search" + (params.length ? "?" + params.join("&") : ""));
  },

  // ----- S4 — classroom Q&A + group polls -----
  // Pick / clear the accepted answer of a question message (owner/admin only).
  acceptAnswer: (messageId, answerId) =>
    api.post("/v1/chat/messages/" + encodeURIComponent(messageId) + "/accept-answer", answerId ? { answerId } : {}),
  // The Q&A list of a conversation: { questions:[message + answersCount], hasMore, nextCursor }.
  questions: (conversationId, { answered, before, limit } = {}) => {
    const params = [];
    if (answered === true || answered === "true") params.push("answered=true");
    if (answered === false || answered === "false") params.push("answered=false");
    if (before) params.push("before=" + encodeURIComponent(before));
    if (limit) params.push("limit=" + encodeURIComponent(limit));
    return api.get(
      "/v1/chat/conversations/" + encodeURIComponent(conversationId) + "/questions" + (params.length ? "?" + params.join("&") : ""),
    );
  },
  // Create a poll (a new kind="poll" message; body = question). → the enriched message.
  createPoll: (conversationId, { question, options, multi }) =>
    api.post("/v1/chat/conversations/" + encodeURIComponent(conversationId) + "/polls", {
      question,
      options,
      ...(multi ? { multi: true } : {}),
    }),
  // Vote / re-vote (option indexes; last-write-wins). → the enriched poll message.
  votePoll: (pollId, choices) => api.post("/v1/chat/polls/" + encodeURIComponent(pollId) + "/vote", { choices }),
  // Close voting (creator or conversation owner/admin). → the enriched poll message.
  closePoll: (pollId) => api.post("/v1/chat/polls/" + encodeURIComponent(pollId) + "/close", {}),

  // SEEN-BY (read receipts roster). Member-gated server-side (a non-member / cross-tenant
  // / missing message → 403/404 no-leak). →
  //   GROUP/CHANNEL: { kind, readers:[{ userId, fullName, avatarUrl, readAt }], readCount }
  //                  (the OTHER members whose lastReadAt >= the message createdAt, newest first).
  //   DM:            { kind:"dm", read:boolean, readAt:ISO|null, readers:[…] }.
  // The list is capped server-side (~500). Used by the «دیده‌شده توسط» sheet on own messages.
  readBy: (messageId) => api.get("/v1/chat/messages/" + encodeURIComponent(messageId) + "/read-by"),

  // R-CHAT M3 — batch presence read. userIds: string[] → comma-joined query.
  //   → [{ userId, status:"online"|"offline", lastSeenAt: ISO|null }].
  // lastSeenAt is privacy-gated server-side (Profile.preferences.privacy
  // .showLastSeen): "contacts" (default) reveals it only to accepted connections /
  // shared-conversation members; otherwise it comes back null (status only).
  presence: (userIds) =>
    api.get(
      "/v1/chat/presence?userIds=" + encodeURIComponent((userIds || []).filter(Boolean).join(",")),
    ),

  // R-CHAT M4 — global unread summary for the «پیام‌ها» nav badge.
  //   → { totalUnread } (from per-member lastReadAt — same source as the list).
  unreadSummary: () => api.get("/v1/chat/unread-summary"),

  // R-CHAT media (Telegram-class attachments). Upload an image/file to a conversation the
  // caller is a member of; multipart field "file" + optional "body" caption. The backend
  // IMMEDIATELY creates the message (an attachment is a message with kind!="text") and fans
  // it out via the SAME socket "message" event, so the POST resolves to the enriched message
  // (kind:"image"|"file", attachmentUrl, attachmentThumbUrl, attachmentName, attachmentMeta).
  // Uses uploadMultipart for an XHR upload-progress bar (the fetch client has no progress);
  // onProgress(pct 0..100) drives the composer progress UI. A bad MIME/kind/size → a Persian
  // 400 on the ApiError; an over-ceiling file → a 413 (multer) naming the chat file cap (MB).
  // M7 — a VOICE note (audio/webm|ogg|mpeg|mp4 from MediaRecorder) goes through this SAME
  // route; the server classifies it as kind "voice" (token-served, inline-disposition <audio>).
  uploadAttachment: (conversationId, file, onProgress, body) => {
    const fd = new FormData();
    fd.append("file", file);
    if (body && String(body).trim()) fd.append("body", String(body).trim());
    return uploadMultipart(
      "/v1/chat/conversations/" + encodeURIComponent(conversationId) + "/attachments",
      fd,
      onProgress,
    );
  },

  // R-CHAT M5a — PIN MESSAGES. Authority is server-enforced (group/channel
  // owner/admin; DM either member) + tenant + membership gated; a 404 never leaks
  // a foreign message/conversation. A pin/unpin fans out the NEW socket event
  // "message:pin" { conversationId, messageId, pinned } to the room, and the
  // ~10-pin cap rejects with a Persian 400.
  //   pin(messageId)    → the updated message (kind/body/attachment* + pinnedAt/pinnedBy).
  //   unpin(messageId)  → { ok:true } (idempotent on an already-unpinned message).
  pin: (messageId) => api.post("/v1/chat/messages/" + encodeURIComponent(messageId) + "/pin", {}),
  unpin: (messageId) => api.delete("/v1/chat/messages/" + encodeURIComponent(messageId) + "/pin"),
  // → the conversation's pinned messages (member-gated), newest pin first; each row
  //   is the SAME client-safe message shape history returns (token-minted
  //   attachmentUrl/thumbUrl + publicAttachmentMeta — never storageKey).
  listPins: (conversationId) =>
    api.get("/v1/chat/conversations/" + encodeURIComponent(conversationId) + "/pins"),

  // R-CHAT M5b — SHARED-MEDIA GALLERY. Cursor-paginated read over the
  // conversation's image|file messages (member-gated), newest first, returning the
  // SAME client-safe message shape as history (token-minted attachmentUrl/thumbUrl
  // + publicAttachmentMeta). type ∈ "image" | "file"; before is an ISO keyset
  // cursor; limit 1..50. → { messages:[…], hasMore, nextCursor }.
  listMedia: (conversationId, { type, before, limit } = {}) => {
    const q = [];
    if (type) q.push("type=" + encodeURIComponent(type));
    if (before) q.push("before=" + encodeURIComponent(before));
    if (limit) q.push("limit=" + encodeURIComponent(limit));
    return api.get(
      "/v1/chat/conversations/" + encodeURIComponent(conversationId) + "/media" + (q.length ? "?" + q.join("&") : ""),
    );
  },

  // S-22 wave 2 — COURSE-aggregated shared-media library. Aggregates image|file
  // messages across ALL of the course's section chat spaces (section_group +
  // section_channel) the caller is a member of (membership-scoped server-side —
  // a student sees their section's media, an instructor every section they run,
  // a stranger an empty page; a missing course → 404). Same client-safe message
  // shape + keyset paging as listMedia. type ∈ "image"|"file"; before = ISO cursor.
  // → { messages:[…], hasMore, nextCursor }.
  courseMedia: (courseId, { type, before, limit } = {}) => {
    const q = [];
    if (type) q.push("type=" + encodeURIComponent(type));
    if (before) q.push("before=" + encodeURIComponent(before));
    if (limit) q.push("limit=" + encodeURIComponent(limit));
    return api.get(
      "/v1/chat/course/" + encodeURIComponent(courseId) + "/media" + (q.length ? "?" + q.join("&") : ""),
    );
  },

  // R-CHAT M6b — REPORT A MESSAGE. Member-gated server-side (you can only report a
  // message in a conversation you're in) + @AuditAction; one OPEN report per
  // (reporter, message) is idempotent (P2002-guarded → the server resolves a
  // duplicate to the existing open report, never a hard error). reason is optional
  // (a short free-text note). A foreign message/conversation → 404 (no-leak); a
  // non-member → 403 with a Persian message on the ApiError. →
  //   { id, status:"open", messageId, conversationId, reason?, createdAt }.
  report: (messageId, reason) =>
    api.post(
      "/v1/chat/messages/" + encodeURIComponent(messageId) + "/report",
      reason && String(reason).trim() ? { reason: String(reason).trim() } : {},
    ),

  // R-CHAT M8 — FORWARD a message into another conversation the caller is a member of.
  // BE forward contract (chat.controller ForwardDto):
  //   POST /v1/chat/messages/:id/forward  { targetConversationId }
  // DOUBLE-gated server-side: the caller must be a member of BOTH the SOURCE (to read
  // the message) AND the TARGET (to post); the target's post-permission (channel) + DM
  // block gate apply; a missing/cross-tenant source OR target → 404/403 no-leak. The
  // server creates a brand-NEW message in the target (copying the body + any
  // image/file/voice attachment BYTES into the target's subtree, re-minting a fresh
  // per-viewer token-gated url on read; replyTo/pin/reactions are NOT carried) stamped
  // with forwardedFromName + forwardedFromId (the ORIGINAL author, snapshotted; a
  // re-forward still credits the true origin). It fans the new message out via the SAME
  // socket "message" event (no new event) + the same in-app notification as a normal
  // send, and RESOLVES to the enriched, client-safe message (same shape history
  // returns). The FE appends it if the target is the open thread (de-duped by id vs the
  // socket echo).
  forward: (messageId, targetConversationId) =>
    api.post(
      "/v1/chat/messages/" + encodeURIComponent(messageId) + "/forward",
      { targetConversationId },
    ),

  // R-CHAT (multi-select) — BATCH FORWARD up to 50 messages into one target conversation.
  //   POST /v1/chat/messages/forward-batch  { messageIds:string[] (<=50), targetConversationId }
  // Same gating as the single forward, applied per source message: the caller must be a
  // member of EACH source message's conversation AND the target (assertCanDm / member-gated).
  // Reuses the single-forward internals (snapshot forwardedFromName/Id, copy attachment bytes).
  // Source order is preserved. → the created messages array (same client-safe shape as forward).
  forwardBatch: (messageIds, targetConversationId) =>
    api.post("/v1/chat/messages/forward-batch", {
      messageIds: (messageIds || []).slice(0, 50),
      targetConversationId,
    }),

  // R-CHAT (multi-select) — BATCH DELETE up to 50 messages.
  //   POST /v1/chat/messages/delete-batch  { messageIds:string[] (<=50) }
  // Each message is deleted only if the caller may delete it (same rule as the single
  // DELETE /messages/:id — own message, or group/channel owner/admin). Ones it can't
  // delete are silently skipped. → { deleted:string[] } (only the ids actually deleted).
  deleteBatch: (messageIds) =>
    api.post("/v1/chat/messages/delete-batch", {
      messageIds: (messageIds || []).slice(0, 50),
    }),

  // R-CHAT (my mentions) — the caller's own @-mention notifications, newest first.
  //   GET /v1/chat/mentions?cursor=  →
  //     { results:[{ conversationId, conversationTitle, messageId, senderName,
  //                  snippet, createdAt, readAt }], nextCursor }
  // Source = NotificationLog (tenant + userId=caller + kind="in_app" + template="chat.mention");
  // the BE joins the Message (for the snippet) + Conversation (for the title) and drops rows
  // whose message/convo was deleted. Tenant-scoped, only the caller's mentions (no leak). Page ~20.
  mentions: (cursor) =>
    api.get("/v1/chat/mentions" + (cursor ? "?cursor=" + encodeURIComponent(cursor) : "")),

  // S-22 wave 2 — mention DIGEST: a per-conversation roll-up of the caller's
  // UNREAD @mentions (grouped counts + latest snippet), newest-active first.
  // Same source + gating as mentions() but grouped. →
  //   { groups:[{ conversationId, conversationTitle, count, latestAt,
  //               latestSnippet, latestSenderName }], totalUnread }.
  mentionsSummary: () => api.get("/v1/chat/mentions/summary"),
};

// R-CHAT media — resolve the browser-ready src for an attachment message. The backend
// re-mints a FRESH short-lived token-gated URL for the CURRENT viewer on every message read
// (message.attachmentUrl = …/stream?t=…, message.attachmentThumbUrl = …/thumb?t=…), so this
// helper just picks the right one: an IMAGE thumbnail prefers the (FE-constrained) thumb url,
// the lightbox / a FILE download uses the full stream url. Returns "" when the message has no
// usable attachment url (so an <img>/anchor never points at a broken path). The url already
// carries the /api prefix + the ?t= token — it loads directly in a plain <img src>/<a href>.
//   variant: "thumb" (default, list/bubble image) | "full" (lightbox image, file download).
export const attachmentSrc = (message, variant = "thumb") => {
  if (!message) return "";
  const isImage = message.kind === "image";
  if (variant === "thumb" && isImage && message.attachmentThumbUrl) {
    return message.attachmentThumbUrl;
  }
  return message.attachmentUrl || message.attachmentThumbUrl || "";
};

// R-CONNECT — academic social-connect layer (§social). Two primitives over the
// real /v1/connect/* API: FOLLOW (one-way, no approval) + CONNECT (symmetric,
// needs acceptance → an accepted connection unlocks direct DM), plus BLOCK
// (overrides everything). Every mutation is @AuditAction + P2002-guarded
// server-side; cross-tenant / self / missing target → 404 (no-leak); a block in
// either direction → 403 on connect/follow attempts. The single policy source
// (connect-policy.ts canDirectMessage/canConnect/canFollow) is resolved into the
// relationship payload the UI reads — the FE never re-implements the policy.
export const connectApi = {
  // --- CONNECT (symmetric; needs acceptance) ---
  // request → { status:"pending"|"accepted", connectionId, dmConversationId? }
  // (auto-accepts when a reverse request was already pending).
  request: ({ userId, note } = {}) =>
    api.post("/v1/connect/connections", { userId, ...(note ? { note } : {}) }),
  // respond (addressee only) → { status:"accepted"|"declined", connectionId,
  // dmConversationId? } (accept unlocks DM).
  respond: (connectionId, action) =>
    api.post("/v1/connect/connections/" + encodeURIComponent(connectionId) + "/respond", { action }),
  // remove an accepted connection OR cancel a pending outgoing request (by the
  // OTHER user's id, not the connectionId) → { ok:true }.
  removeConnection: (userId) =>
    api.delete("/v1/connect/connections/" + encodeURIComponent(userId)),

  // --- FOLLOW (one-way; no approval; idempotent) ---
  follow: (userId) => api.post("/v1/connect/follow/" + encodeURIComponent(userId), {}),
  unfollow: (userId) => api.delete("/v1/connect/follow/" + encodeURIComponent(userId)),

  // --- BLOCK (overrides everything; idempotent; tears down connection + the
  //     blocked user's follow) ---
  block: (userId) => api.post("/v1/connect/block/" + encodeURIComponent(userId), {}),
  unblock: (userId) => api.delete("/v1/connect/block/" + encodeURIComponent(userId)),

  // --- LISTS (no body) ---
  // → [{ connectionId, userId, fullName, avatarUrl, connectedAt }]
  connections: () => api.get("/v1/connect/connections"),
  // → [{ connectionId, note, requestedAt, userId, fullName, avatarUrl }]
  incoming: () => api.get("/v1/connect/requests/incoming"),
  outgoing: () => api.get("/v1/connect/requests/outgoing"),
  // → [{ userId, fullName, avatarUrl, since }]
  following: () => api.get("/v1/connect/following"),
  followers: () => api.get("/v1/connect/followers"),

  // --- RELATIONSHIP RESOLVER ---
  // → the relationship payload: { connected, pendingOutgoing, pendingIncoming,
  //   following, followsYou, blocked, blockedBy, canDirectMessage, canConnect,
  //   canFollow }.
  relationship: (userId) =>
    api.get("/v1/connect/relationship/" + encodeURIComponent(userId)),

  // --- DISCOVERY (connect-scoped people search + suggestions + block list) ---
  // PEOPLE SEARCH — the connect-scoped analog of peopleApi.search: same tenant,
  // EXCLUDES self + cross-tenant, never leaks a blocked-by user's detail beyond a
  // neutral row, and ATTACHES the relationship (same shape relationshipTo returns)
  // so each row can render the right action inline. q>=2 (else the server answers
  // an empty page). Cursor-paginated.
  //   → { results:[{ userId, fullName, avatarUrl, role, relationship }], nextCursor }
  search: ({ q, role, cursor } = {}) => {
    const p = [];
    if (q) p.push("q=" + encodeURIComponent(q));
    if (role) p.push("role=" + encodeURIComponent(role));
    if (cursor) p.push("cursor=" + encodeURIComponent(cursor));
    return api.get("/v1/connect/search" + (p.length ? "?" + p.join("&") : ""));
  },
  // SUGGESTIONS — up to 12 same-tenant users who share a CourseSection OR an
  // existing conversation with the caller but are NOT yet connected/following/
  // blocked. `reason` is a short Persian string («هم‌کلاسی در …» / «در یک گفت‌وگوی
  // مشترک»). Cheap, capped, no paging.
  //   → { results:[{ userId, fullName, avatarUrl, role, reason }] }
  suggestions: () => api.get("/v1/connect/suggestions"),
  // BLOCK LIST — the users the CALLER has blocked (unblock via the existing
  // DELETE /v1/connect/block/:userId).
  //   → { results:[{ userId, fullName, avatarUrl, blockedAt }] }
  blocks: () => api.get("/v1/connect/blocks"),

  // --- ENDORSEMENTS (skill endorsements on a profile's expertise/topics) ---
  // Idempotent toggle-ON: endorse a single skill (a short string, ≤60) on
  // another same-tenant user. P2002-guarded server-side, so re-endorsing the
  // same skill is a no-op → { endorsed:true }. Can't endorse yourself (the
  // server no-ops/400s; the UI hides the control on your own profile anyway).
  endorse: ({ userId, skill }) =>
    api.post("/v1/connect/endorse", { userId, skill }),
  // Toggle-OFF: remove your endorsement of that skill → { endorsed:false }
  // (200 even if it wasn't there). userId + skill travel as query params.
  unendorse: ({ userId, skill }) =>
    api.delete(
      "/v1/connect/endorse?userId=" +
        encodeURIComponent(userId) +
        "&skill=" +
        encodeURIComponent(skill),
    ),

  // --- PROFILE EXTRAS (privacy-gated rich aggregate for /u/:userId) ---
  // GET → { connectionCount, followerCount, followingCount, mutualConnections,
  //   sharedSections:[{id,title}], endorsements:[{skill,count,endorsedByMe}],
  //   achievements:[{...public}], recentActivity:[{...public}] }.
  // Server respects the target's profileVisibility: a private target (that is
  // not the viewer + not connected) returns ONLY the counts (no sections /
  // activity), and achievements/recentActivity are public-safe items only.
  profileExtras: (userId) =>
    api.get("/v1/connect/profile-extras/" + encodeURIComponent(userId)),
};

// R-SIS-7 — People directory + reusable UserPicker source (§9.3). admin/super_admin/
// faculty_admin. Read-only search (name/email, optional role filter) + person detail.
export const peopleApi = {
  search: ({ q, role } = {}) => {
    const p = [];
    if (q) p.push("q=" + encodeURIComponent(q));
    if (role) p.push("role=" + encodeURIComponent(role));
    return api.get("/v1/people" + (p.length ? "?" + p.join("&") : ""));
  },
  get: (id) => api.get("/v1/people/" + encodeURIComponent(id)),
};

// R-SIS-6 — کمیسیون موارد خاص (§8.10). admin/super_admin/faculty_admin. Detection-
// only enqueue (scan / manual) + 2-step decide→confirm; an "expel" dismisses the
// student. Server enforces faculty_admin scope + the distinct second approver.
export const commissionApi = {
  cases: (status) => api.get("/v1/commission/cases" + (status ? "?status=" + encodeURIComponent(status) : "")),
  get: (id) => api.get("/v1/commission/cases/" + encodeURIComponent(id)),
  scan: (includeNear) => api.post("/v1/commission/scan" + (includeNear ? "?includeNear=true" : ""), {}),
  enqueue: (userId) => api.post("/v1/commission/enqueue", { userId }),
  review: (id) => api.post("/v1/commission/cases/" + encodeURIComponent(id) + "/review", {}),
  decide: (id, decision, note) => api.post("/v1/commission/cases/" + encodeURIComponent(id) + "/decide", { decision, note }),
  confirm: (id) => api.post("/v1/commission/cases/" + encodeURIComponent(id) + "/confirm", {}),
};

// R-COMPLY W2c — faculty dean reports (faculty-scoped aggregates for a term).
// GET /v1/faculty-reports?termId=<id> → { termId, facultyId, kpis:{students,
// instructors, passRate, avgGpa, capacityFilledPct}, byDepartment:[...],
// byProgram:[...] }. faculty_admin → own faculty; admin may pass ?facultyId.
export const facultyReportsApi = {
  get: ({ termId, facultyId } = {}) => {
    const q = [];
    if (termId) q.push("termId=" + encodeURIComponent(termId));
    if (facultyId) q.push("facultyId=" + encodeURIComponent(facultyId));
    return api.get("/v1/faculty-reports" + (q.length ? "?" + q.join("&") : ""));
  },
};

// W2 (D-2026-06-14) — instructor's own assigned sections (teaching load).
// GET /v1/sections/mine → [{ id, course, term, schedule, capacity, seatsTaken,
// _count.enrollments, instructor }]. Used by the instructor dashboard.
// W6 (D-2026-06-14) — proctored online exams (/v1/exams).
export const examsApi = {
  start: (assessmentId, identity = {}) =>
    api.post("/v1/exams/" + encodeURIComponent(assessmentId) + "/start", identity),
  my: (assessmentId) => api.get("/v1/exams/" + encodeURIComponent(assessmentId) + "/my"),
  integrity: (attemptId, kind) =>
    api.post("/v1/exams/attempts/" + encodeURIComponent(attemptId) + "/integrity", { kind }),
  // R-EXAM-2 — autosave in-progress answers (draft) for crash/reload recovery
  save: (attemptId, answers) =>
    api.post("/v1/exams/attempts/" + encodeURIComponent(attemptId) + "/save", { answers }),
  submit: (attemptId, body) =>
    api.post("/v1/exams/attempts/" + encodeURIComponent(attemptId) + "/submit", body),
  // proctor (staff): all attempts for an exam with identity + integrity trail
  attempts: (assessmentId) =>
    api.get("/v1/exams/" + encodeURIComponent(assessmentId) + "/attempts"),
  // R-EXAM-3 (§13.6) — record a proctor's human decision on an attempt
  proctorDecision: (attemptId, { decision, note }) =>
    api.patch("/v1/exams/attempts/" + encodeURIComponent(attemptId) + "/proctor", { decision, note }),
  // R-EXAM-3 — per-question item analysis for the exam (staff)
  analysis: (assessmentId) =>
    api.get("/v1/exams/" + encodeURIComponent(assessmentId) + "/analysis"),
};

// S-13 — Online-Judge verdicts for a submission's `code` questions. Role-scoped
// server-side: the owning student gets a safe view (hidden cases collapsed), course
// staff get full per-case detail, anyone else → 404.
export const judgeApi = {
  forSubmission: (submissionId) =>
    api.get("/v1/judge/submissions/" + encodeURIComponent(submissionId)),
};

export const sectionsApi = {
  mine: (termId) =>
    api.get("/v1/sections/mine" + (termId ? "?termId=" + encodeURIComponent(termId) : "")),
  // Academic roles (admin/super_admin/faculty_admin — faculty-scoped server-side).
  list: (termId) =>
    api.get("/v1/sections" + (termId ? "?termId=" + encodeURIComponent(termId) : "")),
  // W3 — dedicated audited instructor (re)assignment for «تخصیص استاد».
  assignInstructor: (id, instructorId) =>
    api.patch("/v1/sections/" + encodeURIComponent(id) + "/instructor", { instructorId }),
};

// R-GRADE-1 — official grade workflow (§7.12 موقت→اعلام→اعتراض→قطعی).
// Instructor surface is ownership-enforced server-side; students see announced
// grades via my() and may object once while the window is open.
export const gradeSubmissionApi = {
  my: () => api.get("/v1/grade-submission/my"),
  sections: (termId) =>
    api.get("/v1/grade-submission/sections" + (termId ? "?termId=" + encodeURIComponent(termId) : "")),
  section: (id) => api.get("/v1/grade-submission/sections/" + encodeURIComponent(id)),
  // تحلیل کلاس — real per-section analytics derived from finalGrade (enrolled,
  // graded, average, passRate, distribution[], atRisk[]). Ownership-enforced.
  analytics: (id) =>
    api.get("/v1/grade-submission/sections/" + encodeURIComponent(id) + "/analytics"),
  saveGrades: (id, grades) =>
    api.patch("/v1/grade-submission/sections/" + encodeURIComponent(id) + "/grades", { grades }),
  announce: (id, objectionDeadline) =>
    api.post("/v1/grade-submission/sections/" + encodeURIComponent(id) + "/announce", { objectionDeadline }),
  finalize: (id) => api.post("/v1/grade-submission/sections/" + encodeURIComponent(id) + "/finalize", {}),
  object: (id, text) =>
    api.post("/v1/grade-submission/sections/" + encodeURIComponent(id) + "/object", { text }),
  reply: (objectionId, text) =>
    api.post("/v1/grade-submission/objections/" + encodeURIComponent(objectionId) + "/reply", { text }),
  // S6 — admin post-قطعی correction of ONE student's grade (+ mandatory reason);
  // records old→new in GradeCorrection and writes an AuditLog row.
  correct: (sectionId, studentUserId, finalGrade, reason) =>
    api.post(
      "/v1/grade-submission/sections/" +
        encodeURIComponent(sectionId) +
        "/enrollments/" +
        encodeURIComponent(studentUserId) +
        "/correct",
      { finalGrade, reason },
    ),
  // S6 — the section's correction history (staff read).
  corrections: (sectionId) =>
    api.get("/v1/grade-submission/sections/" + encodeURIComponent(sectionId) + "/corrections"),
  // S-14 — exam→formal-grade bridge. components() reads the course weighting
  // scheme + published assessments; saveComponents() replaces it; importPreview()
  // shows proposed 0..20 grades from exam scores WITHOUT writing; importGrades()
  // writes them as a DRAFT (overwrite=false fills only empty rows). Never
  // announces/finalizes — the instructor still reviews/edits/announces/finalizes.
  components: (sectionId) =>
    api.get("/v1/grade-submission/sections/" + encodeURIComponent(sectionId) + "/components"),
  saveComponents: (sectionId, components, assignments) =>
    api.put("/v1/grade-submission/sections/" + encodeURIComponent(sectionId) + "/components", {
      components,
      assignments,
    }),
  importPreview: (sectionId) =>
    api.get("/v1/grade-submission/sections/" + encodeURIComponent(sectionId) + "/import-preview"),
  importGrades: (sectionId, overwrite) =>
    api.post(
      "/v1/grade-submission/sections/" + encodeURIComponent(sectionId) + "/import-from-assessments",
      { overwrite: !!overwrite },
    ),
};

// R-MED-1 — stage-based medical progress (پزشکی/دندان/دارو). Student reads own
// stage path; staff (admin/super_admin/faculty_admin, faculty-scoped) records
// gate-exam results / advances stages. Advancement is never automatic.
export const stageProgressApi = {
  me: () => api.get("/v1/stage-progress/me"),
  student: (userId) => api.get("/v1/stage-progress/students/" + encodeURIComponent(userId)),
  list: (programId) => api.get("/v1/stage-progress" + (programId ? "?programId=" + encodeURIComponent(programId) : "")),
  ready: (userId, stageSeq) =>
    api.post("/v1/stage-progress/students/" + encodeURIComponent(userId) + "/ready", { stageSeq }),
  // R-MED — record the EXIT gate result. The BE GateDto is { stageSeq, score:0..20,
  // passed, note? } and timestamps gatePassedAt itself; it does NOT accept examDate
  // (forbidNonWhitelisted would reject it), so we never send one.
  gate: (userId, { stageSeq, score, passed, note }) =>
    api.post("/v1/stage-progress/students/" + encodeURIComponent(userId) + "/gate", { stageSeq, score, passed, note }),
  advance: (userId, stageSeq, note) =>
    api.post("/v1/stage-progress/students/" + encodeURIComponent(userId) + "/advance", { stageSeq, note }),
  // R-MED-DEEP — record the ENTRY آزمون جامع (comprehensive-exam) result for a stage
  // whose requiresComprehensiveExam is true. Target that gated stage's OWN seq.
  // { stageSeq, score:0..20, passed, note? }; the BE timestamps compExamPassedAt
  // itself. Advancing the PRIOR stage INTO this one is blocked until passed.
  comprehensiveExam: (userId, { stageSeq, score, passed, note }) =>
    api.post(
      "/v1/stage-progress/students/" + encodeURIComponent(userId) + "/comprehensive-exam",
      { stageSeq, score, passed, note },
    ),
};

// R-MED-W4 — ProgramStage STRUCTURE CRUD (the seq + gate-exam + minCredits a
// STAGE_BASED medical program is built from). admin/super_admin DEFINE the
// structure; faculty_admin (ریاست دانشکده) READS their own faculty's stages via
// list() but every mutation is admin-only (least-privilege, enforced server-side).
// list requires a programId; reorder takes the FULL ordered id list (1..n).
export const programStagesApi = {
  list: (programId) =>
    api.get("/v1/program-stages" + (programId ? "?programId=" + encodeURIComponent(programId) : "")),
  create: (data) => api.post("/v1/program-stages", data), // { programId, seq, name, gateExam?, minCredits? }
  update: (id, data) => api.patch("/v1/program-stages/" + encodeURIComponent(id), data), // { seq?, name?, gateExam?, minCredits? }
  reorder: (programId, orderedStageIds) =>
    api.post("/v1/program-stages/reorder", { programId, orderedStageIds }),
  remove: (id) => api.delete("/v1/program-stages/" + encodeURIComponent(id)),
};

// WELL-1 — wellness (سلامت و مشاوره) self-assessment. The student takes a
// psychometric test (PHQ-9/GAD-7/WHO-5/Rosenberg) and reads ONLY their own
// attempts/results (server self-scopes: 404 on anyone else's). tests/testDetail
// are the catalogue + take-page reads; start/answer/submit drive an attempt
// (answer autosaves a single item; submit scores + returns the result, with
// crisis resources inline when result.safetyFlag is true). resultsMine/result
// are the self-only history; resources is the always-available crisis +
// psychoeducation safety floor (any authenticated user).
export const wellnessApi = {
  tests: (category) =>
    api.get("/v1/wellness/tests" + (category ? "?category=" + encodeURIComponent(category) : "")),
  testDetail: (id) => api.get("/v1/wellness/tests/" + encodeURIComponent(id)),
  start: (testId) => api.post("/v1/wellness/tests/" + encodeURIComponent(testId) + "/start", {}),
  // answer = { questionId, value?, selectedCategories? } — one autosaved item.
  answer: (attemptId, answer) =>
    api.post("/v1/wellness/attempts/" + encodeURIComponent(attemptId) + "/answer", answer),
  // submit may carry a final `answers` array to flush the last autosave; omit otherwise.
  submit: (attemptId, answers) =>
    api.post(
      "/v1/wellness/attempts/" + encodeURIComponent(attemptId) + "/submit",
      answers ? { answers } : {},
    ),
  resultsMine: () => api.get("/v1/wellness/results/mine"),
  result: (id) => api.get("/v1/wellness/results/" + encodeURIComponent(id)),
  resources: () => api.get("/v1/wellness/resources"),
  // WELL-3 — daily mood check-in (idempotent per day) + trend + dashboard roll-up.
  // recordMood = { value: 1..5, note? } → the recorded/updated entry.
  recordMood: ({ value, note }) =>
    api.post("/v1/wellness/mood", note !== undefined ? { value, note } : { value }),
  // moodTrend → the caller's recent mood entries (oldest→newest) for the sparkline.
  moodTrend: (days) =>
    api.get("/v1/wellness/mood/trend" + (days !== undefined ? "?days=" + encodeURIComponent(days) : "")),
  // summary → self-scoped dashboard roll-up (recentMood, streak, avgMood, latestResults, upcoming).
  summary: () => api.get("/v1/wellness/summary"),
};

// WELL-5 — AI counselor (روان‌شناس هوش مصنوعی). Self-scoped chat on the ai-gateway.
// The backend runs an INDEPENDENT crisis detector, so a crisis reply + resources
// come back even in mock mode; `fallback:true` (or a thrown error) → the client
// uses its offline keyword engine so the chat never dies.
export const aiCounselApi = {
  // message({ message, conversationId?, useTestResults? }) →
  //   { conversationId, reply, suggestions, safetyFlag, crisis?, fallback? }.
  message: ({ message, conversationId, useTestResults }) =>
    api.post("/v1/wellness/counselor/message", { message, conversationId, useTestResults }),
  // history(conversationId?) → { conversationId, messages:[{ id, role, content, safetyFlag, createdAt }] }.
  history: (conversationId) =>
    api.get(
      "/v1/wellness/counselor/history" +
        (conversationId ? "?conversationId=" + encodeURIComponent(conversationId) : ""),
    ),
};

// WELL-4 — counseling (مشاوره). Two audiences over the /wellness prefix:
//   * STUDENT: counselors (booking picker) + book + mine + cancel-own.
//   * COUNSELOR (least-privilege): inbox + updateAppt + crisisQueue + ackCrisis
//     + studentSummary (LINKED student only). All authz/scoping is server-side.
export const counselingApi = {
  // --- counselor self profile (R-profile-rework; self-only, role counselor) ---
  // getOwn → the calling counselor's OWN WellnessCounselor row, normalised:
  //   { id, specialtyFa, bioFa, topics:string[], isActive }. 404 if the caller is
  //   not a counselor (the profile page swallows the 404 and hides the section).
  getOwn: () => api.get("/v1/wellness/counselor/me"),
  // updateOwn(data) → self-edit the OWN row. Body keys match UpdateCounselorSelfDto:
  //   { specialtyFa?, bioFa?, topicsJson?:string[], isActive? } — NOTE the array key is
  //   `topicsJson` (the global forbidNonWhitelisted pipe rejects a stray `topics` key).
  //   Returns the same normalised shape as getOwn (with `topics`) so the page re-renders.
  updateOwn: (data) => api.patch("/v1/wellness/counselor/me", data),

  // --- student booking ---
  // counselors → active counselor directory: [{ id, name, specialtyFa, bioFa, topics }].
  counselors: () => api.get("/v1/wellness/counselors"),
  // book = { counselorId, slotAt (ISO-8601), mode: 'online'|'in_person', note? }.
  // studentId is the caller (never sent). Returns the created 'requested' appointment.
  book: ({ counselorId, slotAt, mode, note }) =>
    api.post("/v1/wellness/appointments", { counselorId, slotAt, mode, ...(note !== undefined ? { note } : {}) }),
  // mine → the student's own appointments (+ counselorName, counselorSpecialtyFa).
  mine: () => api.get("/v1/wellness/appointments/mine"),
  // --- counselor desk ---
  // inbox → the counselor's own appointments (+ studentName); optional status filter.
  inbox: (status) =>
    api.get("/v1/wellness/appointments/inbox" + (status ? "?status=" + encodeURIComponent(status) : "")),
  // updateAppt(id, status): counselor confirm/cancel/done (own); student cancel (own requested).
  updateAppt: (id, status) =>
    api.patch("/v1/wellness/appointments/" + encodeURIComponent(id), { status }),
  // crisisQueue → the tenant's OPEN/ACK crisis alerts, scoped triage view
  //   [{ id, studentId, studentName, severity, status, testNameFa, band, ... }].
  crisisQueue: () => api.get("/v1/wellness/crisis-queue"),
  // ackCrisis(id, status): status 'acknowledged' | 'closed'.
  ackCrisis: (id, status) =>
    api.patch("/v1/wellness/crisis-queue/" + encodeURIComponent(id), { status }),
  // studentSummary(studentId) → scoped wellness summary for a LINKED student
  //   ({ student, latestBands, moodTrend, avgMood, openAlertCount }); 404 if unlinked.
  studentSummary: (studentId) =>
    api.get("/v1/wellness/student/" + encodeURIComponent(studentId) + "/summary"),
  // --- R-MON W9: counselor monitoring/reporting (own caseload, least-privilege) ---
  // caseload → THIS counselor's roster of ongoing students (LIGHT snapshot, NO
  //   bands/scores): [{ studentId, name, openAlerts, lastAppointment, lastMood, testsTaken }].
  caseload: () => api.get("/v1/wellness/caseload"),
  // caseloadReport → AGGREGATE roll-up over the same caseload — counts/means ONLY,
  //   no per-student rows/names/scores: { caseloadSize, studentsWithOpenAlerts,
  //   openAlertsTotal, alertsBySeverity, appointmentsByStatus, avgMood30d,
  //   testCompletionsByCategory }.
  caseloadReport: () => api.get("/v1/wellness/caseload/report"),
  // studentTimeline(studentId) → unified chronological continuity-feed for a LINKED
  //   student (404 unlinked); privacy-projected items discriminated by `type`
  //   (appointment | test{band} | mood{value} | alert{severity,status}). NO scores/answers.
  studentTimeline: (studentId) =>
    api.get("/v1/wellness/student/" + encodeURIComponent(studentId) + "/timeline"),
  // scheduleFollowup(studentId, body) → counselor schedules a follow-up for a LINKED
  //   student (404 unlinked / no profile). body = { slotAt (ISO), mode, note? };
  //   created 'confirmed' (no student-request step). Returns the created appointment.
  scheduleFollowup: (studentId, { slotAt, mode, note }) =>
    api.post("/v1/wellness/caseload/" + encodeURIComponent(studentId) + "/appointments", {
      slotAt,
      mode,
      ...(note !== undefined ? { note } : {}),
    }),
};

// R-SIS-5 — service desk (پیشخوان §17). Student + staff request surface +
// admin workflow builder. All authz/state-machine is server-side.
export const serviceDeskApi = {
  types: () => api.get("/v1/service-requests/types"),
  mine: (status) =>
    api.get("/v1/service-requests/mine" + (status ? "?status=" + encodeURIComponent(status) : "")),
  pending: () => api.get("/v1/service-requests/pending-approval"),
  get: (id) => api.get("/v1/service-requests/" + encodeURIComponent(id)),
  create: (data) => api.post("/v1/service-requests", data),
  submit: (id) => api.post("/v1/service-requests/" + encodeURIComponent(id) + "/submit"),
  resubmit: (id) => api.post("/v1/service-requests/" + encodeURIComponent(id) + "/resubmit"),
  cancel: (id) => api.post("/v1/service-requests/" + encodeURIComponent(id) + "/cancel"),
  action: (id, decision, note) =>
    api.post("/v1/service-requests/" + encodeURIComponent(id) + "/action", { decision, note }),
  // admin builder
  typesAdmin: () => api.get("/v1/service-request-types"),
  createType: (data) => api.post("/v1/service-request-types", data),
  updateType: (id, data) => api.patch("/v1/service-request-types/" + encodeURIComponent(id), data),
  deleteType: (id) => api.delete("/v1/service-request-types/" + encodeURIComponent(id)),
  setSteps: (id, steps) =>
    api.patch("/v1/service-request-types/" + encodeURIComponent(id) + "/steps", { steps }),
};

// ---------- applications (Phase B R3.b D71) ----------
//
// Two parallel surfaces: studentApplications + instructorApplications.
// The public POST (publicSubmitStudent / publicSubmitInstructor) is
// the only un-authenticated path; everything else is admin-gated or
// SelfOrAdmin per Q7.a.
//
// Public submission is rate-limited (5/IP/hour) + idempotent on
// (tenantId, applicantEmail, programId|departmentId) per Q8.a.
// Spam-flag NotificationLog rows surface in notificationLogsApi.list.

export const studentApplicationsApi = {
  // Public submission. Returns the application row + an _idempotent flag
  // indicating whether it was a new create (false) or an existing-row
  // return (true). Caller can branch on that for UX.
  publicSubmit: (data) => publicApi.post("/v1/applications/student", data),

  // Phase B R6 (D80) — public anon tracking by token (no auth).
  trackByToken: (token) =>
    publicApi.get("/v1/applications/student/track?token=" + encodeURIComponent(token)),
  withdrawByToken: (token) =>
    publicApi.post("/v1/applications/student/track/withdraw", { token }),

  // Own application (any authenticated user; 404 if no app).
  getOwn: () => api.get("/v1/applications/student/me"),

  // SelfOrAdmin withdraw (applicant withdraws own; admin withdraws any).
  withdraw: (id) => api.post("/v1/applications/student/" + encodeURIComponent(id) + "/withdraw"),

  // Admin list + filters (status, programId).
  list: ({ status, programId } = {}) => {
    const q = [];
    if (status) q.push("status=" + encodeURIComponent(status));
    if (programId) q.push("programId=" + encodeURIComponent(programId));
    return api.get("/v1/applications/student" + (q.length ? "?" + q.join("&") : ""));
  },
  get: (id) => api.get("/v1/applications/student/" + encodeURIComponent(id)),

  // Admin transition. Backend enforces state machine + Q4.a verification
  // gate + Q5.a transactional side effect on ENROLLED.
  transition: (id, to) =>
    api.post("/v1/applications/student/" + encodeURIComponent(id) + "/transition", { to }),

  // Q4.a admin escape hatch — manually flag verification.
  verifyEmail: (id, verified = true) =>
    api.patch("/v1/applications/student/" + encodeURIComponent(id) + "/verify-email", { verified }),
  verifyPhone: (id, verified = true) =>
    api.patch("/v1/applications/student/" + encodeURIComponent(id) + "/verify-phone", { verified }),

  // Phase B R4 (D73 Q1.a) — admin sets the target CourseOffering the
  // accepted applicant enrolls into on ENROLLED. Pass null to clear
  // (→ Student-only, no regression).
  setTargetOffering: (id, offeringId) =>
    api.patch("/v1/applications/student/" + encodeURIComponent(id) + "/target-offering", { offeringId }),

  delete: (id) => api.delete("/v1/applications/student/" + encodeURIComponent(id)),
};

export const instructorApplicationsApi = {
  publicSubmit: (data) => publicApi.post("/v1/applications/instructor", data),
  // Phase B R6 (D80) — public anon tracking by token (no auth).
  trackByToken: (token) =>
    publicApi.get("/v1/applications/instructor/track?token=" + encodeURIComponent(token)),
  withdrawByToken: (token) =>
    publicApi.post("/v1/applications/instructor/track/withdraw", { token }),
  getOwn: () => api.get("/v1/applications/instructor/me"),
  withdraw: (id) => api.post("/v1/applications/instructor/" + encodeURIComponent(id) + "/withdraw"),
  list: ({ status, departmentId } = {}) => {
    const q = [];
    if (status) q.push("status=" + encodeURIComponent(status));
    if (departmentId) q.push("departmentId=" + encodeURIComponent(departmentId));
    return api.get("/v1/applications/instructor" + (q.length ? "?" + q.join("&") : ""));
  },
  get: (id) => api.get("/v1/applications/instructor/" + encodeURIComponent(id)),
  transition: (id, to) =>
    api.post("/v1/applications/instructor/" + encodeURIComponent(id) + "/transition", { to }),
  verifyEmail: (id, verified = true) =>
    api.patch("/v1/applications/instructor/" + encodeURIComponent(id) + "/verify-email", { verified }),
  verifyPhone: (id, verified = true) =>
    api.patch("/v1/applications/instructor/" + encodeURIComponent(id) + "/verify-phone", { verified }),
  delete: (id) => api.delete("/v1/applications/instructor/" + encodeURIComponent(id)),
};

// ---------- public catalog (Phase B R6 D82) ----------
// Anon program list for the public /apply form. Uses publicApi (no auth):
// GET /v1/programs/public?tenantSlug= → [{ id, slug, name, nameEn,
// degreeLevel, department }] (active programs only). The only public read
// in the catalog surface; program data is non-sensitive marketing info.
export const publicCatalogApi = {
  listPrograms: (tenantSlug) =>
    publicApi.get("/v1/programs/public?tenantSlug=" + encodeURIComponent(tenantSlug)),
  // R-AUTH-2 — whether the /apply form should surface + require the email-OTP
  // step (mirrors the server gate; off until a real email provider is wired).
  applyConfig: () => publicApi.get("/v1/applications/student/config"),
};

// ---------- mock هدی identity verification preview (D-2026-06-14) ----------
// Public. Lets the registration wizard show the 3-tier (سه‌سطحی) result live
// before submit. POST { fullName, nationalId?, birthCertNo?, birthDate? } →
// { nationalIdValid, level, status, ref, detail:{ tiers:[...] } }.
export const identityVerificationApi = {
  preview: (body) => publicApi.post("/v1/identity-verification/preview", body),
};

// ---------- admin: application documents (D-2026-06-14) ----------
// GET the uploaded کارت ملی/شناسنامه/مدارک/رزومه for an application (admin).
export const applicationDocsApi = {
  student: (id) => api.get("/v1/applications/student/" + id + "/documents"),
  instructor: (id) => api.get("/v1/applications/instructor/" + id + "/documents"),
};

// ---------- S-18 — HR / hiring (استخدام) ----------
// Staff hiring pipeline (application → docs review → verify → contract → salary
// → active). Access matrix is server-enforced: applications/contract/lifecycle =
// admin/faculty_admin; salary assignment = finance_manager/super_admin only.
export const hrApi = {
  me: () => api.get("/v1/hr/me"),
  listApplications: ({ status, targetRole } = {}) => {
    const q = [];
    if (status) q.push("status=" + encodeURIComponent(status));
    if (targetRole) q.push("targetRole=" + encodeURIComponent(targetRole));
    return api.get("/v1/hr/applications" + (q.length ? "?" + q.join("&") : ""));
  },
  getApplication: (id) => api.get("/v1/hr/applications/" + encodeURIComponent(id)),
  createApplication: (data) => api.post("/v1/hr/applications", data),
  documents: (id) => api.get("/v1/hr/applications/" + encodeURIComponent(id) + "/documents"),
  reviewDocument: (id, docId, decision, note) =>
    api.post(
      "/v1/hr/applications/" + encodeURIComponent(id) + "/documents/" + encodeURIComponent(docId) + "/review",
      { decision, note },
    ),
  transition: (id, to) => api.post("/v1/hr/applications/" + encodeURIComponent(id) + "/transition", { to }),
  verifyEmail: (id, verified = true) =>
    api.patch("/v1/hr/applications/" + encodeURIComponent(id) + "/verify-email", { verified }),
  verifyPhone: (id, verified = true) =>
    api.patch("/v1/hr/applications/" + encodeURIComponent(id) + "/verify-phone", { verified }),
  createContract: (id, data) => api.post("/v1/hr/applications/" + encodeURIComponent(id) + "/contract", data),
  deleteApplication: (id) => api.delete("/v1/hr/applications/" + encodeURIComponent(id)),
  listContracts: ({ userId, status } = {}) => {
    const q = [];
    if (userId) q.push("userId=" + encodeURIComponent(userId));
    if (status) q.push("status=" + encodeURIComponent(status));
    return api.get("/v1/hr/contracts" + (q.length ? "?" + q.join("&") : ""));
  },
  getContract: (id) => api.get("/v1/hr/contracts/" + encodeURIComponent(id)),
  assignSalary: (id) => api.post("/v1/hr/contracts/" + encodeURIComponent(id) + "/assign-salary", {}),
  terminateContract: (id, reason) =>
    api.post("/v1/hr/contracts/" + encodeURIComponent(id) + "/terminate", { reason }),
};

// ---------- public هیئت علمی directory (D-2026-06-14) ----------
// Anon faculty directory for the /faculty page: GET /v1/instructors/public?
// tenantSlug= → { total, members:[{ fullName, rankLabel, expertise[],
// departmentName, facultyName, schoolName, schoolId, courseCount }], schools }.
// Real Instructor data (rank + department→بخش→دانشکده), no PII.
export const facultyDirectoryApi = {
  list: (tenantSlug) =>
    publicApi.get("/v1/instructors/public?tenantSlug=" + encodeURIComponent(tenantSlug)),
};

// ---------- plans / تعرفه‌ها (D-2026-06-14) ----------
// public(): anon /pricing data. The admin CRUD uses the authed `api` client.
export const plansApi = {
  public: (tenantSlug) =>
    publicApi.get("/v1/plans/public?tenantSlug=" + encodeURIComponent(tenantSlug)),
  list: () => api.get("/v1/plans"),
  create: (body) => api.post("/v1/plans", body),
  update: (id, body) => api.patch("/v1/plans/" + id, body),
  remove: (id) => api.del("/v1/plans/" + id),
};

// ---------- entitlements / سقف پیام و امکانات پلن (Phase B, D-2026-06-30) ----------
// me(): the signed-in user's effective plan + AI message quota + usage. Powers
// the quota meter + plan badge + near-cap upgrade CTA on the AI surfaces.
export const entitlementsApi = {
  me: () => api.get("/v1/entitlements/me"),
};

// ---------- S9 / S-9 — self-serve subscription flow ----------
// mine(): current active subscription (or {active:false}).
// context(planId, period): preview what a checkout would do — relation
//   (none/same/upgrade/downgrade/lateral), prorated charge/credit, walletEnabled.
// checkout(planId, period, payFrom): start/apply a purchase → free/zero-net or a
//   downgrade/wallet payment → {activated:true, ...}; a paid gateway plan →
//   { redirectUrl, isMock, orderId, amount } → FE sends the browser to redirectUrl.
// cancel(): end the current subscription (falls back to the free plan).
export const subscriptionsApi = {
  mine: () => api.get("/v1/subscriptions/mine"),
  context: (planId, period) =>
    api.get(
      "/v1/subscriptions/context?planId=" +
        encodeURIComponent(planId) +
        (period ? "&period=" + encodeURIComponent(period) : ""),
    ),
  checkout: (planId, period, payFrom) =>
    api.post("/v1/subscriptions/checkout", {
      planId,
      ...(period ? { period } : {}),
      ...(payFrom ? { payFrom } : {}),
    }),
  cancel: () => api.post("/v1/subscriptions/cancel", {}),
};

// ---------- کیف پول (S9-wallet) ----------
// Per-user wallet on the double-entry ledger. mine() → { walletId, balance,
// currency, transactions[] }. topup() → { orderId, redirectUrl, isMock } (send the
// browser to redirectUrl; the @Public callback settles + 302s to /wallet?topup=…).
// payTuition() debits the wallet against a bill (server clamps to remaining/balance;
// 400 on insufficient funds). Staff (finance_manager/super_admin): userWallet /
// credit / adjust.
export const walletApi = {
  mine: () => api.get("/v1/wallet/me"),
  topup: (amount) => api.post("/v1/wallet/topup", { amount }),
  payTuition: (billId, amount) =>
    api.post("/v1/wallet/pay/tuition", amount != null ? { billId, amount } : { billId }),
  userWallet: (userId) => api.get("/v1/wallet/user/" + encodeURIComponent(userId)),
  credit: (userId, { amount, reason, source } = {}) =>
    api.post("/v1/wallet/user/" + encodeURIComponent(userId) + "/credit", {
      amount,
      reason,
      ...(source ? { source } : {}),
    }),
  adjust: (userId, { amount, reason } = {}) =>
    api.post("/v1/wallet/user/" + encodeURIComponent(userId) + "/adjust", { amount, reason }),
  // S-W6 — money-integrity reconciliation status for the caller's tenant (read-only,
  // finance_manager/super_admin). Returns { ok, walletDrift, ledgerNet, clearingDrift, … }.
  reconciliation: () => api.get("/v1/wallet/reconciliation"),
  // S-W5 — bank withdrawal (payout). request holds the amount; staff approve/reject.
  requestWithdrawal: ({ amount, ibanSheba, accountHolderName } = {}) =>
    api.post("/v1/wallet/withdraw", { amount, ibanSheba, accountHolderName }),
  myWithdrawals: () => api.get("/v1/wallet/withdrawals"),
  withdrawalQueue: () => api.get("/v1/wallet/withdrawals/queue"),
  approveWithdrawal: (id, bankTraceId) =>
    api.post("/v1/wallet/withdrawals/" + encodeURIComponent(id) + "/approve", { bankTraceId }),
  rejectWithdrawal: (id, reason) =>
    api.post("/v1/wallet/withdrawals/" + encodeURIComponent(id) + "/reject", { reason }),
};

// ---------- AI agents / دستیارهای هوشمند (Phase C, D-2026-06-30) ----------
// The "star" assistants: 6 kinds (general | field_specialist | exercise |
// career | research | mentor). kinds() returns each kind + whether the user's
// plan unlocks it. chat() consumes the monthly AI quota (403 ai_quota_exceeded)
// and is gated per-kind (403 feature_not_in_plan) — the UI turns both into an
// upgrade CTA. Model is mock + pluggable behind ai-gateway (no cloud).
export const agentsApi = {
  kinds: () => api.get("/v1/agents/kinds"),
  listSessions: () => api.get("/v1/agents/sessions"),
  getSession: (id) => api.get("/v1/agents/sessions/" + encodeURIComponent(id)),
  createSession: ({ agentKind, courseId, title } = {}) =>
    api.post("/v1/agents/sessions", {
      agentKind,
      ...(courseId ? { courseId } : {}),
      ...(title ? { title } : {}),
    }),
  // S-19 — optional grounding context (courseId/assessmentId/page) is validated
  // server-side against the caller's tenant before it reaches the gateway.
  chat: (id, { message, courseId, assessmentId, page } = {}) =>
    api.post("/v1/agents/sessions/" + encodeURIComponent(id) + "/chat", {
      message,
      ...(courseId ? { courseId } : {}),
      ...(assessmentId ? { assessmentId } : {}),
      ...(page ? { page } : {}),
    }),
  deleteSession: (id) => api.del("/v1/agents/sessions/" + encodeURIComponent(id)),
};

// ---------- AI agent channels (Phase C4 → S1) ----------
// Link external messengers (telegram live; whatsapp/bale/rubika/sms scaffolded)
// to the agent chat. link() is plan-gated server-side (403 channel_not_in_plan);
// for telegram it returns { linkCode, botUsername?, instructions } — the FE
// renders a one-tap t.me deep-link (https://t.me/<bot>?start=<code>).
export const channelsApi = {
  list: () => api.get("/v1/channels"),
  link: (channel) => api.post("/v1/channels/" + encodeURIComponent(channel) + "/link", {}),
  unlink: (id) => api.del("/v1/channels/" + encodeURIComponent(id)),
};

// ---------- verification (R-AUTH-2 — applicant OTP at /apply) ----------
// Public (applicants have no account). request() issues + sends a 6-digit code;
// verify() returns { verified, token } — the token goes into the application
// submit (emailVerificationToken) to stamp applicantEmailVerifiedAt.
export const verificationApi = {
  request: ({ tenantSlug, channel = "email", target }) =>
    publicApi.post("/v1/verification/request", { tenantSlug, channel, target }),
  verify: ({ tenantSlug, channel = "email", target, code }) =>
    publicApi.post("/v1/verification/verify", { tenantSlug, channel, target, code }),
};

// ---------- notifications (R-NAV-5 — in-app inbox over R-Notif) ----------
// The bell + /inbox read the current user's own notifications.
//   list   → { items:[{id,type,title,body,kind,read,createdAt}], total,
//             unreadCount, page, limit }
//   markRead / markAllRead → flip readAt server-side (no body needed).
export const notificationsApi = {
  list: ({ unread, page, limit } = {}) => {
    const q = [];
    if (unread) q.push("unread=true");
    if (page) q.push("page=" + encodeURIComponent(page));
    if (limit) q.push("limit=" + encodeURIComponent(limit));
    return api.get("/v1/notifications" + (q.length ? "?" + q.join("&") : ""));
  },
  markRead: (id) => api.patch("/v1/notifications/" + encodeURIComponent(id) + "/read"),
  markAllRead: () => api.patch("/v1/notifications/read-all"),
};

// R-SUPPORT — IT/account support tickets (helpdesk §10). Requester self-data;
// support/admin/super_admin agents see + manage the tenant queue.
export const supportTicketsApi = {
  list: ({ status, mine } = {}) => {
    const q = [];
    if (status) q.push("status=" + encodeURIComponent(status));
    if (mine) q.push("mine=true");
    return api.get("/v1/support-tickets" + (q.length ? "?" + q.join("&") : ""));
  },
  get: (id) => api.get("/v1/support-tickets/" + encodeURIComponent(id)),
  create: (body) => api.post("/v1/support-tickets", body),
  reply: (id, body) => api.post("/v1/support-tickets/" + encodeURIComponent(id) + "/messages", { body }),
  update: (id, data) => api.patch("/v1/support-tickets/" + encodeURIComponent(id), data),
};

// R-MON W3 — support people pillar. A tickets-only role: the backend scopes a
// SUPPORT caller to ticket-requesters in their queue (admin/super_admin are
// tenant-wide) and 404s any action on a non-requester — the FE re-implements NO
// scope. `users(q)` returns ONLY minimal account fields (id/fullName/email/roles/
// active/locked/failedLogins); resetPassword/unlock reuse the R-AUTH users-admin
// endpoints (the support tier branch lives server-side on the same routes).
//   - resetPassword(id) → { reset:true, tempPassword } (relay out-of-band).
//   - unlock(id)        → { unlocked:true } (re-activates a locked account).
export const supportApi = {
  users: (q) => api.get("/v1/support/users" + (q ? "?q=" + encodeURIComponent(q) : "")),
  resetPassword: (id) =>
    api.post("/v1/users/" + encodeURIComponent(id) + "/reset-password", {}),
  unlock: (id) => api.post("/v1/users/" + encodeURIComponent(id) + "/unlock", {}),
  // SELF dashboard roll-up for the logged-in support agent. GET /v1/support/me/summary
  // (@Roles support/admin/super_admin):
  //   { openAssignedToMe, openQueueTotal, resolvedByMeThisWeek, avgResolutionHours|null }.
  meSummary: () => api.get("/v1/support/me/summary"),
};

// R-TEACH — class attendance. Teaching staff: list sections, read a section's
// roster for a date (status null = unmarked), and upsert marks for a session.
export const attendanceApi = {
  sections: () => api.get("/v1/attendance/sections"),
  roster: (sectionId, date) =>
    api.get("/v1/attendance/" + encodeURIComponent(sectionId) + (date ? "?date=" + encodeURIComponent(date) : "")),
  save: (sectionId, date, marks) =>
    api.post("/v1/attendance/" + encodeURIComponent(sectionId), { date, marks }),
  // R-COMPLY W3 — student's own attendance per enrolled section + absence
  // summary + the آیین‌نامه ۳/۱۶ over-threshold flag (computed, surfaced; حذف is
  // a human decision). GET /v1/attendance/mine.
  mine: () => api.get("/v1/attendance/mine"),
  // R-COMPLY W3 (staff) — the section's at-risk roster: per-student present/
  // absent tally, presence ratio + the آیین‌نامه ۳/۱۶ over-threshold flag.
  // FLAG ONLY (حذف درس is a human/commission decision). Shape:
  //   { sectionId, courseCode, courseTitle, termTitle, totalSessions, threshold,
  //     atRiskCount, students:[{ studentUserId, fullName, present, absent,
  //     excused, late, totalSessions, unexcusedAbsences, absenceRatio,
  //     overThreshold }] }. GET /v1/attendance/:sectionId/at-risk.
  atRisk: (sectionId) =>
    api.get("/v1/attendance/" + encodeURIComponent(sectionId) + "/at-risk"),
};

// R-GOV — admin broadcast announcements. send fans out an in_app notification to
// every active tenant user (real inbox delivery).
export const announcementsApi = {
  list: () => api.get("/v1/announcements"),
  create: (body) => api.post("/v1/announcements", body),
  send: (id) => api.post("/v1/announcements/" + encodeURIComponent(id) + "/send", {}),
};

// R-ORG — organization invoices (B2B billing). org/admin.
export const orgInvoicesApi = {
  list: ({ status } = {}) => api.get("/v1/org/invoices" + (status ? "?status=" + encodeURIComponent(status) : "")),
  create: (body) => api.post("/v1/org/invoices", body),
  get: (id) => api.get("/v1/org/invoices/" + encodeURIComponent(id)),
  pay: (id) => api.post("/v1/org/invoices/" + encodeURIComponent(id) + "/pay", {}),
};

// R-GOV — notification templates (admin).
export const notifTemplatesApi = {
  list: () => api.get("/v1/notif-templates"),
  create: (body) => api.post("/v1/notif-templates", body),
  update: (id, body) => api.patch("/v1/notif-templates/" + encodeURIComponent(id), body),
};

// R-LIB — کتابخانهٔ دیجیتال (content library). URL/reference-based curated
// resources (guide/template/video/link/document/book). list() is OPEN to any
// authed user and returns PUBLISHED rows only; managers (content_manager/admin/
// super_admin) also see drafts. CRUD is role-gated server-side (the FE shows the
// «افزودن/ویرایش/حذف» controls only to managers). Soft-delete on remove.
//   list({type,q,tag}) → LibraryResourceView[] (newest first)
//   get(id) / create(body) / update(id,body) / remove(id) → { ok:true }
// A row carries: { id, type, title, author?, description?, url, tag?, language?,
//   level?, isPublished, createdBy?, createdAt, updatedAt }.
export const libraryApi = {
  list: ({ type, q, tag } = {}) => {
    const p = [];
    if (type) p.push("type=" + encodeURIComponent(type));
    if (q) p.push("q=" + encodeURIComponent(q));
    if (tag) p.push("tag=" + encodeURIComponent(tag));
    return api.get("/v1/library" + (p.length ? "?" + p.join("&") : ""));
  },
  get: (id) => api.get("/v1/library/" + encodeURIComponent(id)),
  create: (body) => api.post("/v1/library", body),
  update: (id, body) => api.patch("/v1/library/" + encodeURIComponent(id), body),
  remove: (id) => api.delete("/v1/library/" + encodeURIComponent(id)),
};

// R-MOD — community moderation reports. create open to any authed; manage = moderator/admin.
export const moderationApi = {
  reports: ({ status } = {}) => api.get("/v1/moderation/reports" + (status ? "?status=" + encodeURIComponent(status) : "")),
  create: (body) => api.post("/v1/moderation/reports", body),
  resolve: (id, body) => api.post("/v1/moderation/reports/" + encodeURIComponent(id) + "/resolve", body),
  // R-MON W10 — aggregate triage stats (pending/resolved/dismissed + by-type/sev).
  // Route lives under the moderation/reports controller → /moderation/reports/stats.
  stats: () => api.get("/v1/moderation/reports/stats"),
  // R-MON W10 — "people behind the content": resolve a flagged report into the
  // author identity (name/roles; email only for admin) + their COMMUNITY-360 slice
  // + a "view in context" link target. NEVER academic/finance/wellness.
  reportContext: (id) => api.get("/v1/moderation/reports/" + encodeURIComponent(id) + "/context"),
};

// R-CONTENT — content review queue (مدیر محتوا).
export const contentReviewApi = {
  list: ({ status } = {}) => api.get("/v1/content-review" + (status ? "?status=" + encodeURIComponent(status) : "")),
  create: (body) => api.post("/v1/content-review", body),
  review: (id, body) => api.post("/v1/content-review/" + encodeURIComponent(id) + "/review", body),
};

// R-ORG — organization members / seats (B2B).
export const orgMembersApi = {
  list: ({ status } = {}) => api.get("/v1/org/members" + (status ? "?status=" + encodeURIComponent(status) : "")),
  create: (body) => api.post("/v1/org/members", body),
  update: (id, body) => api.patch("/v1/org/members/" + encodeURIComponent(id), body),
  remove: (id) => api.delete("/v1/org/members/" + encodeURIComponent(id)),
};

// R-PARENT — a parent's linked children + each child's real grades/attendance.
export const parentApi = {
  // SELF dashboard roll-up (GET /v1/parent/me/summary): child count + per-child
  //   wellness band/openConcern + pending-concern count (band LABELS only):
  //   { childCount, children:[{ studentUserId, name, program, openConcern, worstBand }],
  //     pendingConcernCount }.
  meSummary: () => api.get("/v1/parent/me/summary"),
  // SELF communication preferences (additive Profile.preferences sub-key).
  //   GET  /v1/parent/me/preferences →
  //     { channels:string[], frequency:string|null, wellnessAlerts, academicAlerts }.
  //   PATCH /v1/parent/me/preferences — body { communicationPreferences:{ channels?,
  //     frequency?, wellnessAlerts?, academicAlerts? } }; partial merge; returns the
  //     same GET shape. Unknown channel / bad frequency → 400 (class-validator).
  getPreferences: () => api.get("/v1/parent/me/preferences"),
  updatePreferences: (communicationPreferences) =>
    api.patch("/v1/parent/me/preferences", { communicationPreferences }),
  children: () => api.get("/v1/parent/children"),
  grades: (childId) => api.get("/v1/parent/children/" + encodeURIComponent(childId) + "/grades"),
  attendance: (childId) => api.get("/v1/parent/children/" + encodeURIComponent(childId) + "/attendance"),
  schedule: (childId) => api.get("/v1/parent/children/" + encodeURIComponent(childId) + "/schedule"),
  tuition: (childId) => api.get("/v1/parent/children/" + encodeURIComponent(childId) + "/tuition"),
  // WELL-6 — a SUPPORTIVE, summarized wellness view of a linked child (§5.5):
  // band LABELS per dimension + a mood-trend direction + openConcern + warm
  // copy (+ crisis helplines only when openConcern). NEVER raw answers/scores.
  childWellness: (childId) =>
    api.get("/v1/parent/children/" + encodeURIComponent(childId) + "/wellness"),
  // R-MON W4 — a linked child's read-only academic STATUS. exams = published
  // quiz/exam status (title, course, date, status graded|submitted|pending, +
  // grade IF graded). NEVER the raw questions/answers. assertLinked → 404.
  exams: (childId) =>
    api.get("/v1/parent/children/" + encodeURIComponent(childId) + "/exams"),
  // assignments = assignment submission status (title, course, due, submitted?/
  // graded?, + grade IF graded). NEVER the submitted content. assertLinked → 404.
  assignments: (childId) =>
    api.get("/v1/parent/children/" + encodeURIComponent(childId) + "/assignments"),
};

// R-MON W4 — admin parent-link onboarding (admin / super_admin / support). The
// runtime way to link/unlink a والد to a دانشجو (previously seed-only). create is
// idempotent (re-adding a revoked pair un-revokes it); revoke is non-destructive.
export const adminApi = {
  parentLinks: {
    list: (q) =>
      api.get("/v1/admin/parent-links" + (q ? "?q=" + encodeURIComponent(q) : "")),
    create: ({ parentUserId, studentUserId, relation } = {}) =>
      api.post("/v1/admin/parent-links", { parentUserId, studentUserId, relation }),
    revoke: (id) => api.delete("/v1/admin/parent-links/" + encodeURIComponent(id)),
    // Minimal, role-scoped picker source so support (not allowed on /v1/people)
    // can still choose a parent/student to link. Returns PickablePerson[].
    candidates: ({ q = "", role } = {}) => {
      const qs = [];
      if (role) qs.push("role=" + encodeURIComponent(role));
      if (q) qs.push("q=" + encodeURIComponent(q));
      return api.get("/v1/admin/parent-links/candidates" + (qs.length ? "?" + qs.join("&") : ""));
    },
  },
  // R-MON W7 — section-assistant (دستیار آموزشی) onboarding (admin / super_admin /
  // faculty_admin, faculty-scoped server-side). assign is idempotent (re-adding an
  // unassigned TA un-deletes the row); unassign is a non-destructive soft-delete.
  // A TA may then DRAFT-grade its assigned section but NEVER finalize (قطعی).
  sectionAssistants: {
    list: (sectionId) =>
      api.get("/v1/admin/sections/" + encodeURIComponent(sectionId) + "/assistants"),
    assign: (sectionId, userId) =>
      api.post("/v1/admin/sections/" + encodeURIComponent(sectionId) + "/assistants", { userId }),
    unassign: (sectionId, id) =>
      api.delete(
        "/v1/admin/sections/" + encodeURIComponent(sectionId) + "/assistants/" + encodeURIComponent(id),
      ),
  },
  // R-MON W12/W13 — read-only system-health console (admin/super_admin). Alias of
  // systemHealthApi.get(); kept on adminApi for callers reaching it via the admin
  // surface. → { appVersion, uptimeSeconds, now, counts:{…}, tenants, nodeVersion,
  // heapUsedMB, heapTotalMB }.
  systemHealth: () => api.get("/v1/admin/system-health"),
};

// ---------- per-role SELF dashboards (role profile/dashboard summary) ----------
//
// Self-scoped roll-ups consumed by the role section of the «پروفایل من» page. Every
// route resolves the target user from the JWT (no id in body/params); a wrong-role
// caller gets 403 and a caller with no matching record gets 0-valued counts (never a
// leak). All paths verified against the backend controllers.

// ریاست دانشگاه (admin) + ادمین کل (super_admin). meSummary is TENANT-scoped
// (GET /v1/admin/me/summary, @Roles admin/super_admin):
//   { tenantId, users:{ active, inactive, total }, roleCount, permissionCount,
//     roleDistribution:[{ role, label, users }] }.
// superSummary is CROSS-TENANT (GET /v1/admin/super/summary, @Roles super_admin):
//   { tenantCount, userCount, roleDistribution:[{ role, users }] }.
export const adminDashboardApi = {
  meSummary: () => api.get("/v1/admin/me/summary"),
  superSummary: () => api.get("/v1/admin/super/summary"),
};

// دستیار آموزشی (ta). GET /v1/ta/me/summary (@Roles ta/admin/super_admin):
//   { assignedSections, enrolledStudents, openGradeObjections,
//     sections:[{ sectionId, courseCode, courseTitle, enrolled }] }.
export const taDashboardApi = {
  meSummary: () => api.get("/v1/ta/me/summary"),
};

// مدیر محتوا (content_manager). GET /v1/content-manager/me/summary
// (@Roles content_manager/admin/super_admin):
//   { pendingReviews, approvedThisMonth, authoredCourses, pendingReviewTasks }.
export const contentManagerApi = {
  meSummary: () => api.get("/v1/content-manager/me/summary"),
};

// سازمان (org). GET /v1/org/me/summary (@Roles org/admin/super_admin):
//   { activeMembers, totalMembers, seats:{ active, used },
//     invoices:{ overdue, due, total } }.
export const orgDashboardApi = {
  meSummary: () => api.get("/v1/org/me/summary"),
};

// R-MON W13 — production AuditLog viewer query surface. The list endpoint
// (@Roles admin/super_admin/support; support gets a redacted view server-side)
// filters by action/subject/actorId/requestId + a createdAt RANGE (since=gte,
// until=lte) + limit/offset. Every param is optional and forwarded only when
// present so the bare call returns the latest page. since/until are ISO date
// strings; an invalid date is silently ignored server-side.
export const auditApi = {
  list: ({ action, subject, actorId, requestId, since, until, limit, offset } = {}) => {
    const q = [];
    if (action) q.push("action=" + encodeURIComponent(action));
    if (subject) q.push("subject=" + encodeURIComponent(subject));
    if (actorId) q.push("actorId=" + encodeURIComponent(actorId));
    if (requestId) q.push("requestId=" + encodeURIComponent(requestId));
    if (since) q.push("since=" + encodeURIComponent(since));
    if (until) q.push("until=" + encodeURIComponent(until));
    if (limit != null) q.push("limit=" + encodeURIComponent(limit));
    if (offset != null) q.push("offset=" + encodeURIComponent(offset));
    return api.get("/v1/audit-logs" + (q.length ? "?" + q.join("&") : ""));
  },
};

// R-MOD — automated moderation rules (moderator/admin).
export const autoRulesApi = {
  list: () => api.get("/v1/moderation/auto-rules"),
  create: (body) => api.post("/v1/moderation/auto-rules", body),
  update: (id, body) => api.patch("/v1/moderation/auto-rules/" + encodeURIComponent(id), body),
  remove: (id) => api.delete("/v1/moderation/auto-rules/" + encodeURIComponent(id)),
};

// R-ASSESS — grading rubrics (instructor/admin authoring).
export const rubricsApi = {
  list: () => api.get("/v1/rubrics"),
  create: (body) => api.post("/v1/rubrics", body),
  update: (id, body) => api.patch("/v1/rubrics/" + encodeURIComponent(id), body),
  remove: (id) => api.delete("/v1/rubrics/" + encodeURIComponent(id)),
};

// R-ADMIN — admin/academic calendar events.
export const calendarApi = {
  list: ({ category } = {}) => api.get("/v1/calendar-events" + (category ? "?category=" + encodeURIComponent(category) : "")),
  create: (body) => api.post("/v1/calendar-events", body),
  update: (id, body) => api.patch("/v1/calendar-events/" + encodeURIComponent(id), body),
  remove: (id) => api.delete("/v1/calendar-events/" + encodeURIComponent(id)),
  // R-CAL2 (تقویم یکپارچه) — unified, role-aware "my calendar" feed. The server
  // merges the caller's own sources (registered/taught section MEETINGS expanded
  // to dated occurrences within the term window, section EXAMS, ASSIGNMENT
  // due-dates, active Term process WINDOWS, and published org CalendarEvent rows)
  // into one normalized shape and caps the range at <=62 days. from/to are
  // Gregorian YYYY-MM-DD; both default server-side to the current month →
  //   { events: Array<{ id, type:"meeting"|"exam"|"assignment"|"term"|"event",
  //     title, subtitle?, start(ISO), end?(ISO), allDay, date(YYYY-MM-DD), href?,
  //     colorKey }> }. Own-scoped + tenant-scoped server-side.
  // childId (optional) → a PARENT views that linked child's calendar (server gates the link).
  me: ({ from, to, childId } = {}) => {
    const q = [];
    if (from) q.push("from=" + encodeURIComponent(from));
    if (to) q.push("to=" + encodeURIComponent(to));
    if (childId) q.push("childId=" + encodeURIComponent(childId));
    return api.get("/v1/calendar/me" + (q.length ? "?" + q.join("&") : ""));
  },
  // R-CAL3 (تقویم — رویدادهای شخصی + اشتراک) — the caller's OWN personal events
  // (PersonalEvent). create/update/remove are own-scoped server-side; the rows merge
  // into GET /v1/calendar/me as type "personal" within [from,to]. Body shape:
  //   { title(<=120), date(YYYY-MM-DD), startMin?, endMin?, allDay?, note?(<=500), colorKey? }.
  //   startMin/endMin = minutes-from-midnight wall-clock (same convention as the section
  //   schedule). allDay events omit the times. create → 201 the event row; update is a
  //   partial PATCH → the updated row; remove → { removed: true }.
  personal: {
    create: (body) => api.post("/v1/calendar/personal", body),
    update: (id, body) => api.patch("/v1/calendar/personal/" + encodeURIComponent(id), body),
    remove: (id) => api.delete("/v1/calendar/personal/" + encodeURIComponent(id)),
  },
  // R-CAL3 — the caller's stable iCal subscribe/export URL. → { url } a webcal/https
  // feed URL containing a signed (HMAC) token; the .ics it serves is RFC-5545 and updates
  // live as the user's events change (subscribed calendars re-poll it). Authed; the token
  // itself is validated server-side at the @Public /calendar/feed/:token serve route.
  feedUrl: () => api.get("/v1/calendar/feed-url"),
};

// R-ADMIN — current tenant info (only own tenant; cross-tenant listing is intentionally not exposed).
export const tenantsApi = {
  me: () => api.get("/v1/tenants/me"),
};

// R-ADMIN — third-party integration config store (admin).
export const integrationsApi = {
  list: () => api.get("/v1/integrations"),
  save: (key, body) => api.put("/v1/integrations/" + encodeURIComponent(key), body),
};

// R-ADMIN — AI governance policy store (admin).
export const aiGovApi = {
  list: () => api.get("/v1/ai-governance"),
  save: (key, body) => api.put("/v1/ai-governance/" + encodeURIComponent(key), body),
};

// Plan item #6 / R-MON W12+W13 — read-only system-health console (admin/
// super_admin). Real prisma counts + Node runtime, tenant-scoped → { appVersion,
// uptimeSeconds, now, counts:{...}, tenants, nodeVersion, heapUsedMB, heapTotalMB }.
// A count of -1 is the server's "this single query failed" sentinel.
export const systemHealthApi = {
  get: () => api.get("/v1/admin/system-health"),
};

// ساعات حضور استاد (§6) — office hours. Instructor manages own weekly slots
// (mySlots/create/update/delete + per-slot bookings); student browses
// instructors + their slots, books a free one, and manages own bookings.
// The backend resolves the caller-instructor by Instructor.userId===userId and
// enforces tenant scope + capacity server-side; the page reflects the rules.
export const officeHoursApi = {
  // instructor — own slots + per-slot bookings
  mySlots: () => api.get("/v1/office-hours/my-slots"),
  createSlot: (data) => api.post("/v1/office-hours/slots", data),
  updateSlot: (id, data) => api.patch("/v1/office-hours/slots/" + encodeURIComponent(id), data),
  deleteSlot: (id) => api.delete("/v1/office-hours/slots/" + encodeURIComponent(id)),
  slotBookings: (id) => api.get("/v1/office-hours/slots/" + encodeURIComponent(id) + "/bookings"),
  // student — browse instructors (with their weekly slots + bookedCount) + book/cancel
  instructors: () => api.get("/v1/office-hours/instructors"),
  book: (slotId, topic) =>
    api.post("/v1/office-hours/bookings", topic ? { slotId, topic } : { slotId }),
  myBookings: () => api.get("/v1/office-hours/my-bookings"),
  cancelBooking: (id) => api.post("/v1/office-hours/bookings/" + encodeURIComponent(id) + "/cancel", {}),
};

// §12.14 — class recordings archive (reads the existing recordings controller).
export const recordingsApi = {
  list: ({ courseId } = {}) =>
    api.get("/v1/recordings" + (courseId ? "?courseId=" + encodeURIComponent(courseId) : "")),
  get: (id) => api.get("/v1/recordings/" + encodeURIComponent(id)),
};

// انجمن گفتگو (§6) — community forum (any authed user). Votes are the source of
// truth: a thread/reply upvote count = COUNT(votes for that target); voteThread/
// voteReply toggle (create/delete) the caller's vote row server-side. Threads
// list supports tag + free-text (q) filters. Moderators (admin/super_admin/
// moderator) may pin + delete any thread/reply; authors may delete their own.
// All authz is enforced server-side; the page reflects the rules.
export const communityApi = {
  threads: ({ tag, q } = {}) => {
    const p = [];
    if (tag) p.push("tag=" + encodeURIComponent(tag));
    if (q) p.push("q=" + encodeURIComponent(q));
    return api.get("/v1/community/threads" + (p.length ? "?" + p.join("&") : ""));
  },
  createThread: (data) => api.post("/v1/community/threads", data), // { title, body, tag? }
  thread: (id) => api.get("/v1/community/threads/" + encodeURIComponent(id)),
  reply: (threadId, body) =>
    api.post("/v1/community/threads/" + encodeURIComponent(threadId) + "/replies", { body }),
  voteThread: (id) => api.post("/v1/community/threads/" + encodeURIComponent(id) + "/vote", {}),
  voteReply: (id) => api.post("/v1/community/replies/" + encodeURIComponent(id) + "/vote", {}),
  pinThread: (id, pinned) =>
    api.patch("/v1/community/threads/" + encodeURIComponent(id) + "/pin", { pinned }),
  deleteThread: (id) => api.delete("/v1/community/threads/" + encodeURIComponent(id)),
  deleteReply: (id) => api.delete("/v1/community/replies/" + encodeURIComponent(id)),
  // R-MON W10 — flag a thread/reply (any authed user). The report carries the
  // content id + its author server-side, so a moderator can later see who + open
  // it in context. { reason }.
  reportThread: (id, reason) =>
    api.post("/v1/community/threads/" + encodeURIComponent(id) + "/report", { reason }),
  reportReply: (id, reason) =>
    api.post("/v1/community/replies/" + encodeURIComponent(id) + "/report", { reason }),
  // R-MON W10 — moderator aggregate community stats + a user's COMMUNITY-360 slice
  // (threads/replies/votes/reports). Community-domain only; agents-gated server-side.
  stats: () => api.get("/v1/community/stats"),
  userActivity: (userId) =>
    api.get("/v1/community/people/" + encodeURIComponent(userId) + "/activity"),
};

// R-PORT — کارنامک (student portfolio). Any authed user manages their OWN list:
// mine() returns every own row (public + private), newest first. CRUD is bound
// to the caller server-side (userId never travels in the body); PATCH/DELETE of
// a foreign/missing id → 404 (no leak). ofUser(userId) views another user's
// portfolio (isPublic=true rows only, unless owner or admin/super_admin). Soft-
// delete on remove. A row carries: { id, userId, title, kind, description?, url?,
// repoUrl?, tag?, year?, isPublic, createdAt, updatedAt }. kind ∈
// project|publication|certificate|experience|skill.
export const portfolioApi = {
  mine: () => api.get("/v1/portfolio/me"),
  ofUser: (userId) => api.get("/v1/portfolio/users/" + encodeURIComponent(userId)),
  create: (body) => api.post("/v1/portfolio", body),
  update: (id, body) => api.patch("/v1/portfolio/" + encodeURIComponent(id), body),
  remove: (id) => api.delete("/v1/portfolio/" + encodeURIComponent(id)),
};

// R-CRED — مدارک دیجیتال (digital credentials). The complete loop:
//   • mine()           — the caller's OWN live credentials (any authed user),
//                        newest issuedAt first. Full rows: { id, userId, type,
//                        title, issuer, description, issuedAt, credentialCode,
//                        url, status, revokedAt, revokedReason, ... }.
//   • verify(code)     — tenant-scoped verify by code (any authed user). Returns
//                        PUBLIC-SAFE fields ONLY: { holderName, type, title,
//                        issuer, issuedAt, status } — never ids/PII. 404 if the
//                        code isn't a live credential in the caller's tenant.
//   • issue(body)      — REGISTRAR ONLY (admin/super_admin, server-guarded +
//                        @AuditAction). Issues TO a holder (a user never self-
//                        issues). body = { userId(holder), type, title, issuer,
//                        description?, url?, issuedAt? }. 404 if the holder isn't
//                        a user in the tenant; the unique credentialCode is
//                        generated server-side.
//   • revoke(id,reason)— REGISTRAR ONLY (server-guarded + @AuditAction). Status
//                        change only (never a hard delete); idempotent on an
//                        already-revoked row.
// No update/delete — credentials are issue/revoke only (immutable once issued).
export const credentialsApi = {
  mine: () => api.get("/v1/credentials/me"),
  verify: (code) => api.get("/v1/credentials/verify/" + encodeURIComponent(code)),
  issue: (body) => api.post("/v1/credentials", body),
  revoke: (id, reason) =>
    api.post("/v1/credentials/" + encodeURIComponent(id) + "/revoke", reason ? { reason } : {}),
};

// R-LABS — virtual-lab attempt persistence. The simulators in LabsRich.tsx are
// genuine built-in interactive components (the catalog is static config, not a
// mock); what was missing was that COMPLETIONS vanished. These endpoints make
// the lab real by persisting an attempt when a sim is completed:
//   • record(body)   — any authed user; userId is server-bound to the caller
//                      (NEVER sent in the body — a forged userId is rejected 400
//                      by the whitelist pipe). body = { labId, labTitle,
//                      category?, score?, resultJson?(small summary, ≤16KB),
//                      status?("completed"|"in_progress"), startedAt?,
//                      completedAt? }. @AuditAction("lab.attempt.record"). → row.
//   • mine()         — the caller's OWN attempts (tenant-scoped, newest first).
//   • mySummary()    — self KPI roll-up: { totalAttempts, completedCount,
//                      distinctLabs, avgScore|null, lastAt|null }.
//   • ofUser(userId) — STAFF ONLY (instructor/admin/super_admin, server-guarded);
//                      a single student's lab history for grading/analytics.
//                      404 (no-leak) if the target user isn't in the tenant.
export const labsApi = {
  record: (body) => api.post("/v1/labs/attempts", body),
  mine: () => api.get("/v1/labs/attempts/me"),
  mySummary: () => api.get("/v1/labs/attempts/me/summary"),
  ofUser: (userId) =>
    api.get("/v1/labs/users/" + encodeURIComponent(userId) + "/attempts"),
};

// R-LABS (deep) — آزمایشگاه مجازی REAL module. VirtualLab catalog + evolving
// LabSubmission (one per student per lab) + instructor/content_manager grading +
// content CRUD. Tenant-scoped + role-guarded server-side; userId always bound to
// the caller (never sent in a body). Backs apps/web/src/pages/LabsRich.tsx.
//   CATALOG (any authed)
//   • list({discipline}) — published labs (optional discipline filter). → [lab]
//                  lab = { id, title, discipline, summary, objectives[], materials[],
//                          steps:[{ title, instructions, embedUrl?, prompt? }],
//                          estimatedMinutes, difficulty, courseId?, isPublished }.
//   • get(id)            — one lab (published, or any for the author/staff). → lab
//   STUDENT (own-scoped)
//   • mySubmission(labId)        — the caller's OWN evolving draft; AUTO-CREATES an
//                  in_progress submission on first read. → submission =
//                  { id, labId, status, answers:{ [stepIndex]: text }, score?,
//                    feedback?, submittedAt?, gradedAt? }.
//   • saveDraft(labId, answers)  — PUT the draft answers (own, only while
//                  in_progress). → submission.
//   • submitReport(labId)        — lock the draft → submitted. → submission.
//   • mySubmissions()            — the caller's OWN lab reports + status (list).
//   INSTRUCTOR / content_manager (scoped, server-guarded)
//   • submissionsForLab(labId)   — submitted reports to grade for one lab. → [sub]
//   • gradeSubmission(id, { score, feedback }) — score (0..100) + feedback → graded.
//   content_manager / admin — CRUD
//   • create(body) / update(id, body) / publish(id, isPublished)
//   • myAuthored() — labs the caller can manage (drafts + published, scoped).
export const labApi = {
  list: ({ discipline } = {}) =>
    api.get("/v1/labs" + (discipline && discipline !== "all" ? "?discipline=" + encodeURIComponent(discipline) : "")),
  get: (id) => api.get("/v1/labs/" + encodeURIComponent(id)),
  // student — own evolving submission
  mySubmission: (labId) =>
    api.get("/v1/labs/" + encodeURIComponent(labId) + "/submission"),
  saveDraft: (labId, answers) =>
    api.put("/v1/labs/" + encodeURIComponent(labId) + "/submission", { answers }),
  submitReport: (labId) =>
    api.post("/v1/labs/" + encodeURIComponent(labId) + "/submission/submit", {}),
  mySubmissions: () => api.get("/v1/labs/submissions/me"),
  // instructor / content_manager — review + grade
  submissionsForLab: (labId) =>
    api.get("/v1/labs/" + encodeURIComponent(labId) + "/submissions"),
  gradeSubmission: (id, { score, feedback }) =>
    api.post("/v1/labs/submissions/" + encodeURIComponent(id) + "/grade", { score, feedback }),
  // content_manager / admin — authoring
  myAuthored: () => api.get("/v1/labs/authored/me"),
  create: (body) => api.post("/v1/labs", body),
  update: (id, body) => api.patch("/v1/labs/" + encodeURIComponent(id), body),
  publish: (id, isPublished) =>
    api.post("/v1/labs/" + encodeURIComponent(id) + "/publish", { isPublished }),
};

// R-CAREER — خدمات شغلی (career services). REAL backend behind CareerRich.tsx
// (was SAMPLE_JOBS / SAMPLE_CV hardcoded). Three complete loops; tenant is a hard
// boundary on every query, userId is ALWAYS server-bound (never sent in the body).
//   JOBS
//   • jobs({type,q,status}) — browse (any authed). OPEN + non-deleted only;
//                  career services may pass status="all" to also see closed.
//   • job(id)    — one job (non-managers: open only; else 404 no-leak).
//   • postJob(body)       — admin/super_admin only (@Roles + audit). body =
//                  { title, company, location?, type, description, applyUrl?, tag? }.
//                  type ∈ full_time|part_time|internship|contract.
//   • updateJob(id, body) — edit / close (status:"closed"). admin/super_admin.
//   • deleteJob(id)       — SOFT-delete. admin/super_admin.
//   APPLICATIONS
//   • apply(jobId, note?) — apply (any authed; userId = caller, server-bound;
//                  idempotent on the unique). 404 if the job is gone/closed.
//   • myApplications()    — the caller's OWN applications (job title/company/status).
//   • applicants(jobId)   — a job's applicants. admin/super_admin only.
//   • updateApplication(id, body) — set status (applied|reviewing|accepted|
//                  rejected) + optional note. admin/super_admin + audit.
//   CV
//   • cvMine()            — the caller's OWN CV (or null).
//   • cvSave(body)        — UPSERT the caller's OWN CV (server-bound userId).
//                  body = { headline?, summary?, skills?, experienceJson?,
//                  educationJson?, links? }; each JSON field size-capped (≤16KB).
//   • cvOfUser(userId)    — owner OR admin/super_admin; else 404 (no-leak).
export const careerApi = {
  jobs: ({ type, q, status } = {}) => {
    const p = [];
    if (type) p.push("type=" + encodeURIComponent(type));
    if (q) p.push("q=" + encodeURIComponent(q));
    if (status) p.push("status=" + encodeURIComponent(status));
    return api.get("/v1/career/jobs" + (p.length ? "?" + p.join("&") : ""));
  },
  job: (id) => api.get("/v1/career/jobs/" + encodeURIComponent(id)),
  postJob: (body) => api.post("/v1/career/jobs", body),
  updateJob: (id, body) => api.patch("/v1/career/jobs/" + encodeURIComponent(id), body),
  deleteJob: (id) => api.delete("/v1/career/jobs/" + encodeURIComponent(id)),
  apply: (jobId, note) =>
    api.post("/v1/career/jobs/" + encodeURIComponent(jobId) + "/apply", note ? { note } : {}),
  myApplications: () => api.get("/v1/career/applications/me"),
  applicants: (jobId) =>
    api.get("/v1/career/jobs/" + encodeURIComponent(jobId) + "/applicants"),
  updateApplication: (id, body) =>
    api.patch("/v1/career/applications/" + encodeURIComponent(id), body),
  cvMine: () => api.get("/v1/career/cv/me"),
  cvSave: (body) => api.put("/v1/career/cv/me", body),
  cvOfUser: (userId) => api.get("/v1/career/cv/" + encodeURIComponent(userId)),
};

// R-MARKET P1 — «بازار دانش» authoring studio (course STRUCTURE only). Author-
// owned (instructor/content_manager + admin/super_admin oversight; the public
// marketplace browse tab is P4 — NOT here). Every write 404s a non-owner non-
// admin (no-leak) and 409s a non-editable course (only draft/changes_requested
// are editable). The chart hangs off the course's CURRENT version; reorder takes
// the FULL ordered id list for the parent. Each mutation returns the full nested
// course detail ({ …course, currentVersion:{ modules:[{ lessons:[{ sections }] }] } }),
// so the editor can render straight from the response with no extra refetch.
export const marketApi = {
  // --- course ---
  coursesMine: () => api.get("/v1/market/courses/mine"),
  course: (id) => api.get("/v1/market/courses/" + encodeURIComponent(id)),
  createCourse: (body) => api.post("/v1/market/courses", body),
  updateCourse: (id, body) =>
    api.patch("/v1/market/courses/" + encodeURIComponent(id), body),
  deleteCourse: (id) => api.delete("/v1/market/courses/" + encodeURIComponent(id)),
  // --- modules (on the course's current version) ---
  addModule: (id, body) =>
    api.post("/v1/market/courses/" + encodeURIComponent(id) + "/modules", body),
  updateModule: (id, moduleId, body) =>
    api.patch(
      "/v1/market/courses/" + encodeURIComponent(id) + "/modules/" + encodeURIComponent(moduleId),
      body,
    ),
  deleteModule: (id, moduleId) =>
    api.delete(
      "/v1/market/courses/" + encodeURIComponent(id) + "/modules/" + encodeURIComponent(moduleId),
    ),
  reorderModules: (id, orderedIds) =>
    api.patch("/v1/market/courses/" + encodeURIComponent(id) + "/modules/reorder", { orderedIds }),
  // --- lessons (under a module) ---
  addLesson: (id, moduleId, body) =>
    api.post(
      "/v1/market/courses/" + encodeURIComponent(id) + "/modules/" + encodeURIComponent(moduleId) + "/lessons",
      body,
    ),
  updateLesson: (id, lessonId, body) =>
    api.patch(
      "/v1/market/courses/" + encodeURIComponent(id) + "/lessons/" + encodeURIComponent(lessonId),
      body,
    ),
  deleteLesson: (id, lessonId) =>
    api.delete(
      "/v1/market/courses/" + encodeURIComponent(id) + "/lessons/" + encodeURIComponent(lessonId),
    ),
  reorderLessons: (id, moduleId, orderedIds) =>
    api.patch(
      "/v1/market/courses/" + encodeURIComponent(id) + "/modules/" + encodeURIComponent(moduleId) + "/lessons/reorder",
      { orderedIds },
    ),
  // --- sections (under a lesson) ---
  addSection: (id, lessonId, body) =>
    api.post(
      "/v1/market/courses/" + encodeURIComponent(id) + "/lessons/" + encodeURIComponent(lessonId) + "/sections",
      body,
    ),
  updateSection: (id, sectionId, body) =>
    api.patch(
      "/v1/market/courses/" + encodeURIComponent(id) + "/sections/" + encodeURIComponent(sectionId),
      body,
    ),
  deleteSection: (id, sectionId) =>
    api.delete(
      "/v1/market/courses/" + encodeURIComponent(id) + "/sections/" + encodeURIComponent(sectionId),
    ),
  reorderSections: (id, lessonId, orderedIds) =>
    api.patch(
      "/v1/market/courses/" + encodeURIComponent(id) + "/lessons/" + encodeURIComponent(lessonId) + "/sections/reorder",
      { orderedIds },
    ),

  // --- AI-assist (R-MARKET §8 — author conveniences; dev-mock-first, author-only) ---
  // Every call is author-only (own course; cross → 404), per-user hourly throttled
  // (429 with a Persian message), STRICTLY validated server-side, and audited to
  // AiInteractionLog by AiBridgeService. The generate calls MUTATE NOTHING — they
  // propose a DRAFT the author must review/edit; only applyOutline writes the chart.
  //
  //   §8a — generateOutline → { outline:{ modules:[{title, lessons:[{title,
  //         sections:[{title, contentType}]}]}] }, meta }. The model reasons over the
  //         STORED course title/description/level; `moduleCount` (1..20) only hints
  //         the size. The author previews + edits, then APPLIES it.
  aiOutline: (id, body) =>
    api.post("/v1/market/courses/" + encodeURIComponent(id) + "/ai-outline", body || {}),
  //   §8a — applyOutline → the FULL course detail (the chart batch-created on the DRAFT
  //         working version; content refs left empty for the author). The WHOLE outline
  //         is re-validated server-side — the client preview is never trusted.
  applyOutline: (id, modules) =>
    api.post("/v1/market/courses/" + encodeURIComponent(id) + "/apply-outline", { modules }),
  //   §8b — generateInteractive → { content:<ValidatedInteractive>, meta }. `kind` ∈
  //         multichoice|flashcards|blanks|text; `topic` (required) + `instruction`
  //         (optional) + `count`. The builder PARSES content into its model + fills the
  //         manual form so the author refines before saving.
  aiInteractive: (id, sectionId, body) =>
    api.post(
      "/v1/market/courses/" + encodeURIComponent(id) + "/sections/" + encodeURIComponent(sectionId) + "/ai-interactive",
      body,
    ),
  //   §8b — generateQuiz → { quiz:{ questions:[{question, options, allowMultiple}] }, meta }.
  aiQuiz: (id, sectionId, body) =>
    api.post(
      "/v1/market/courses/" + encodeURIComponent(id) + "/sections/" + encodeURIComponent(sectionId) + "/ai-quiz",
      body,
    ),

  // --- assets (R-MARKET P2 — self-hosted content upload + access-gated stream) ---
  // Upload a media file to the server's own disk. `type` ∈ video|slide|document|audio|image.
  // Returns the MarketAsset view ({ id, type, fileName, sizeBytes, mimeType, durationSeconds, … }).
  // onProgress(pct 0..100) drives the in-dialog progress bar.
  uploadAsset: ({ file, type, durationSeconds } = {}, onProgress) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("type", type);
    if (durationSeconds != null) fd.append("durationSeconds", String(durationSeconds));
    return uploadMultipart("/v1/market/assets", fd, onProgress);
  },
  // The caller's OWN assets (light list, newest first) — backs the "reuse from library" picker.
  assetsMine: () => api.get("/v1/market/assets/mine"),
  // Soft-delete an asset the caller owns (409 if used by a published course).
  deleteAsset: (id) => api.delete("/v1/market/assets/" + encodeURIComponent(id)),
  // Mint a short-lived, access-gated media token → { token, url, expiresAt }. The `url`
  // is the API-relative stream path (NO /api prefix); prepend "/api" before a <video>/<iframe> src.
  assetAccessToken: (id) =>
    api.get("/v1/market/assets/" + encodeURIComponent(id) + "/access-token"),

  // --- review + publish workflow (R-MARKET P3 — doc §1.3 state machine + §6) ---
  // Each call returns the FULL nested course detail (the P3 transitions reuse the P1
  // buildDetailView), so the editor re-renders straight from the response. The
  // author detail now also carries latestReviewDecision / latestReviewNote /
  // latestReviewDecidedAt so the author sees WHY a course is changes_requested/rejected.
  //
  // AUTHOR (own course → 404 else): submit / publish / new-version.
  //   submitCourse — draft|changes_requested → in_review. On a 400 the server returns
  //     { message, missing:[…] } listing the SPECIFIC pre-submit gaps (§6.1); the
  //     ApiError carries them so the caller can show the missing-items list in-dialog.
  submitCourse: (id) => api.post("/v1/market/courses/" + encodeURIComponent(id) + "/submit", {}),
  //   publishCourse — approved → published (or new-version swap §6.4).
  publishCourse: (id) => api.post("/v1/market/courses/" + encodeURIComponent(id) + "/publish", {}),
  //   newVersion — clone the published version → a draft v2 (§6.4); returns the v2 detail.
  newVersion: (id) => api.post("/v1/market/courses/" + encodeURIComponent(id) + "/new-version", {}),
  //   cloneCourse (R-MARKET Wave 1 §11b) — duplicate the author's OWN published course into a
  //     brand-new DRAFT course (fresh v1, deep-copied chart, title «… (رونوشت)»). The original
  //     course + its buyers/enrollments are untouched. Returns the NEW course's full detail
  //     (status:"draft", owned by the caller). 201 Created; 409 if the source isn't published;
  //     no-leak 404 for a cross-author/cross-tenant source.
  cloneCourse: (id) => api.post("/v1/market/courses/" + encodeURIComponent(id) + "/clone", {}),

  // --- cover image (R-MARKET studio fix) — author-only PUBLIC marketing image ---
  // Upload a cover (PNG/JPG/WebP ≤5MB) as multipart `file`. The server stores it under the
  // PUBLIC covers/ subtree, sets coverImageUrl to a token-FREE public url (catalog/cards show
  // it to guests), and returns the FULL course detail (so the editor re-renders straight off
  // the response). onProgress(pct 0..100) drives the in-dialog progress bar. A bad MIME/size
  // → a Persian 400 on the ApiError (shown in-dialog); an over-cap file → a 413 (multer).
  uploadCover: (id, file, onProgress) => {
    const fd = new FormData();
    fd.append("file", file);
    return uploadMultipart("/v1/market/courses/" + encodeURIComponent(id) + "/cover", fd, onProgress);
  },

  // REVIEWER (content_manager + admin/super_admin oversight):
  //   reviewQueue — every in_review course in the tenant (light: title, author,
  //     submittedAt, module/lesson/section counts).
  reviewQueue: () => api.get("/v1/market/review-queue"),
  //   reviewCourse — full course + the SUBMITTED version chart (same detail shape as
  //     the author's editor; the reviewer reads it read-only). Author may also read it.
  reviewCourse: (id) => api.get("/v1/market/courses/" + encodeURIComponent(id) + "/review"),
  //   decideReview — record the decision { decision: approve|changes|reject, note? }.
  //     note REQUIRED (server 400) for changes/reject. in_review → approved|
  //     changes_requested|rejected.
  decideReview: (id, body) =>
    api.post("/v1/market/courses/" + encodeURIComponent(id) + "/review", body),

  // MARKET-ADMIN (admin/super_admin): suspend / unsuspend / archive a published course.
  //   suspendCourse — published → suspended ({ reason } required, server 400 else).
  suspendCourse: (id, body) =>
    api.post("/v1/market/courses/" + encodeURIComponent(id) + "/suspend", body),
  //   unsuspendCourse — suspended → published.
  unsuspendCourse: (id) =>
    api.post("/v1/market/courses/" + encodeURIComponent(id) + "/unsuspend", {}),
  //   archiveCourse — published|approved|suspended → archived ({ reason? } optional).
  archiveCourse: (id, body) =>
    api.post("/v1/market/courses/" + encodeURIComponent(id) + "/archive", body || {}),

  // --- PUBLIC marketplace (R-MARKET P4 — doc §7) ---
  // The browse surface EVERY role + a logged-out GUEST can reach. ALL of these use
  // publicApi (NO auth) and a REQUIRED tenantSlug (the established public pattern —
  // mirrors programsApi/plansApi). The backend exposes ONLY published-course
  // metadata + the chart STRUCTURE (paid sections LOCKED) + free-preview sections;
  // it never leaks paid bytes. A guest mints a free-preview media token via
  // previewToken() (the existing /assets/:id/stream?t= validates it) — used with the
  // SAME SectionViewer the studio/player use (the access-gate decides who streams).
  //
  // catalog({ tenantSlug, q?, category?, level?, language?, sort?, page?, limit? })
  //   → { items:[{ id, title, authorName, coverImageUrl, level, language, avgRating,
  //       reviewCount, enrollmentCount, publishedAt }], page, limit, total, totalPages }.
  //   sort ∈ popular|newest|rating. (free/priceMax are accepted server-side but a
  //   no-op until pricing lands in P5 — not sent here.)
  catalog: ({ tenantSlug, q, category, level, language, sort, page, limit } = {}) => {
    const p = new URLSearchParams();
    p.set("tenantSlug", tenantSlug || "");
    if (q) p.set("q", q);
    if (category) p.set("category", category);
    if (level) p.set("level", level);
    if (language) p.set("language", language);
    if (sort) p.set("sort", sort);
    if (page) p.set("page", String(page));
    if (limit) p.set("limit", String(limit));
    return publicApi.get("/v1/market/catalog?" + p.toString());
  },
  // A published course's public detail: header + author + the FULL chart (paid
  // sections carry locked:true, no content refs) + aggregate counts + author's
  // other courses. 404 if the course is missing / not published / cross-tenant.
  coursePublic: (id, tenantSlug) =>
    publicApi.get(
      "/v1/market/courses/" + encodeURIComponent(id) + "/public?tenantSlug=" +
        encodeURIComponent(tenantSlug || ""),
    ),
  // The free-preview sections (with the refs needed to render them via SectionViewer:
  // richTextBody / externalUrl / assetId + needsMediaToken). ONLY isFreePreview ones.
  previewSections: (id, tenantSlug) =>
    publicApi.get(
      "/v1/market/courses/" + encodeURIComponent(id) + "/preview-sections?tenantSlug=" +
        encodeURIComponent(tenantSlug || ""),
    ),
  // Mint a GUEST media token for a free-preview section's asset → { token, url,
  // expiresAt }. 404 unless the section is free, asset-backed + under a published
  // course. Prepend "/api" (streamSrc) before using `url` as a <video>/<audio>/<iframe> src.
  // R-MARKET CAPTIONS — pass asset="caption" to mint for the section's caption track
  // instead of its media bytes (gated identically); default "media" preserves the old call.
  previewToken: (sectionId, tenantSlug, asset) =>
    publicApi.get(
      "/v1/market/sections/" + encodeURIComponent(sectionId) + "/preview-token?tenantSlug=" +
        encodeURIComponent(tenantSlug || "") +
        (asset && asset !== "media" ? "&asset=" + encodeURIComponent(asset) : ""),
    ),
  // Rating distribution + reviews for a published course → { average, count,
  // distribution:{1..5}, items:[] }. items is empty until reviews ship (P5/P6).
  courseReviews: (id, tenantSlug) =>
    publicApi.get(
      "/v1/market/courses/" + encodeURIComponent(id) + "/reviews?tenantSlug=" +
        encodeURIComponent(tenantSlug || ""),
    ),

  // --- PRICING + PURCHASE + ECONOMICS (R-MARKET P5 — doc §5 + §8) ---
  // PRICING (author/oversight, own course → 404 else). The PUT sets the model
  // («رایگان»/«یک‌باره») + the whole-rial price; the GET reads it back (null-shaped
  // when never priced). The public price for buyers/guests rides on coursePublic()
  // (pricingModel + priceRial) — these author endpoints are JWT-guarded.
  //   setPricing(id, { model:"free"|"one_time", priceRial? }) → { marketCourseId, model, priceRial }
  setPricing: (id, body) =>
    api.put("/v1/market/courses/" + encodeURIComponent(id) + "/pricing", body),
  //   getPricing(id) → { marketCourseId, model, priceRial }  (model/priceRial null if unset)
  getPricing: (id) => api.get("/v1/market/courses/" + encodeURIComponent(id) + "/pricing"),

  // ADMIN economics config (admin/super_admin): revenue-share % to the university +
  // refund policy (window days + max progress %) + tax placeholder.
  //   adminConfig() → { revenueSharePercentToUniversity, refundWindowDays,
  //                     refundMaxProgressPercent, taxPercentage }
  adminConfig: () => api.get("/v1/market/admin/config"),
  setAdminConfig: (body) => api.put("/v1/market/admin/config", body),
  // Author-readable revenue-share % for the studio pricing panel (admin/config is
  // admin-only and 403s an author). Returns { revenueSharePercentToUniversity }.
  shareInfo: () => api.get("/v1/market/share-info"),

  // PURCHASE (any authed buyer). Direct purchase — NO cart. The server guards
  // self-purchase / already-enrolled / unpublished / no-pricing (all 409, no-leak 404).
  //   free  ⇒ { free:true, purchaseId, status:"completed", enrolled:true } (instant)
  //   paid  ⇒ { free:false, purchaseId, paymentId, redirectUrl, isMock, provider, amount }
  //           — redirect the browser to redirectUrl (the existing gateway/mock-confirm
  //           flow; on confirm the callback grants access + books the §5.3 ledger entry).
  //   R-MARKET EXT — an optional body { couponCode } applies a validated discount; the
  //   server re-validates + books the §5.3 ledger on the DISCOUNTED price (an invalid
  //   coupon aborts with a 400). Omit the body for a full-price purchase.
  purchase: (id, body) =>
    api.post("/v1/market/courses/" + encodeURIComponent(id) + "/purchase", body || {}),

  // REFUND a COMPLETED paid purchase (buyer for own purchase, OR admin). The server
  // enforces the time-window + max-progress policy; an admin may pass { override:true }
  // to bypass it. → { ok, purchaseId, status:"refunded", refundLedgerEntryId }.
  refund: (purchaseId, body) =>
    api.post("/v1/market/purchases/" + encodeURIComponent(purchaseId) + "/refund", body || {}),

  // READS:
  //   myCourses() — the buyer's active (non-revoked) enrollments → { items:[{
  //     enrollmentId, marketCourseId, title, coverImageUrl, level, status,
  //     progressPercent, grantedAt, completedAt, lastSectionId }] }.
  myCourses: () => api.get("/v1/market/my-courses"),
  //   earningsMine() — the author's accrued share (NOT yet paid; payout is P7) →
  //     { totals:{ salesCount, grossRial, accruedAuthorShareRial },
  //       perCourse:[{ marketCourseId, title, salesCount, grossRial, accruedAuthorShareRial }] }.
  earningsMine: () => api.get("/v1/market/earnings/mine"),
  //   courseAnalytics(id) — AUTHOR-scoped consumption analytics for ONE of the caller's OWN
  //     courses (a non-owner / non-oversight role gets a no-leak 404). AGGREGATE COUNTS ONLY —
  //     no buyer identity / PII. →
  //     { marketCourseId, title, enrolledCount, completedCount, completionRate,
  //       avgProgressPercent,
  //       sections:[{ sectionId, title, moduleTitle, reachedCount, completedCount, dropoffRate }] }.
  //     dropoffRate = % of enrolled buyers who did NOT finish that section.
  courseAnalytics: (id) =>
    api.get("/v1/market/courses/" + encodeURIComponent(id) + "/analytics"),
  //   adminSalesReport() — tenant-wide totals (admin/super_admin) →
  //     { salesCount, refundedCount, grossRial, universityRevenueRial, authorPayableRial }.
  adminSalesReport: () => api.get("/v1/market/admin/sales-report"),

  // --- LEARNING EXPERIENCE / COURSE PLAYER (R-MARKET P6 — doc §9) ---
  // The ENROLLED BUYER's player + progress + notes + completion certificate. EVERY call is
  // scoped server-side to the caller's OWN active enrollment; a non-enrolled / refunded /
  // cross-tenant user gets 404 (no content / progress / notes leak). Media bytes still flow
  // through the existing enrollment-gated assetAccessToken() — the player carries only assetId.
  //
  //   player(id) — the full current-version chart for the buyer (modules → lessons → sections
  //     WITH content refs assetId/richTextBody/externalUrl/quizExamId + contentType +
  //     durationSeconds) + per-section { completed, lastPositionSeconds } + lesson { locked }
  //     (sequential-lock) + progress { progressPercent, lastSectionId, completedAt,
  //     totalSections, completedSections } + certificateEligible/hasCertificate + course
  //     { id, title, coverImageUrl, level, authorName }. 404 if not enrolled.
  player: (id) => api.get("/v1/market/my-courses/" + encodeURIComponent(id) + "/player"),
  //   saveProgress(id, { sectionId, completed?, lastPositionSeconds? }) — upsert one section's
  //     progress (completed defaults true; lastPositionSeconds only overwritten when sent),
  //     recompute the aggregate → returns the updated progress block (same shape as above).
  saveProgress: (id, { sectionId, completed, lastPositionSeconds } = {}) => {
    const body = { sectionId };
    if (completed !== undefined) body.completed = completed;
    if (lastPositionSeconds != null) body.lastPositionSeconds = lastPositionSeconds;
    return api.post(
      "/v1/market/my-courses/" + encodeURIComponent(id) + "/progress",
      body,
    );
  },
  //   notes(id) — the buyer's OWN notes for the course (newest first) → NoteView[]
  //     ({ id, marketCourseId, sectionId, body, videoPositionSeconds, createdAt, updatedAt }).
  notes: (id) => api.get("/v1/market/my-courses/" + encodeURIComponent(id) + "/notes"),
  //   addNote(id, { body, sectionId?, videoPositionSeconds? }) — create a note / video bookmark.
  addNote: (id, body) =>
    api.post("/v1/market/my-courses/" + encodeURIComponent(id) + "/notes", body),
  //   updateNote(id, noteId, { body?, sectionId?, videoPositionSeconds? }) — edit own note.
  updateNote: (id, noteId, body) =>
    api.patch(
      "/v1/market/my-courses/" + encodeURIComponent(id) + "/notes/" + encodeURIComponent(noteId),
      body,
    ),
  //   deleteNote(id, noteId) — delete own note → { ok:true }.
  deleteNote: (id, noteId) =>
    api.delete(
      "/v1/market/my-courses/" + encodeURIComponent(id) + "/notes/" + encodeURIComponent(noteId),
    ),
  //   issueCertificate(id) — issue (idempotent) the «گواهی پایان دورهٔ بازار» (reuses the §20
  //     Credential system). 409 before 100% (and a passing quiz, if any). → cert view
  //     ({ id, serial, type, title, issuer, description, issuedAt, url, status }).
  issueCertificate: (id) =>
    api.post("/v1/market/my-courses/" + encodeURIComponent(id) + "/certificate", {}),
  //   getCertificate(id) — fetch / verify the issued cert. 404 if none has been issued.
  getCertificate: (id) =>
    api.get("/v1/market/my-courses/" + encodeURIComponent(id) + "/certificate"),

  // --- Q&A / FORUM + AUTHOR PAYOUT (R-MARKET P7 — FINAL — doc §4.4/§7.6/§9.5 + §5.4) ---
  //
  // Q&A (course pre-purchase + in-player questions). The LIST is PUBLIC (a guest/prospective
  // buyer reads the thread before buying) → publicApi + a REQUIRED tenantSlug (the established
  // public pattern). ASK/REPLY + DELETE are authed (the JWT carries the tenant).
  //
  //   courseQuestions(id, tenantSlug) — @Public: a published course's thread →
  //     { items:[{ id, userId, authorName, body, isAnswerByAuthor, sectionId, createdAt,
  //       replies:[{ id, userId, authorName, body, isAnswerByAuthor, sectionId, createdAt }] }] }.
  //     404 for a draft / cross-tenant / unknown course (no-leak).
  courseQuestions: (id, tenantSlug) =>
    publicApi.get(
      "/v1/market/courses/" + encodeURIComponent(id) + "/questions?tenantSlug=" +
        encodeURIComponent(tenantSlug || ""),
    ),
  //   askQuestion(id, { body, sectionId?, parentId? }) — authed. A top-level question
  //     (no parentId) may be asked by ANY authed user; a reply (parentId set) requires the
  //     course author OR an active enrollment (else 403). isAnswerByAuthor is set server-side.
  //     Returns the created question (with empty replies[]) or the created reply.
  askQuestion: (id, body) =>
    api.post("/v1/market/courses/" + encodeURIComponent(id) + "/questions", body),
  //   deleteQuestion(id, qid) — authed soft-delete (own | course author | moderator) → { ok }.
  //     A top-level question takes its replies with it; a stranger gets a no-leak 404.
  deleteQuestion: (id, qid) =>
    api.delete(
      "/v1/market/courses/" + encodeURIComponent(id) + "/questions/" + encodeURIComponent(qid),
    ),

  // Wave D (Gap 5) — answer pinning + light moderation. Pinned threads/answers sort to the top;
  // a moderator (content_manager / oversight) can HIDE an abusive Q/reply (hidden disappears for
  // learners, stays flagged «پنهان‌شده» on the moderation read). Each view row now also carries
  // pinnedAt + hidden + hiddenAt. All authed; the tenant rides the JWT.
  //
  //   moderationQuestions(id) — REVIEWER full thread INCLUDING hidden rows (each flagged) →
  //     same shape as courseQuestions but with hidden=true rows visible. Non-moderator → 404.
  moderationQuestions: (id) =>
    api.get("/v1/market/courses/" + encodeURIComponent(id) + "/questions/moderation"),
  //   pinQuestion(id, qid) / unpinQuestion(id, qid) — AUTHOR pins/unpins (author of the course
  //     only; else 404 no-leak) → { ok, id, pinned }.
  pinQuestion: (id, qid) =>
    api.post(
      "/v1/market/courses/" + encodeURIComponent(id) + "/questions/" + encodeURIComponent(qid) + "/pin",
    ),
  unpinQuestion: (id, qid) =>
    api.post(
      "/v1/market/courses/" + encodeURIComponent(id) + "/questions/" + encodeURIComponent(qid) + "/unpin",
    ),
  //   hideQuestion(id, qid) / unhideQuestion(id, qid) — REVIEWER hides/unhides (content_manager /
  //     oversight only; else 404 no-leak) → { ok, id, hidden }.
  hideQuestion: (id, qid) =>
    api.post(
      "/v1/market/courses/" + encodeURIComponent(id) + "/questions/" + encodeURIComponent(qid) + "/hide",
    ),
  unhideQuestion: (id, qid) =>
    api.post(
      "/v1/market/courses/" + encodeURIComponent(id) + "/questions/" + encodeURIComponent(qid) + "/unhide",
    ),

  // Wave D (Gap 6) — downloadable lesson resources / handouts. The author attaches an EXISTING
  // uploaded asset (POST /v1/market/assets) to a lesson with a title; an ENROLLED buyer lists +
  // downloads each behind the SAME token-gated /assets/:id/stream (non-enrolled → 404 no-leak).
  //
  //   AUTHOR (instructor / content_manager / admin / super_admin):
  //   lessonResourcesAuthor(id, lessonId) — author list →
  //     { items:[{ id, lessonId, assetId, title, order, fileName, sizeBytes, mimeType, assetType, createdAt }] }.
  lessonResourcesAuthor: (id, lessonId) =>
    api.get(
      "/v1/market/courses/" + encodeURIComponent(id) + "/lessons/" + encodeURIComponent(lessonId) + "/resources",
    ),
  //   attachResource(id, lessonId, { assetId, title, order? }) → the created AuthorResourceView.
  //     409 if the same asset is already attached to this lesson.
  attachResource: (id, lessonId, body) =>
    api.post(
      "/v1/market/courses/" + encodeURIComponent(id) + "/lessons/" + encodeURIComponent(lessonId) + "/resources",
      body,
    ),
  //   deleteResource(id, lessonId, resourceId) → { ok } (soft-delete; the asset is untouched).
  deleteResource: (id, lessonId, resourceId) =>
    api.delete(
      "/v1/market/courses/" + encodeURIComponent(id) + "/lessons/" + encodeURIComponent(lessonId) +
        "/resources/" + encodeURIComponent(resourceId),
    ),
  //   reorderResources(id, lessonId, { orderedIds }) → { ok }.
  reorderResources: (id, lessonId, body) =>
    api.patch(
      "/v1/market/courses/" + encodeURIComponent(id) + "/lessons/" + encodeURIComponent(lessonId) +
        "/resources/reorder",
      body,
    ),
  //   ENROLLED BUYER: lessonResources(id, lessonId) — list WITH per-file token-gated download
  //     urls → { items:[{ id, title, order, fileName, sizeBytes, mimeType, assetType,
  //     downloadUrl, expiresAt, createdAt }] }. A non-enrolled / refunded user → 404 (no-leak).
  lessonResources: (id, lessonId) =>
    api.get(
      "/v1/market/my-courses/" + encodeURIComponent(id) + "/lessons/" + encodeURIComponent(lessonId) + "/resources",
    ),

  // AUTHOR PAYOUT (§5.4 تسویه با استاد). The §5.3 author share ACCRUES to the ledger account
  // 2300 «پرداختنی به استاد» on each confirmed purchase (P5); these settle it out.
  //
  //   ADMIN/FINANCE (admin | super_admin | finance_manager):
  //   adminPayouts() — authors with an accrued/paid/unpaid balance + recent payout history →
  //     { authors:[{ authorUserId, authorName, coursesCount, accruedRial, paidRial, unpaidRial,
  //       grossRial, universityShareRial, lastPayoutAt }],
  //       payouts:[{ id, authorUserId, authorName, grossSalesRial, universityShareRial,
  //       authorShareRial, status, paidAt, ledgerEntryId, createdAt, … }] }.
  adminPayouts: () => api.get("/v1/market/admin/payouts"),
  //   runPayout(authorUserId) — settle the author's whole accrued-unpaid balance: books a
  //     BALANCED ledger entry (DEBIT 2300 / CREDIT 1000) inside a Serializable txn (double-pay
  //     safe). → { ok, payoutId, authorShareRial, ledgerEntryId, status:"paid" }. 409 if nothing
  //     is owed («مبلغی برای تسویه نیست»); 404 for a cross-tenant author; 403 for a non-finance role.
  runPayout: (authorUserId) => api.post("/v1/market/admin/payouts", { authorUserId }),
  //   payoutsMine() — the caller's OWN payout history (most recent first) →
  //     { items:[{ id, periodFrom, periodTo, grossSalesRial, universityShareRial,
  //       authorShareRial, status, paidAt, ledgerEntryId, createdAt }] }.
  payoutsMine: () => api.get("/v1/market/payouts/mine"),

  // ===========================================================================
  // R-MARKET FEATURE EXTENSIONS (additive §10.3) — buyer reviews / coupons /
  // wishlist / categories. RBAC is enforced SERVER-SIDE on every call (the UI
  // mirrors it); the public reviews READ rides on courseReviews() above (it now
  // returns the REAL distribution + items). All write paths are JWT-guarded.
  // ===========================================================================

  // --- REVIEWS (buyer star-reviews; §7.6) ---
  //   postReview(id, { rating, comment? }) — enrolled buyer UPSERTS their OWN review (one per
  //     user per course). 403 for the course author / a non-enrolled user; 404 for an
  //     unpublished/cross-tenant course. → the created/updated review item.
  postReview: (id, body) =>
    api.post("/v1/market/courses/" + encodeURIComponent(id) + "/reviews", body),
  //   replyReview(id, rid, { reply? }) — the COURSE AUTHOR replies to a review on their course
  //     (404 no-leak for anyone else). An empty reply clears it. → the updated review item.
  replyReview: (id, rid, body) =>
    api.post(
      "/v1/market/courses/" + encodeURIComponent(id) + "/reviews/" + encodeURIComponent(rid) + "/reply",
      body || {},
    ),
  //   hideReview / unhideReview(id, rid, { reason? }) — moderator|content_manager|admin|
  //     super_admin only (drops/re-adds the review from/to the public list + recompute).
  hideReview: (id, rid, body) =>
    api.post(
      "/v1/market/courses/" + encodeURIComponent(id) + "/reviews/" + encodeURIComponent(rid) + "/hide",
      body || {},
    ),
  unhideReview: (id, rid) =>
    api.post(
      "/v1/market/courses/" + encodeURIComponent(id) + "/reviews/" + encodeURIComponent(rid) + "/unhide",
      {},
    ),

  // --- COUPONS (discount codes; §5) ---
  //   createCoupon({ code, marketCourseId?, description?, percentOff?|amountOffRial?,
  //     validUntil?, maxUses? }) — course-specific (marketCourseId set) → the course author
  //     or admin; global (omit marketCourseId) → admin/super_admin only. Exactly ONE of
  //     percentOff (1..100) | amountOffRial (>0). → the created coupon.
  createCoupon: (body) => api.post("/v1/market/coupons", body),
  //   couponsMine() — the caller's OWN coupons (those they created) → { items:[CouponView] }.
  couponsMine: () => api.get("/v1/market/coupons/mine"),
  //   adminCoupons() — admin/super_admin: all tenant coupons → { items:[CouponView] }.
  adminCoupons: () => api.get("/v1/market/admin/coupons"),
  //   deactivateCoupon(id) / activateCoupon(id) — PATCH active (creator/admin; 404 no-leak).
  deactivateCoupon: (id) =>
    api.patch("/v1/market/coupons/" + encodeURIComponent(id), { active: false }),
  activateCoupon: (id) =>
    api.patch("/v1/market/coupons/" + encodeURIComponent(id), { active: true }),
  //   deleteCoupon(id) — DELETE (creator/admin; 404 no-leak) → { ok, id }.
  deleteCoupon: (id) => api.delete("/v1/market/coupons/" + encodeURIComponent(id)),
  //   validateCoupon({ code, marketCourseId }) — authed buyer preview. NEVER throws on an
  //     invalid code — returns { valid:false, reason } so the buyer sees a friendly message;
  //     on a valid code → { valid:true, couponId, listPriceRial, discountedPriceRial, discountRial }.
  //     Throws only on a missing/cross-tenant course (404).
  validateCoupon: (body) => api.post("/v1/market/coupons/validate", body),

  // --- WISHLIST (buyer "save for later"; §7) — any authed user, OWN list only ---
  //   wishlist() — the caller's own items + a light course card each →
  //     { items:[{ id, marketCourseId, title, coverImageUrl, level, pricingModel, priceRial, addedAt }] }.
  wishlist: () => api.get("/v1/market/wishlist"),
  //   addWishlist(courseId) — idempotent add (404 if not published) → { ok, id, added }.
  addWishlist: (courseId) =>
    api.post("/v1/market/wishlist/" + encodeURIComponent(courseId), {}),
  //   removeWishlist(courseId) — remove own item (404 if not in the caller's list) → { ok, id }.
  removeWishlist: (courseId) =>
    api.delete("/v1/market/wishlist/" + encodeURIComponent(courseId)),

  // --- CATEGORIES (catalog taxonomy; §7.2) — public read; admin CRUD ---
  //   categories(tenantSlug) — @Public: the tenant's ACTIVE categories (catalog filter) →
  //     { items:[{ id, name, slug, order, active }] }.
  categories: (tenantSlug) =>
    publicApi.get("/v1/market/categories?tenantSlug=" + encodeURIComponent(tenantSlug || "")),
  //   adminCategories() — admin/super_admin: all categories (active + inactive).
  adminCategories: () => api.get("/v1/market/admin/categories"),
  //   adminCreateCategory({ name, slug, order?, active? }) — admin/super_admin.
  adminCreateCategory: (body) => api.post("/v1/market/admin/categories", body),
  //   adminUpdateCategory(id, { name?, slug?, order?, active? }) — admin/super_admin.
  adminUpdateCategory: (id, body) =>
    api.patch("/v1/market/admin/categories/" + encodeURIComponent(id), body),
  //   adminDeleteCategory(id) — admin/super_admin → { ok, id }.
  adminDeleteCategory: (id) =>
    api.delete("/v1/market/admin/categories/" + encodeURIComponent(id)),

  // --- CATALOG (P4 EXTENDED) — real category + price filtering ---
  //   catalogExt({ tenantSlug, q?, category?, level?, language?, sort?, free?, priceMax?,
  //     page?, limit? }) — same shape as catalog() but forwards the now-REAL category id +
  //     free (boolean) + priceMax (whole rial) filters.
  catalogExt: ({ tenantSlug, q, category, level, language, sort, free, priceMax, page, limit } = {}) => {
    const p = new URLSearchParams();
    p.set("tenantSlug", tenantSlug || "");
    if (q) p.set("q", q);
    if (category) p.set("category", category);
    if (level) p.set("level", level);
    if (language) p.set("language", language);
    if (sort) p.set("sort", sort);
    if (free) p.set("free", "true");
    if (priceMax != null && priceMax > 0) p.set("priceMax", String(priceMax));
    if (page) p.set("page", String(page));
    if (limit) p.set("limit", String(limit));
    return publicApi.get("/v1/market/catalog?" + p.toString());
  },

  // ===========================================================================
  // R-MARKET BUNDLES — «بستهٔ دوره» course bundles (doc §۸.۶ / backend
  // market-bundle.controller.ts + market-bundle.service.ts). An author packages a
  // set of THEIR OWN published courses into a MarketBundle priced at a single
  // bundle priceRial (typically LESS than the sum of the per-course prices — the
  // «savings»). A buyer pays ONCE (REUSING the existing course Payment/checkout
  // flow + the shared @Public GET /market/purchase/callback — NO new callback)
  // and on a confirmed payment is GRANTED a MarketEnrollment for EVERY course in
  // the bundle (idempotent). Revenue-share flows to the §5.3 Ledger like a course
  // purchase (v1 = bundle-author-only).
  //
  // Every view is the backend BundleDetailView:
  //   { id, tenantId, authorUserId, title, description, coverImageUrl, priceRial,
  //     status, slug, publishedAt, enrollmentCount,
  //     items:[{ itemId, marketCourseId, order, title, coverImageUrl, level, slug, priceRial }],
  //     sumOfIndividualRial, savingsRial }.
  // ===========================================================================

  // --- AUTHOR CRUD (instructor|content_manager|admin|super_admin — own bundle; 404 else) ---
  //   bundlesMine() — the caller's OWN bundles (newest first), each with price math → BundleDetailView[].
  bundlesMine: () => api.get("/v1/market/bundles/mine"),
  //   bundle(id) — own-bundle detail → BundleDetailView (no-leak 404 cross-author/tenant).
  bundle: (id) => api.get("/v1/market/bundles/" + encodeURIComponent(id)),
  //   createBundle({ title*, description?, coverImageUrl?, priceRial? }) — a DRAFT bundle
  //     (priceRial defaults 0 = free). 201 → the new bundle's BundleDetailView.
  createBundle: (body) => api.post("/v1/market/bundles", body),
  //   updateBundle(id, { title?, description?, coverImageUrl?, priceRial? }) — edit metadata/price.
  //     Editable only while DRAFT (a published bundle's price/items are locked → 409). → BundleDetailView.
  updateBundle: (id, body) =>
    api.patch("/v1/market/bundles/" + encodeURIComponent(id), body),
  //   setBundlePrice(id, priceRial) — set just the server-side bundle price (whole rial; 0=free). → BundleDetailView.
  setBundlePrice: (id, priceRial) =>
    api.put("/v1/market/bundles/" + encodeURIComponent(id) + "/price", { priceRial }),
  //   addBundleItem(id, marketCourseId) — add ONE of the author's OWN PUBLISHED courses. 400 for a
  //     foreign/unpublished/missing course (no-leak); 409 if already in the bundle. → BundleDetailView.
  addBundleItem: (id, marketCourseId) =>
    api.post("/v1/market/bundles/" + encodeURIComponent(id) + "/items", { marketCourseId }),
  //   removeBundleItem(id, marketCourseId) — drop a course from the bundle (draft only). → BundleDetailView.
  removeBundleItem: (id, marketCourseId) =>
    api.delete(
      "/v1/market/bundles/" + encodeURIComponent(id) + "/items/" + encodeURIComponent(marketCourseId),
    ),
  //   publishBundle(id) — draft → published (requires ≥2 items; 400 else). → BundleDetailView.
  publishBundle: (id) =>
    api.post("/v1/market/bundles/" + encodeURIComponent(id) + "/publish", {}),
  //   deleteBundle(id) — soft-delete the caller's OWN bundle → { ok:true }.
  deleteBundle: (id) => api.delete("/v1/market/bundles/" + encodeURIComponent(id)),

  // --- PURCHASE (any authed buyer) — reuses the course gateway/mock-confirm flow ---
  //   purchaseBundle(id) — begin a bundle buy. The server uses the SERVER-SIDE bundle price.
  //     free ⇒ { free:true, purchaseId, status:"completed", enrolled:true, coursesGranted, pricePaidRial:0 }
  //     paid ⇒ { free:false, purchaseId, paymentId, redirectUrl, isMock, provider, amount, pricePaidRial }
  //            — redirect the browser to redirectUrl (the SAME gateway/mock flow; the shared
  //            /market/purchase/callback grants EVERY course + books the §5.3 ledger on confirm).
  purchaseBundle: (id) =>
    api.post("/v1/market/bundles/" + encodeURIComponent(id) + "/purchase", {}),

  // --- PUBLIC (guest + any role; resolved by ?tenantSlug — the established public pattern) ---
  //   bundlesPublic(tenantSlug) — the tenant's PUBLISHED bundles → BundleDetailView[].
  bundlesPublic: (tenantSlug) =>
    publicApi.get("/v1/market/bundles/public?tenantSlug=" + encodeURIComponent(tenantSlug || "")),
  //   bundlePublic(idOrSlug, tenantSlug) — a published bundle's public detail (items + price +
  //     sum-of-individual + savings). 404 if missing/not published/cross-tenant (no-leak).
  bundlePublic: (idOrSlug, tenantSlug) =>
    publicApi.get(
      "/v1/market/bundles/" + encodeURIComponent(idOrSlug) + "/public?tenantSlug=" +
        encodeURIComponent(tenantSlug || ""),
    ),

  // ===========================================================================
  // R-MARKET OPEN-RESPONSE RUBRIC GRADING (Gap 9 — doc §9, backend
  // market-open-response.controller.ts + market-open-response.service.ts). A section
  // whose contentType is "open_response" is an essay/task graded against a RUBRIC.
  // RBAC is enforced SERVER-SIDE on every call (the UI mirrors it); no-leak 404s.
  //
  // The RUBRIC view (author + learner, same shape):
  //   { id, sectionId, marketCourseId, title, description, criteria:[{ title, maxPoints,
  //     levels?:[{ label, points }] }], gradingMode:"staff"|"self"|"peer",
  //     peersPerSubmission, maxTotalPoints }.
  // A GRADE view (the grader's identity is OMITTED for peer grades):
  //   { graderRole, scores:[{ criterionIndex, points }], totalPoints, feedback, gradedAt }.
  // ===========================================================================

  // --- AUTHOR — set / read / clear the rubric on an open_response section ---
  //   getRubric(id, sectionId) — the author's rubric for an OWNED open_response section,
  //     or null when unset. 404 cross-author/cross-tenant/non-open_response section.
  getRubric: (id, sectionId) =>
    api.get(
      "/v1/market/courses/" + encodeURIComponent(id) + "/sections/" + encodeURIComponent(sectionId) + "/rubric",
    ),
  //   setRubric(id, sectionId, { title*, description?, criteria:[{ title*, maxPoints*,
  //     levels?:[{ label*, points* }] }], gradingMode?, peersPerSubmission? }) — create/REPLACE
  //     the rubric + grading mode. Per-criterion maxPoints + level rules re-validated server-side
  //     (400 on a malformed payload). → the RubricView (with the server-computed maxTotalPoints).
  setRubric: (id, sectionId, body) =>
    api.put(
      "/v1/market/courses/" + encodeURIComponent(id) + "/sections/" + encodeURIComponent(sectionId) + "/rubric",
      body,
    ),
  //   clearRubric(id, sectionId) — soft-delete the rubric (clears the task config) → { ok:true }.
  clearRubric: (id, sectionId) =>
    api.delete(
      "/v1/market/courses/" + encodeURIComponent(id) + "/sections/" + encodeURIComponent(sectionId) + "/rubric",
    ),

  // --- STAFF GRADING — the course author/oversight grades the queue (own course → 404 else) ---
  //   gradingQueue(id, includeGraded?) — the ungraded-submission queue for an OWNED course →
  //     { items:[{ responseId, sectionId, sectionTitle, learnerUserId, learnerName, body,
  //       attachmentAssetId, status, submittedAt, hasStaffGrade }] }. includeGraded=true also
  //       returns already-graded rows (so the author can revise a grade).
  gradingQueue: (id, includeGraded) =>
    api.get(
      "/v1/market/courses/" + encodeURIComponent(id) + "/grading-queue" +
        (includeGraded ? "?includeGraded=true" : ""),
    ),
  //   gradeResponse(id, responseId, { scores:[{ criterionIndex, points }], feedback? }) — a STAFF
  //     grade (AUTHORITATIVE — flips the submission to "graded", locking resubmits). Per-criterion
  //     points server-validated ≤ that criterion's maxPoints; total is the server SUM (a client
  //     total is ignored). Idempotent per grader (a re-grade UPDATES). → the GradeView.
  gradeResponse: (id, responseId, body) =>
    api.post(
      "/v1/market/courses/" + encodeURIComponent(id) + "/responses/" + encodeURIComponent(responseId) + "/grade",
      body,
    ),

  // --- ENROLLED LEARNER — submit / read / self-grade / peer-grade (enrollment gate in service) ---
  //   openResponseMine(id, sectionId) — the caller's OWN submission + the rubric + any grade they
  //     may see → { rubric, submission:{ id, body, attachmentAssetId, status, submittedAt,
  //     updatedAt }|null, grade:GradeView|null, peerGrades:GradeView[]|null }. A non-enrolled /
  //     refunded / cross-tenant caller → 404 (no-leak). Peer grader identities are NEVER surfaced.
  openResponseMine: (id, sectionId) =>
    api.get(
      "/v1/market/my-courses/" + encodeURIComponent(id) + "/sections/" + encodeURIComponent(sectionId) + "/open-response",
    ),
  //   submitOpenResponse(id, sectionId, { body?, attachmentAssetId? }) — submit OR resubmit (one
  //     active submission per learner per section; a resubmit REPLACES in place while not graded —
  //     409 once a staff grade lands). At least a body OR a file is required. → the MyOpenResponseView.
  submitOpenResponse: (id, sectionId, body) =>
    api.put(
      "/v1/market/my-courses/" + encodeURIComponent(id) + "/sections/" + encodeURIComponent(sectionId) + "/open-response",
      body,
    ),
  //   selfGradeOpenResponse(id, sectionId, { scores, feedback? }) — the learner self-assesses their
  //     OWN submission (only when gradingMode="self"; 403 otherwise). Does NOT lock. → the GradeView.
  selfGradeOpenResponse: (id, sectionId, body) =>
    api.post(
      "/v1/market/my-courses/" + encodeURIComponent(id) + "/sections/" + encodeURIComponent(sectionId) + "/open-response/self-grade",
      body,
    ),
  //   peerTasks(id, sectionId) — the peer-grading task list for a peer-mode open_response section:
  //     fellow learners' submissions the caller may grade (NOT their own; submitter ANONYMIZED) →
  //     { items:[{ responseId, sectionId, sectionTitle, anonymousLabel, body, attachmentAssetId,
  //       rubric, myGrade:GradeView|null }] }. [] when not peer mode / nothing to grade.
  peerTasks: (id, sectionId) =>
    api.get(
      "/v1/market/my-courses/" + encodeURIComponent(id) + "/sections/" + encodeURIComponent(sectionId) + "/peer-tasks",
    ),
  //   peerGradeResponse(id, responseId, { scores, feedback? }) — a DIFFERENT enrolled learner grades
  //     a fellow learner's submission (gradingMode="peer", under peersPerSubmission cap; 403/409
  //     otherwise). The submitter never learns who graded. → the GradeView.
  peerGradeResponse: (id, responseId, body) =>
    api.post(
      "/v1/market/my-courses/" + encodeURIComponent(id) + "/responses/" + encodeURIComponent(responseId) + "/peer-grade",
      body,
    ),
};

// ---------- bookmarks (general-purpose «ذخیره‌ها») ----------
//
// A general-purpose, per-user "save for later" list across many surfaces (chat
// messages, library resources, labs, lessons, external links). This is DISTINCT
// from the market wishlist (which stays market-course-only). All routes are authed
// + own+tenant scoped + @AuditSkip (high-freq personal toggles). Mirrors the
// wishlist module shape; the client state lives in pages/bookmark-state.ts.
//
//   resourceType ∈ {"chat_message","library","lab","lesson","link"}.
//   title       = display snapshot (a deleted resource still renders).
//   targetHref  = where clicking navigates (in-app route or external https url).
export const bookmarkApi = {
  // add({ resourceType, resourceId, title?, targetHref?, note? }) — IDEMPOTENT
  //   upsert on the unique (re-POST returns the existing row, no dup) →
  //   201 { id, resourceType, resourceId, title, targetHref, note, createdAt, saved:true }.
  add: ({ resourceType, resourceId, title, targetHref, note }) =>
    api.post("/v1/bookmarks", {
      resourceType,
      resourceId,
      ...(title != null ? { title } : {}),
      ...(targetHref != null ? { targetHref } : {}),
      ...(note != null ? { note } : {}),
    }),
  // remove(resourceType, resourceId) → 200 { removed:true } (200 even if not saved).
  remove: (resourceType, resourceId) =>
    api.delete(
      "/v1/bookmarks?resourceType=" +
        encodeURIComponent(resourceType) +
        "&resourceId=" +
        encodeURIComponent(resourceId),
    ),
  // list({ type?, q?, cursor? }) → { results:[…], nextCursor } (newest first, ~30/page).
  list: ({ type, q, cursor } = {}) => {
    const p = [];
    if (type) p.push("type=" + encodeURIComponent(type));
    if (q) p.push("q=" + encodeURIComponent(q));
    if (cursor) p.push("cursor=" + encodeURIComponent(cursor));
    return api.get("/v1/bookmarks" + (p.length ? "?" + p.join("&") : ""));
  },
  // ids() → { ids: string[] } where each id = "<resourceType>:<resourceId>" — the
  //   FE renders saved-state on buttons WITHOUT N calls. own+tenant scoped.
  ids: () => api.get("/v1/bookmarks/ids"),
};

// ---------- ministry curricula (کتابخانه وزارت علوم) ----------
//
// Tenant-scoped reference library of ministry-approved program templates extracted
// from the 3268 scanned PDFs via OCR pipeline (scripts/ocr_pipeline.py).
// super_admin / admin can batch-import and instantiate programs into the live tree.
//
//   list({q?, degreeLevel?, fieldGroup?, page?, limit?})
//     → { items:[MinistryProgramRow…], total, page, limit }
//     MinistryProgramRow: { id, nameFa, nameEn, degreeLevel, degreeLevelCode, fieldGroup,
//       specialization, totalCredits, extractionConfidence, approvalDate, coursesCount }
//
//   stats() → { total, importDone, importError, byDegree:{}, byGroup:{} }
//
//   importLog({page?, limit?, status?})
//     → { items:[MinistryImportLog…], total } (admin only)
//
//   get(id) → full MinistryProgram incl. courses[] and policies[]
//
//   update(id, { nameFa?, nameEn?, degreeLevel?, totalCredits?, policies? })
//     → updated MinistryProgram (OCR error correction)
//
//   instantiate(id, { facultyId, departmentId?, specializationLabel? })
//     → { programId, curriculumId, coursesCreated } (201)
export const ministryCurriculaApi = {
  list: ({ q, degreeLevel, fieldGroup, page, limit } = {}) => {
    const p = [];
    if (q) p.push("q=" + encodeURIComponent(q));
    if (degreeLevel) p.push("degreeLevel=" + encodeURIComponent(degreeLevel));
    if (fieldGroup) p.push("fieldGroup=" + encodeURIComponent(fieldGroup));
    if (page != null) p.push("page=" + page);
    if (limit != null) p.push("limit=" + limit);
    return api.get("/v1/ministry-curricula" + (p.length ? "?" + p.join("&") : ""));
  },
  stats: () => api.get("/v1/ministry-curricula/stats"),
  importLog: ({ page, limit, status } = {}) => {
    const p = [];
    if (page != null) p.push("page=" + page);
    if (limit != null) p.push("limit=" + limit);
    if (status) p.push("status=" + encodeURIComponent(status));
    return api.get("/v1/ministry-curricula/import-log" + (p.length ? "?" + p.join("&") : ""));
  },
  get: (id) => api.get("/v1/ministry-curricula/" + encodeURIComponent(id)),
  update: (id, body) => api.patch("/v1/ministry-curricula/" + encodeURIComponent(id), body),
  instantiate: (id, body) =>
    api.post("/v1/ministry-curricula/" + encodeURIComponent(id) + "/instantiate", body),
};
